import React, { useState, useCallback, useEffect, useRef, useMemo} from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel, Handle, Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { apiCall, apiCallJson } from './Api';
import { getCurrentToken } from './SessionManager';
import {
  Save,
  Play,
  Plus,
  Trash2,
  Upload,
  Download,
  Workflow,
  Zap,
  X,
  List,
  ArrowLeft,
  Search, 
  Calendar,
  BarChart3,
  Edit3,
  Copy,
  RefreshCw,
  Filter, Code, Database, FileText,
  MessageSquare, 
  MousePointer, 
  Image, 
  Video, 
  BoomBox,
  MoreHorizontal,
  Layers, Send, RotateCcw, CheckCircle,
  Key,
  Eye,
  EyeOff,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Undo2,
  Redo2,
  ClipboardList,
  Bot,
  UserCheck,
  Paperclip,
  ShieldCheck,
  Users,
  Radio,
  Globe,
  Sparkles
} from 'lucide-react';
import { Clock, History, GitBranch, DatabaseBackup, PhoneCall, Bell, ListOrdered } from 'lucide-react';
import WorkflowChatPreview from './WorkflowChatPreview';
import AgentHandoffDashboard from './AgentHandoffDashboard';
import AnalyticsDashboard from './AnalyticsDashboard';
import EntityPermissionManager from './EntityPermissionManager';
import ChannelDeploymentManager from './ChannelDeploymentManager';
import { generateId, useAutoSave, useUndoRedo, validateWorkflow, CHANNEL_TYPES, STEP_TYPES } from './ChatbotDesignerUtils';
import { useConfirm, useToast, ValidationModal } from './ChatbotDesignerModals';
import EnhancedCanvas, { detectFlowGroups } from './EnhancedDesigner';
import ApiConnectionsManager from './ApiConnectionsManager';
import ChatbotUsageReport from './ChatbotUsageReport';
import { PageLayout, PageHeader, PageBody } from './PageLayout';


const FloatingChatWidget = ({ workflowId, apiEndpoint, apiKey }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  // apiKey from GET is truncated display-only (e.g. "wf_api_...").
  // Live Preview always uses authToken (JWT) for admin access.
  const fullApiKey = apiKey?.api_key && !apiKey.api_key.endsWith('...') ? apiKey.api_key : null;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 bg-blue-600 text-white w-14 h-14 rounded-full shadow-lg hover:bg-blue-700 flex items-center justify-center z-50"
        >
          <svg 
            className="w-6 h-6" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" 
            />
          </svg>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-lg shadow-2xl overflow-hidden z-50 flex flex-col">
          <div className="flex-1 overflow-hidden">
            <WorkflowChatPreview 
              workflowId={workflowId}
              apiEndpoint={apiEndpoint}
              apiKey={fullApiKey}
              authToken={getCurrentToken()}
            />
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-2 right-2 text-white hover:text-gray-200 bg-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs z-10"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
};

const StepNode = ({ data, selected }) => {
  const stepData = data.stepData || {};
  const hasButtons = stepData.buttons && stepData.buttons.length > 0;
  const hasOptions = stepData.options && stepData.options.length > 0;
  const hasLogic = stepData.action?.type === 'logic';

  const getStepIcon = (messageType) => {
    const iconProps = { className: "h-4 w-4" };
    
    switch (messageType) {
      case 'text': 
        return <MessageSquare {...iconProps} />;
      case 'buttons': 
        return <MousePointer {...iconProps} />;
      case 'options': 
        return <List {...iconProps} />;
      case 'image': 
        return <Image {...iconProps} />;
      case 'video': 
        return <Video {...iconProps} />;
      case 'audio': 
        return <BoomBox {...iconProps} />;
      case 'carousel':
        return <Layers {...iconProps} />;
      case 'whatsapp_flow':
        return <ClipboardList {...iconProps} />;
      case 'ai_response':
        return <Bot {...iconProps} />;
      case 'handoff':
        return <UserCheck {...iconProps} />;
      case 'file_upload':
        return <Paperclip {...iconProps} />;
      case 'delay':
        return <Clock {...iconProps} />;
      case 'ab_test':
        return <GitBranch {...iconProps} />;
      case 'sheet_read': 
        return <Download {...iconProps} />;
      case 'sheet_write': 
        return <Upload {...iconProps} />;
      case 'table_read':
        return <Database {...iconProps} />;
      case 'table_write':
        return <DatabaseBackup {...iconProps} />;
      case 'request_callback':
        return <PhoneCall {...iconProps} />;
      case 'eod_sync_job':
        return <RefreshCw {...iconProps} />;
      case 'reminder_job':
        return <Bell {...iconProps} />;
      case 'api_call':
        return <Code {...iconProps} />;
      case 'router':
        return <GitBranch {...iconProps} />;
      case 'api_sequence':
        return <ListOrdered {...iconProps} />;
      default: 
        return <MoreHorizontal {...iconProps} />;
    }
  };

  const getStepColor = (messageType) => {
    switch (messageType) {
      case 'text': return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'buttons': return 'bg-green-50 border-green-200 text-green-700';
      case 'options': return 'bg-purple-50 border-purple-200 text-purple-700';
      case 'image': return 'bg-orange-50 border-orange-200 text-orange-700';
      case 'video': return 'bg-red-50 border-red-200 text-red-700';
      case 'audio': return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'carousel': return 'bg-pink-50 border-pink-200 text-pink-700';
      case 'whatsapp_flow': return 'bg-teal-50 border-teal-200 text-teal-700';
      case 'ai_response': return 'bg-violet-50 border-violet-200 text-violet-700';
      case 'handoff': return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'file_upload': return 'bg-cyan-50 border-cyan-200 text-cyan-700';
      case 'delay': return 'bg-rose-50 border-rose-200 text-rose-700';
      case 'ab_test': return 'bg-indigo-50 border-indigo-200 text-indigo-700';
      case 'sheet_read': return 'bg-lime-50 border-lime-200 text-lime-700';
      case 'sheet_write': return 'bg-rose-50 border-rose-200 text-rose-700';
      case 'table_read': return 'bg-sky-50 border-sky-200 text-sky-700';
      case 'table_write': return 'bg-teal-50 border-teal-200 text-teal-700';
      case 'request_callback': return 'bg-orange-50 border-orange-200 text-orange-700';
      case 'eod_sync_job': return 'bg-indigo-50 border-indigo-200 text-indigo-700';
      case 'reminder_job': return 'bg-violet-50 border-violet-200 text-violet-700';
      case 'api_call': return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'router': return 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700';
      case 'api_sequence': return 'bg-teal-50 border-teal-200 text-teal-700';
      default: return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  const getSelectedStyle = (messageType) => {
    switch (messageType) {
      case 'text': return 'border-blue-500 ring-2 ring-blue-200';
      case 'buttons': return 'border-green-500 ring-2 ring-green-200';
      case 'options': return 'border-purple-500 ring-2 ring-purple-200';
      case 'image': return 'border-orange-500 ring-2 ring-orange-200';
      case 'video': return 'border-red-500 ring-2 ring-red-200';
      case 'audio': return 'border-yellow-500 ring-2 ring-yellow-200';
      case 'carousel': return 'border-pink-500 ring-2 ring-pink-200';
      case 'whatsapp_flow': return 'border-teal-500 ring-2 ring-teal-200';
      case 'ai_response': return 'border-violet-500 ring-2 ring-violet-200';
      case 'handoff': return 'border-amber-500 ring-2 ring-amber-200';
      case 'file_upload': return 'border-cyan-500 ring-2 ring-cyan-200';
      case 'delay': return 'border-rose-500 ring-2 ring-rose-200';
      case 'ab_test': return 'border-indigo-500 ring-2 ring-indigo-200';
      case 'sheet_read': return 'border-lime-500 ring-2 ring-lime-200';
      case 'sheet_write': return 'border-rose-500 ring-2 ring-rose-200';
      case 'table_read': return 'border-sky-500 ring-2 ring-sky-200';
      case 'table_write': return 'border-teal-500 ring-2 ring-teal-200';
      case 'request_callback': return 'border-orange-500 ring-2 ring-orange-200';
      case 'eod_sync_job': return 'border-indigo-500 ring-2 ring-indigo-200';
      case 'reminder_job': return 'border-violet-500 ring-2 ring-violet-200';
      case 'api_call': return 'border-blue-500 ring-2 ring-blue-200';
      case 'router': return 'border-fuchsia-500 ring-2 ring-fuchsia-200';
      case 'api_sequence': return 'border-teal-500 ring-2 ring-teal-200';
      default: return 'border-gray-500 ring-2 ring-gray-200';
    }
  };

  return (
    <div className={`px-4 py-3 rounded-lg border-2 min-w-48 transition-all duration-200 ${
      selected ? getSelectedStyle(data.stepData.message_type) : getStepColor(data.stepData.message_type)
    }`}>
      {/* Top target handle */}
      <Handle
        type="target"
        position={Position.Top}
        id="top-target"
        className="w-3 h-3 !bg-blue-500"
      />
      
      {/* Left target handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="left-target"
        className="w-3 h-3 !bg-blue-500"
      />

      <div className="p-3">
        <div className="flex items-center space-x-2 mb-2">
          {getStepIcon(stepData.message_type)}
          <div className="flex-1">
            <div className="font-semibold text-sm text-gray-800">{stepData.name}</div>
            <div className="text-xs text-gray-500 capitalize">{stepData.message_type}</div>
          </div>
        </div>
        
        {stepData.message && (
          <div className="text-xs text-gray-600 mt-2 max-w-xs overflow-hidden text-ellipsis">
            {stepData.message.substring(0, 50)}...
          </div>
        )}
      </div>

      {/* Bottom source handle - default next step */}
      {!hasButtons && !hasOptions && !hasLogic && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom-source"
          className="w-3 h-3 !bg-blue-500"
        />
      )}

      {/* Right side handles for buttons */}
      {hasButtons && stepData.buttons.map((button, index) => {
        const totalButtons = stepData.buttons.length;
        const topOffset = ((index + 1) / (totalButtons + 1)) * 100;
        
        return (
          <React.Fragment key={`btn-${index}`}>
            <Handle
              type="source"
              position={Position.Right}
              id={`button-${index}`}
              style={{ top: `${topOffset}%` }}
              className="w-3 h-3 !bg-green-500"
            />
          </React.Fragment>
        );
      })}

      {/* Right side handles for options */}
      {hasOptions && stepData.options.map((option, index) => {
        const totalOptions = stepData.options.length;
        const topOffset = ((index + 1) / (totalOptions + 1)) * 100;
        
        return (
          <React.Fragment key={`opt-${index}`}>
            <Handle
              type="source"
              position={Position.Right}
              id={`option-${index}`}
              style={{ top: `${topOffset}%` }}
              className="w-3 h-3 !bg-purple-500"
            />
            <div
              style={{
                position: 'absolute',
                right: '-80px',
                top: `calc(${topOffset}% - 10px)`,
                fontSize: '10px',
                background: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                border: '1px solid #a855f7',
                color: '#a855f7',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 1000
              }}
            >
              {option.text}
            </div>
          </React.Fragment>
        );
      })}

      {/* Bottom handles for logic conditions */}
      {hasLogic && (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="logic-true"
            style={{ left: '33%' }}
            className="w-3 h-3 !bg-green-600"
          />
          <div
            style={{
              position: 'absolute',
              left: '33%',
              bottom: '-25px',
              transform: 'translateX(-50%)',
              fontSize: '10px',
              background: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              border: '1px solid #16a34a',
              color: '#16a34a',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 1000
            }}
          >
            TRUE
          </div>
          
          <Handle
            type="source"
            position={Position.Bottom}
            id="logic-false"
            style={{ left: '66%' }}
            className="w-3 h-3 !bg-red-600"
          />
          <div
            style={{
              position: 'absolute',
              left: '66%',
              bottom: '-25px',
              transform: 'translateX(-50%)',
              fontSize: '10px',
              background: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              border: '1px solid #dc2626',
              color: '#dc2626',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 1000
            }}
          >
            FALSE
          </div>
        </>
      )}
    </div>
  );
};

const nodeTypes = {
  stepNode: StepNode,
};

const ChatbotDesigner = ({ user }) => {
  const [view, setView] = useState('list'); // 'list' or 'designer'
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [workflowId, setWorkflowId] = useState(null);
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNodeMenu, setShowNodeMenu] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [floatingWorkflowMode, setFloatingWorkflowMode] = useState(false);
  const [agentDashboardOpen, setAgentDashboardOpen] = useState(false);
  const [translationsOpen, setTranslationsOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [translations, setTranslations] = useState({});
  const [defaultLanguage, setDefaultLanguage] = useState('en');

  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [channelsOpen, setChannelsOpen] = useState(false);
  const [jobDashboardOpen, setJobDashboardOpen] = useState(false);
  const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);

  // --- NEW: Confirm, Toast, Undo/Redo, Validation, AutoSave ---
  const { confirm, ConfirmDialog } = useConfirm();
  const { showToast, ToastContainer } = useToast();
  const { pushState, undo, redo, prepareUndo, canUndo, canRedo } = useUndoRedo();
  const [validationModal, setValidationModal] = useState({ isOpen: false, errors: [], warnings: [] });
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Track node/edge changes for undo (debounced to avoid flooding)
  const undoTimerRef = useRef(null);
  const pushUndoState = useCallback(() => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      pushState({ nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) });
    }, 500);
  }, [nodes, edges, pushState]);

  const [groups, setGroups] = useState([]);

  // Keyboard shortcuts: Ctrl+Z / Ctrl+Y
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const prev = undo();
        if (prev) {
          prepareUndo({ nodes, edges });
          setNodes(prev.nodes);
          setEdges(prev.edges);
        }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        const next = redo();
        if (next) {
          pushState({ nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) });
          setNodes(next.nodes);
          setEdges(next.edges);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [nodes, edges, undo, redo, prepareUndo, pushState, setNodes, setEdges]);

  // Auto-save DISABLED — was overriding job next_run_at on every save.
  // Use Ctrl+S or the Save button for manual saves.
  const autoSaveTimerRef = useRef(null);
  // --- END NEW ---

  useEffect(() => {
    if (workflowId && apiKeyModalOpen) {
      fetchApiKeyInfo(workflowId);
    }
  }, [workflowId, apiKeyModalOpen]);

  useEffect(() => {
    if (nodes.length > 0) {
      setGroups(detectFlowGroups(nodes, edges));
    }
  }, [nodes, edges]);


  const defaultStepData = {
    name: 'New Step',
    message_type: 'text',
    message: '',
    buttons: [],
    options: [],
    carousel_items: [],
    media_url: '',
    variable_mappings: {},
    validation_rules: {},
    next_step: '',
    action: {
      type: 'none',
      config: {}
    },
    ai_config: {
      provider: 'anthropic',
      model: '',
      system_prompt: '',
      temperature: 0.7,
      max_tokens: 1024,
      fallback_message: ''
    },
    handoff_config: {
      department: 'general',
      priority: 'normal',
      reason: ''
    },
    file_upload_config: {
      variable_name: 'uploaded_file',
      allowed_types: [],
      max_size_mb: 10
    },
    delay_config: {
      delay_seconds: 3,
      show_typing: true
    },
    ab_config: {
      variants: [
        { name: 'Variant A', percentage: 50, next_step: '' },
        { name: 'Variant B', percentage: 50, next_step: '' }
      ]
    },
    sheet_config: {
      source_type: 'google_sheets',
      source_url: '',
      sheet_name: '',
      google_credentials_json: '',
      phone_column: '',
      template_name: '',
      template_language: 'en_US',
      template_params: [],
      template_buttons: [],
      batch_size: 50,
      filter_column: '',
      filter_value: '',
      skip_duplicates: true,
      column_mappings: {},
      write_mode: 'append',
      match_column: '',
      match_variable: '',
    },
    template_config: {
      enabled: false,
      template_name: '',
      language: 'en',
      params: [],
    },
    table_config: {
      table_name: '',
      pg_table_name: '',
      phone_column: '',
      template_name: '',
      template_language: 'en_US',
      template_params: [],
      batch_size: 50,
      filter_column: '',
      filter_value: '',
      column_mappings: {},
      write_mode: 'append',
      match_column: '',
      match_variable: '',
    },
    job_config: {
      run_at_time: '23:00',
      timezone: 'Asia/Kolkata',
      campaign_node_id: '',
      writeback_column: 'response_summary',
      format: 'json',
      message_filter: 'all',
      template_name: '',
      template_language: 'en_US',
      interval_hours: 24,
      max_reminders: 3,
      target: 'no_reply',
    },
  };

  const onConnect = useCallback(
    (params) => {
      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);
      
      if (!sourceNode || !targetNode) return;

      const sourceHandle = params.sourceHandle;
      let edgeLabel = 'default';
      let edgeColor = '#3b82f6';
      let edgeIdPrefix = '';
      
      // Check if connection already exists
      setEdges((eds) => {
        const existingEdge = eds.find(e => 
          e.source === params.source && 
          e.target === params.target && 
          e.sourceHandle === params.sourceHandle
        );
        
        if (existingEdge) {
          return eds; // Connection already exists, don't create duplicate
        }
        
        // Determine edge properties and update node data based on handle type
        if (sourceHandle && sourceHandle.startsWith('button-')) {
          const buttonIndex = parseInt(sourceHandle.split('-')[1]);
          const button = sourceNode.data.stepData.buttons[buttonIndex];
          if (button) {
            edgeLabel = button.text;
            edgeColor = '#10b981';
            edgeIdPrefix = `${params.source}-btn-${buttonIndex}-`;
            
            // Update button next_step
            const updatedButtons = [...sourceNode.data.stepData.buttons];
            updatedButtons[buttonIndex] = { ...button, next_step: targetNode.id };
            
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === sourceNode.id) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      stepData: {
                        ...node.data.stepData,
                        buttons: updatedButtons
                      }
                    }
                  };
                }
                return node;
              })
            );
          }
        } else if (sourceHandle && sourceHandle.startsWith('option-')) {
          const optionIndex = parseInt(sourceHandle.split('-')[1]);
          const option = sourceNode.data.stepData.options[optionIndex];
          if (option) {
            edgeLabel = option.text;
            edgeColor = '#a855f7';
            edgeIdPrefix = `${params.source}-opt-${optionIndex}-`;
            
            // Update option next_step
            const updatedOptions = [...sourceNode.data.stepData.options];
            updatedOptions[optionIndex] = { ...option, next_step: targetNode.id };
            
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === sourceNode.id) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      stepData: {
                        ...node.data.stepData,
                        options: updatedOptions
                      }
                    }
                  };
                }
                return node;
              })
            );
          }
        } else if (sourceHandle === 'logic-true') {
          edgeLabel = 'TRUE';
          edgeColor = '#16a34a';
          edgeIdPrefix = `${params.source}-logic-true-`;
          
          // Update logic action true_step
          setNodes((nds) =>
            nds.map((node) => {
              if (node.id === sourceNode.id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    stepData: {
                      ...node.data.stepData,
                      action: {
                        ...node.data.stepData.action,
                        config: {
                          ...node.data.stepData.action.config,
                          true_step: targetNode.id
                        }
                      }
                    }
                  }
                };
              }
              return node;
            })
          );
        } else if (sourceHandle === 'logic-false') {
          edgeLabel = 'FALSE';
          edgeColor = '#dc2626';
          edgeIdPrefix = `${params.source}-logic-false-`;
          
          // Update logic action false_step
          setNodes((nds) =>
            nds.map((node) => {
              if (node.id === sourceNode.id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    stepData: {
                      ...node.data.stepData,
                      action: {
                        ...node.data.stepData.action,
                        config: {
                          ...node.data.stepData.action.config,
                          false_step: targetNode.id
                        }
                      }
                    }
                  }
                };
              }
              return node;
            })
          );
        } else {
          // Default bottom handle
          edgeLabel = 'default';
          edgeColor = '#3b82f6';
          edgeIdPrefix = `${params.source}-default-`;
          
          // Update next_step
          setNodes((nds) =>
            nds.map((node) => {
              if (node.id === sourceNode.id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    stepData: {
                      ...node.data.stepData,
                      next_step: targetNode.id
                    }
                  }
                };
              }
              return node;
            })
          );
        }

        const edge = {
          ...params,
          id: `${edgeIdPrefix}${params.target}`,
          type: 'smoothstep',
          animated: true,
          label: edgeLabel,
          style: { 
            stroke: edgeColor, 
            strokeWidth: 2 
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: edgeColor,
          },
        };
        
        return addEdge(edge, eds);
      });
    },
    [setEdges, setNodes, nodes]
  );

  const onEdgeClick = useCallback(async (event, edge) => {
    event.stopPropagation();
    const ok = await confirm({
      title: 'Delete Connection',
      message: 'Remove this connection between steps?',
      variant: 'danger',
      confirmText: 'Delete'
    });
    if (ok) {
      pushUndoState();
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    }
  }, [setEdges, confirm, pushUndoState]);

  const onNodeClick = useCallback((event, node) => {
    if (event.detail === 2) { // Double click
      setSelectedNode(node);
    }
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const addNewNode = useCallback((nodeType) => {
    pushUndoState();

    // For sheet nodes: inherit OAuth + service account credentials from existing sheet nodes
    let inheritedSheetConfig = {};
    if (nodeType === 'sheet_read' || nodeType === 'sheet_write') {
      const existingSheetNode = nodes.find(n => {
        const mt = n.data?.stepData?.message_type;
        return (mt === 'sheet_read' || mt === 'sheet_write') && 
               (n.data?.stepData?.sheet_config?.oauth_credentials || n.data?.stepData?.sheet_config?.google_credentials_json);
      });
      if (existingSheetNode) {
        const sc = existingSheetNode.data.stepData.sheet_config;
        inheritedSheetConfig = {
          oauth_credentials: sc.oauth_credentials || '',
          google_credentials_json: sc.google_credentials_json || '',
        };
      }
    }

    const newNode = {
      id: generateId('step'),
      type: 'stepNode',
      position: {
        x: Math.random() * 400 + 100,
        y: Math.random() * 400 + 100,
      },
      data: {
        label: `${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} Step`,
        stepData: {
          ...defaultStepData,
          message_type: nodeType,
          name: `${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} Step`,
          ...(Object.keys(inheritedSheetConfig).length > 0 ? {
            sheet_config: { ...defaultStepData.sheet_config, ...inheritedSheetConfig }
          } : {}),
        },
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setShowNodeMenu(false);
    setSelectedNode(newNode);
  }, [setNodes]);

  const updateNodeData = useCallback((updatedStepData) => {
    if (!selectedNode) return;

    // Update nodes
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          const updatedNode = {
            ...node,
            data: {
              ...node.data,
              label: updatedStepData.name,
              stepData: updatedStepData,
            },
          };
          setTimeout(() => setSelectedNode(updatedNode), 0);
          return updatedNode;
        }
        return node;
      })
    );

    const hasButtons = updatedStepData.buttons && updatedStepData.buttons.length > 0;
    const hasOptions = updatedStepData.options && updatedStepData.options.length > 0;
    const hasLogic = updatedStepData.action?.type === 'logic';

    // Remove all edges from this node first to avoid duplicates
    setEdges((eds) => eds.filter(e => e.source !== selectedNode.id));

    // Auto-create edges based on next_step (for default bottom handle)
    // Only if no buttons, options, or logic
    if (updatedStepData.next_step && !hasButtons && !hasOptions && !hasLogic) {
      const edgeId = `${selectedNode.id}-default-${updatedStepData.next_step}`;
      setEdges((eds) => {
        const exists = eds.find(e => e.id === edgeId);
        if (exists) return eds;
        
        const newEdge = {
          id: edgeId,
          source: selectedNode.id,
          sourceHandle: 'bottom-source',
          target: updatedStepData.next_step,
          targetHandle: 'top-target',
          type: 'smoothstep',
          animated: true,
          label: 'default',
          style: { stroke: '#3b82f6', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#3b82f6',
          },
        };
        return [...eds, newEdge];
      });
    }

    // Auto-create edges for buttons
    if (hasButtons) {
      updatedStepData.buttons.forEach((button, index) => {
        if (button.next_step) {
          const edgeId = `${selectedNode.id}-btn-${index}-${button.next_step}`;
          setEdges((eds) => {
            const exists = eds.find(e => e.id === edgeId);
            if (exists) return eds;
            
            const newEdge = {
              id: edgeId,
              source: selectedNode.id,
              sourceHandle: `button-${index}`,
              target: button.next_step,
              targetHandle: 'left-target',
              type: 'smoothstep',
              animated: true,
              label: button.text,
              style: { stroke: '#10b981', strokeWidth: 2 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#10b981',
              },
            };
            return [...eds, newEdge];
          });
        }
      });
    }

    // Auto-create edges for options
    if (hasOptions) {
      updatedStepData.options.forEach((option, index) => {
        if (option.next_step) {
          const edgeId = `${selectedNode.id}-opt-${index}-${option.next_step}`;
          setEdges((eds) => {
            const exists = eds.find(e => e.id === edgeId);
            if (exists) return eds;
            
            const newEdge = {
              id: edgeId,
              source: selectedNode.id,
              sourceHandle: `option-${index}`,
              target: option.next_step,
              targetHandle: 'left-target',
              type: 'smoothstep',
              animated: true,
              label: option.text,
              style: { stroke: '#a855f7', strokeWidth: 2 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#a855f7',
              },
            };
            return [...eds, newEdge];
          });
        }
      });
    }

    // Auto-create edges for logic conditions
    if (hasLogic) {
      if (updatedStepData.action.config?.true_step) {
        const edgeId = `${selectedNode.id}-logic-true-${updatedStepData.action.config.true_step}`;
        setEdges((eds) => {
          const exists = eds.find(e => e.id === edgeId);
          if (exists) return eds;
          
          const newEdge = {
            id: edgeId,
            source: selectedNode.id,
            sourceHandle: 'logic-true',
            target: updatedStepData.action.config.true_step,
            targetHandle: 'top-target',
            type: 'smoothstep',
            animated: true,
            label: 'TRUE',
            style: { stroke: '#16a34a', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#16a34a',
            },
          };
          return [...eds, newEdge];
        });
      }

      if (updatedStepData.action.config?.false_step) {
        const edgeId = `${selectedNode.id}-logic-false-${updatedStepData.action.config.false_step}`;
        setEdges((eds) => {
          const exists = eds.find(e => e.id === edgeId);
          if (exists) return eds;
          
          const newEdge = {
            id: edgeId,
            source: selectedNode.id,
            sourceHandle: 'logic-false',
            target: updatedStepData.action.config.false_step,
            targetHandle: 'top-target',
            type: 'smoothstep',
            animated: true,
            label: 'FALSE',
            style: { stroke: '#dc2626', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#dc2626',
            },
          };
          return [...eds, newEdge];
        });
      }
    }

    // Auto-create edges for router routes (+ default / error targets)
    if (updatedStepData.message_type === 'router') {
      const rc = updatedStepData.router_config || {};
      const routerEdges = [];
      (rc.routes || []).forEach((route, index) => {
        if (route.to) {
          routerEdges.push({
            id: `${selectedNode.id}-route-${index}-${route.to}`,
            target: route.to,
            label: (route.when && route.when.trim()) ? route.when.trim().slice(0, 24) : 'else',
            color: '#c026d3',
          });
        }
      });
      if (rc.default_to) {
        routerEdges.push({ id: `${selectedNode.id}-route-default-${rc.default_to}`, target: rc.default_to, label: 'default', color: '#9333ea' });
      }
      if (rc.error_to) {
        routerEdges.push({ id: `${selectedNode.id}-route-error-${rc.error_to}`, target: rc.error_to, label: 'on error', color: '#dc2626' });
      }
      if (routerEdges.length) {
        setEdges((eds) => {
          const additions = routerEdges
            .filter(re => !eds.find(e => e.id === re.id))
            .map(re => ({
              id: re.id,
              source: selectedNode.id,
              sourceHandle: 'bottom-source',
              target: re.target,
              targetHandle: 'top-target',
              type: 'smoothstep',
              animated: true,
              label: re.label,
              style: { stroke: re.color, strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, color: re.color },
            }));
          return additions.length ? [...eds, ...additions] : eds;
        });
      }
    }
  }, [selectedNode, setNodes, setEdges]);

  const deleteNode = useCallback(() => {
    if (selectedNode) {
      setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
      setEdges((eds) =>
        eds.filter(
          (edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id
        )
      );
      setSelectedNode(null);
    }
  }, [selectedNode, setNodes, setEdges]);

  // ---- Customer is MANDATORY: every workflow's message flow is billed to a customer.
  // New workflows (and older ones without a customer) prompt before saving; a
  // host_customer user with a single customer is attributed silently.
  const needsBillTo = !!user && user.role !== 'admin';
  const [billTo, setBillTo] = useState({ open: false, buyers: [], canCreate: false, choice: '', newName: '', skipValidation: false });
  const normName = (x) => (x || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const openBillTo = async (skipValidation) => {
    let list = []; let canCreate = false;
    try {
      const r = await apiCall('/api/chatbot/workflows/bill-to-options');
      if (r.ok) { const d = await r.json(); list = d.customers || []; canCreate = !!d.can_create; }
    } catch (e) { /* a new name still works for tenant roles */ }
    if (!canCreate && list.length === 1) { saveWorkflow(skipValidation, { bill_to_customer_id: list[0].customer_id }); return; }
    setBillTo({ open: true, buyers: list, canCreate, choice: '', newName: '', skipValidation });
  };
  const billToMatch = billTo.newName.trim()
    ? billTo.buyers.find((b) => normName(b.name || b.customer_id) === normName(billTo.newName))
    : null;
  const confirmBillTo = () => {
    const { choice, newName, skipValidation } = billTo;
    let extra = null;
    if (choice === '__new__') {
      if (!newName.trim()) { showToast('Enter the customer name', 'error'); return; }
      extra = billToMatch ? { bill_to_customer_id: billToMatch.customer_id } : { bill_to_customer_name: newName.trim() };
    } else if (choice) extra = { bill_to_customer_id: choice };
    else { showToast('A customer is required', 'error'); return; }
    setBillTo((b) => ({ ...b, open: false }));
    saveWorkflow(skipValidation, extra);
  };

  const saveWorkflow = async (skipValidation = false, billExtra = null) => {
    // Validate before save
    if (!skipValidation) {
      const result = validateWorkflow(nodes, edges);
      if (!result.isValid || result.warnings.length > 0) {
        setValidationModal({
          isOpen: true,
          errors: result.errors,
          warnings: result.warnings
        });
        if (!result.isValid) return; // Block save on errors
      }
    }

    if (!workflowId && needsBillTo && !billExtra) { openBillTo(skipValidation); return; }

    setSaving(true);
    try {
      const workflowData = {
        name: workflowName,
        description: workflowDescription,
        nodes,
        edges,
        status: 'draft',
        ...(billExtra || {}),
      };

      const url = workflowId
        ? `/api/chatbot/workflows/${workflowId}`
        : '/api/chatbot/workflows';
      const method = workflowId ? 'PUT' : 'POST';

      const response = await apiCall(url, {
        method,
        body: JSON.stringify(workflowData)
      });

      if (response.ok) {
        const data = await response.json();
        setWorkflowId(data.id);
        setLastSavedAt(new Date());
        const ba = data.billing_attribution;
        showToast(ba ? `Workflow saved — billed to ${ba.customer_name || ba.customer_id}${ba.created ? ' (new customer created)' : ''}`
                     : 'Workflow saved successfully!', 'success');
      } else {
        const err = await response.json().catch(() => ({}));
        if (response.status === 409 && String(err.detail || '').startsWith('customer_required')) {
          openBillTo(skipValidation);        // older workflow without a customer
          return;
        }
        showToast(err.detail || 'Failed to save workflow', 'error');
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      showToast('Failed to save workflow', 'error');
    } finally {
      setSaving(false);
    }
  };

  const exportWorkflow = () => {
    const workflowData = {
      name: workflowName,
      description: workflowDescription,
      nodes,
      edges
    };
    const dataStr = JSON.stringify(workflowData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `workflow_${workflowName.replace(/\s+/g, '_')}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importWorkflow = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          const importedNodes = data.nodes || [];
          let importedEdges = data.edges || [];

          // Build label → ID map for normalization
          const labelToId = {};
          const idSet = new Set();
          importedNodes.forEach(n => {
            idSet.add(n.id);
            const label = n.data?.label || n.data?.stepData?.name;
            if (label && label !== n.id) {
              labelToId[label] = n.id;
            }
          });

          // Normalize edges: remap targets/sources that use labels to actual IDs
          importedEdges = importedEdges
            .map(edge => {
              const newEdge = { ...edge };
              if (newEdge.source && !idSet.has(newEdge.source) && labelToId[newEdge.source]) {
                newEdge.source = labelToId[newEdge.source];
              }
              if (newEdge.target && !idSet.has(newEdge.target) && labelToId[newEdge.target]) {
                newEdge.target = labelToId[newEdge.target];
              }
              return newEdge;
            })
            // Remove edges that still point to non-existent nodes
            .filter(edge => idSet.has(edge.source) && idSet.has(edge.target));

          // Deduplicate: for edges sharing the same source→target, keep the
          // richest one (has label, has specific sourceHandle like button-N).
          // Use source+target+sourceHandle as dedup key so button-specific
          // edges stay separate from default connection edges.
          const edgeScore = (e) => {
            let s = 0;
            if (e.label) s += 2;
            if (e.sourceHandle && e.sourceHandle !== 'bottom-source' && e.sourceHandle !== 'right-source' && e.sourceHandle !== 'left-source') s += 1;
            return s;
          };
          const edgeMap = new Map();
          importedEdges.forEach(edge => {
            // Key: source + target + sourceHandle (so button-0 and button-1 to same target stay separate)
            const handleKey = edge.sourceHandle || 'default';
            const key = `${edge.source}->${edge.target}::${handleKey}`;
            const existing = edgeMap.get(key);
            if (!existing || edgeScore(edge) > edgeScore(existing)) {
              edgeMap.set(key, edge);
            }
          });
          // Also dedup by just source→target if handles are generic (null, bottom-source etc)
          const finalMap = new Map();
          for (const edge of edgeMap.values()) {
            const simpleKey = `${edge.source}->${edge.target}`;
            const existing = finalMap.get(simpleKey);
            if (!existing || edgeScore(edge) > edgeScore(existing)) {
              finalMap.set(simpleKey, edge);
            }
          }
          importedEdges = Array.from(finalMap.values());

          // Ensure all edges have ReactFlow rendering properties
          importedEdges = importedEdges.map(e => ({
            ...e,
            type: e.type || 'smoothstep',
            animated: e.animated !== undefined ? e.animated : true,
            markerEnd: e.markerEnd || { type: MarkerType.ArrowClosed, width: 15, height: 15 },
            style: e.style || { stroke: e.label ? '#10b981' : '#3b82f6', strokeWidth: 2 },
          }));

          // Normalize stepData: remap next_step and button next_step labels to IDs
          importedNodes.forEach(n => {
            const sd = n.data?.stepData;
            if (!sd) return;
            if (sd.next_step && !idSet.has(sd.next_step) && labelToId[sd.next_step]) {
              sd.next_step = labelToId[sd.next_step];
            }
            (sd.buttons || []).forEach(btn => {
              if (btn.next_step && !idSet.has(btn.next_step) && labelToId[btn.next_step]) {
                btn.next_step = labelToId[btn.next_step];
              }
            });
            (sd.options || []).forEach(opt => {
              if (opt.next_step && !idSet.has(opt.next_step) && labelToId[opt.next_step]) {
                opt.next_step = labelToId[opt.next_step];
              }
            });
          });

          setWorkflowName(data.name || 'Imported Workflow');
          setWorkflowDescription(data.description || '');
          setNodes(importedNodes);
          setEdges(importedEdges);
          showToast(`Imported ${importedNodes.length} nodes, ${importedEdges.length} edges`, 'success');
        } catch (error) {
          showToast('Failed to import workflow — invalid JSON', 'error');
        }
      };
      reader.readAsText(file);
    }
  };

  const loadWorkflow = async (id) => {
    try {
      const response = await apiCall(`/api/chatbot/workflows/${id}`);
      if (response.ok) {
        const data = await response.json();
        setWorkflowId(data.workflow.id);
        setWorkflowName(data.workflow.name);
        setWorkflowDescription(data.workflow.description);
        setNodes(data.workflow.nodes || []);
        setEdges(data.workflow.edges || []);
        setView('designer');
        setTestMode(false)
        setFloatingWorkflowMode(false)
        fetchApiKeyInfo(data.workflow.id)
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
      showToast('Failed to load workflow', 'error');
    }
  };

  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false);
  const [apiConnectionsOpen, setApiConnectionsOpen] = useState(false);
  const [usageReportOpen, setUsageReportOpen] = useState(false);

  const createNewWorkflow = () => {
    setShowTemplatePicker(true);
  };

  const createFromTemplate = (templateNodes, templateEdges, templateName) => {
    setWorkflowId(null);
    setWorkflowName(templateName || 'New Workflow');
    setWorkflowDescription('');
    setNodes(templateNodes || []);
    // Ensure template edges have ReactFlow rendering properties
    const normalizedEdges = (templateEdges || []).map(e => ({
      ...e,
      type: e.type || 'smoothstep',
      animated: e.animated !== undefined ? e.animated : true,
      markerEnd: e.markerEnd || { type: MarkerType.ArrowClosed, width: 15, height: 15 },
      style: e.style || { stroke: e.label ? '#10b981' : '#3b82f6', strokeWidth: 2 },
    }));
    setEdges(normalizedEdges);
    setSelectedNode(null);
    setView('designer');
    setShowTemplatePicker(false);
  };

  const createBlankWorkflow = () => {
    setWorkflowId(null);
    setWorkflowName('New Workflow');
    setWorkflowDescription('');
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
    setView('designer');
    setShowTemplatePicker(false);
  };

  const handleAIGenerated = (workflow) => {
    setWorkflowId(null);
    setWorkflowName(workflow.name || 'AI Generated Workflow');
    setWorkflowDescription(workflow.description || '');
    const generatedNodes = (workflow.nodes || []).map(node => ({
      ...node,
      type: node.type || 'stepNode',
      data: {
        ...node.data,
        label: node.data?.label || node.data?.stepData?.name || node.id,
      },
    }));
    const generatedEdges = (workflow.edges || []).map(edge => ({
      ...edge,
      type: edge.type || 'smoothstep',
      animated: edge.animated !== false,
      markerEnd: edge.markerEnd || { type: MarkerType.ArrowClosed, width: 15, height: 15 },
      style: edge.style || { stroke: edge.label ? '#10b981' : '#3b82f6', strokeWidth: 2 },
    }));
    setNodes(generatedNodes);
    setEdges(generatedEdges);
    setSelectedNode(null);
    setView('designer');
    setAiGeneratorOpen(false);
  };

  const backToList = () => {
    setView('list');
  };

  // Fetch existing API key info (masked)
  const fetchApiKeyInfo = async (workflowId) => {
    
    try {
      setApiKeyLoading(true);
      const response = await apiCallJson(
        `/api/chatbot/workflows/${workflowId}/api-key`,
        {method: 'GET'}
      );
      setApiKey(response);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch API key:', error);
      }
      setApiKey(null);
    } finally {
      setApiKeyLoading(false);
    }
  };

  // Generate new API key
  const generateApiKey = async () => {
    if (!workflowId) return;
    
    const ok = await confirm({
      title: 'Generate New API Key',
      message: 'This will invalidate the previous key if one exists. External integrations using the old key will stop working.',
      variant: 'warning',
      confirmText: 'Generate'
    });
    if (!ok) return;
    
    try {
      setApiKeyLoading(true);
      const response = await apiCallJson(
        `/api/chatbot/workflows/${workflowId}/api-key`,
        {method: 'POST'}
      );
      
      setApiKey({
        api_key: response.api_key,
        created_at: new Date().toISOString(),
        is_active: true
      });
      setShowApiKey(true);
      showToast('API key generated! Copy it now — it won\'t be shown again.', 'success', 8000);
    } catch (error) {
      console.error('Failed to generate API key:', error);
      showToast('Failed to generate API key', 'error');
    } finally {
      setApiKeyLoading(false);
    }
  };

  // Revoke API key
  const revokeApiKey = async () => {
    if (!workflowId) return;
    
    const ok = await confirm({
      title: 'Revoke API Key',
      message: 'This will immediately stop all external access using this key. This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Revoke'
    });
    if (!ok) return;
    
    try {
      setApiKeyLoading(true);
      await apiCall(
        `/api/chatbot/workflows/${workflowId}/api-key`,
        {method: 'DELETE'}
      );
      
      setApiKey(null);
      showToast('API key revoked successfully', 'success');
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      showToast('Failed to revoke API key', 'error');
    } finally {
      setApiKeyLoading(false);
    }
  };

  // Copy API key to clipboard
  const copyApiKey = () => {
    // Copy the full key if available (just generated), otherwise the display version
    const keyToCopy = apiKey?.api_key || apiKey?.api_key_display;
    if (!keyToCopy) return;
    
    navigator.clipboard.writeText(keyToCopy).then(() => {
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    });
  };

  if (view === 'list') {
    return (
      <>
        <WorkflowManager 
          user={user}
          onLoadWorkflow={loadWorkflow}
          onCreateNew={createNewWorkflow}
          onManageConnections={() => setApiConnectionsOpen(true)}
          onUsageReport={() => setUsageReportOpen(true)}
        />
        {showTemplatePicker && (
          <TemplatePicker
            onSelectBlank={createBlankWorkflow}
            onSelectTemplate={createFromTemplate}
            onClose={() => setShowTemplatePicker(false)}
          />
        )}
        {usageReportOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[96rem] h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
                <h3 className="font-semibold text-gray-800">Chatbot · Usage Report</h3>
                <button onClick={() => setUsageReportOpen(false)}
                  className="text-gray-400 hover:text-gray-700" title="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <ChatbotUsageReport />
              </div>
            </div>
          </div>
        )}
        {apiConnectionsOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
                <h3 className="font-semibold text-gray-800">Chatbot · API Connections</h3>
                <button onClick={() => setApiConnectionsOpen(false)}
                  className="text-gray-400 hover:text-gray-700" title="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <ApiConnectionsManager user={user} />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 relative">
      <div className="flex-1 flex flex-col">
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={backToList}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Workflows</span>
              </button>
              <div className="border-l border-gray-300 h-8"></div>
              <Workflow className="h-6 w-6 text-blue-600" />
              <div>
                <input
                  type="text"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  className="text-xl font-semibold border-none outline-none focus:ring-2 focus:ring-blue-500 rounded px-2"
                />
                <input
                  type="text"
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  placeholder="Add description..."
                  className="text-sm text-gray-500 border-none outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 mt-1 w-full"
                />
              </div>
            </div>
            <div className="flex items-center space-x-1.5 flex-nowrap">
              {/* ── Panel toggles ── */}
              <button
                onClick={() => { setTranslationsOpen(!translationsOpen); if (!translationsOpen) { setTestMode(false); setAgentDashboardOpen(false); setVersionHistoryOpen(false); setAnalyticsOpen(false); } }}
                disabled={!workflowId}
                className={`flex items-center space-x-1 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                  translationsOpen ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300 text-gray-700'
                } hover:opacity-90 disabled:bg-gray-200 disabled:cursor-not-allowed`}
                title="Multi-language translations"
              >
                <Globe className="h-4 w-4" />
                <span className="hidden xl:inline">i18n</span>
              </button>
              <button
                onClick={() => { setVersionHistoryOpen(!versionHistoryOpen); if (!versionHistoryOpen) { setTestMode(false); setAgentDashboardOpen(false); setTranslationsOpen(false); setAnalyticsOpen(false); } }}
                disabled={!workflowId}
                className={`flex items-center space-x-1 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                  versionHistoryOpen ? 'bg-gray-800 text-white' : 'bg-white border border-gray-300 text-gray-700'
                } hover:opacity-90 disabled:bg-gray-200 disabled:cursor-not-allowed`}
                title="Version history"
              >
                <History className="h-4 w-4" />
              </button>

              <div className="w-px h-6 bg-gray-300 mx-0.5" />

              {/* ── Test ── */}
              <button
                onClick={() => { setTestMode(!testMode); if (!testMode) { setAgentDashboardOpen(false); setTranslationsOpen(false); setVersionHistoryOpen(false); setAnalyticsOpen(false); } }}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm ${
                  testMode ? 'bg-green-600 text-white' : 'bg-white border border-gray-300 text-gray-700'
                } hover:opacity-90`}
              >
                <Play className="h-4 w-4" />
                <span>{testMode ? 'Previewing' : 'Preview'}</span>
              </button>
              <button
                onClick={() => setFloatingWorkflowMode(!floatingWorkflowMode)}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm bg-white border border-gray-300 text-gray-700 hover:opacity-90`}
              >
                <Play className="h-4 w-4" />
                <span>Test</span>
              </button>

              <div className="w-px h-6 bg-gray-300 mx-0.5" />

              {/* ── Edit: Undo/Redo + Save ── */}
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => { const prev = undo(); if (prev) { prepareUndo({ nodes, edges }); setNodes(prev.nodes); setEdges(prev.edges); } }}
                  disabled={!canUndo}
                  className="p-2 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Undo (Ctrl+Z)"
                >
                  <Undo2 className="h-4 w-4" />
                </button>
                <div className="w-px h-6 bg-gray-300" />
                <button
                  onClick={() => { const next = redo(); if (next) { pushState({ nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) }); setNodes(next.nodes); setEdges(next.edges); } }}
                  disabled={!canRedo}
                  className="p-2 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Redo (Ctrl+Y)"
                >
                  <Redo2 className="h-4 w-4" />
                </button>
              </div>

              <button
                onClick={() => saveWorkflow()}
                disabled={saving}
                className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                <Save className="h-4 w-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
              {lastSavedAt && (
                <span className="text-xs text-gray-400 self-center hidden lg:inline" title={lastSavedAt.toLocaleString()}>
                  {lastSavedAt.toLocaleTimeString()}
                </span>
              )}

              <div className="w-px h-6 bg-gray-300 mx-0.5" />

              {/* ── Settings dropdown: Deploy, Share, API Key, Export, Import ── */}
              <div className="relative">
                <button
                  onClick={() => setToolbarMenuOpen(!toolbarMenuOpen)}
                  className="p-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  title="More actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
                {toolbarMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setToolbarMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50 w-52">
                      <button
                        onClick={() => { setChannelsOpen(true); setToolbarMenuOpen(false); }}
                        disabled={!workflowId}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Radio className="h-4 w-4 text-green-600" />
                        <span>Deploy to Channels</span>
                      </button>
                      <button
                        onClick={() => { setJobDashboardOpen(true); setToolbarMenuOpen(false); }}
                        disabled={!workflowId}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <BarChart3 className="h-4 w-4 text-indigo-600" />
                        <span>Campaign Jobs</span>
                      </button>
                      <button
                        onClick={() => { setPermissionsOpen(true); setToolbarMenuOpen(false); }}
                        disabled={!workflowId}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Users className="h-4 w-4 text-blue-600" />
                        <span>Share / Permissions</span>
                      </button>
                      <button
                        onClick={() => { setApiKeyModalOpen(true); setToolbarMenuOpen(false); }}
                        disabled={!workflowId}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Key className="h-4 w-4 text-purple-600" />
                        <span>API Key</span>
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => { exportWorkflow(); setToolbarMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Download className="h-4 w-4 text-gray-500" />
                        <span>Export Workflow</span>
                      </button>
                      <label className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                        <Upload className="h-4 w-4 text-gray-500" />
                        <span>Import Workflow</span>
                        <input
                          type="file"
                          accept=".json"
                          onChange={(e) => { importWorkflow(e); setToolbarMenuOpen(false); }}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 relative">
            <EnhancedCanvas
              nodes={nodes}
              edges={edges}
              setNodes={setNodes}
              setEdges={setEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onEdgeClick={onEdgeClick}
              onPaneClick={onPaneClick}
              selectedNode={selectedNode}
              workflowName={workflowName}
              showNodeMenu={showNodeMenu}
              setShowNodeMenu={setShowNodeMenu}
              addNewNode={addNewNode}
              groups={groups}
            />
          </div>

          {testMode && (
            <div className="w-96 border-l border-gray-200 bg-white">
              <ChatPreview workflowId={workflowId} nodes={nodes} edges={edges} />
            </div>
          )}

          {translationsOpen && workflowId && (
            <div className="w-[420px] border-l border-gray-200 bg-white flex flex-col">
              <TranslationEditor
                workflowId={workflowId}
                nodes={nodes}
                translations={translations}
                setTranslations={setTranslations}
                defaultLanguage={defaultLanguage}
                setDefaultLanguage={setDefaultLanguage}
              />
            </div>
          )}

          {versionHistoryOpen && workflowId && (
            <div className="w-[420px] border-l border-gray-200 bg-white flex flex-col">
              <VersionHistoryPanel
                workflowId={workflowId}
                currentNodes={nodes}
                currentEdges={edges}
                onRollback={(versionData) => {
                  setNodes(versionData.nodes || []);
                  setEdges(versionData.edges || []);
                  if (versionData.name) setWorkflowName(versionData.name);
                  if (versionData.description) setWorkflowDescription(versionData.description);
                  showToast(`Rolled back to v${versionData.version}`, 'success');
                }}
              />
            </div>
          )}

          {floatingWorkflowMode && (
            <FloatingChatWidget 
                workflowId={workflowId}
                apiEndpoint="http://localhost:8000"
                apiKey={apiKey}
              />
          )}

      {selectedNode && !testMode && !agentDashboardOpen && !translationsOpen && !versionHistoryOpen && !analyticsOpen && (
        <div className="w-[420px] xl:w-[560px] 2xl:w-[620px] bg-white border-l border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-800">Properties</h2>
            <button
              onClick={() => setSelectedNode(null)}
              className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-200 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <StepProperties
            step={selectedNode.data.stepData}
            stepId={selectedNode.id}
            onUpdate={updateNodeData}
            onDelete={deleteNode}
            nodes={nodes}
            selectedNode={selectedNode}
            workflowId={workflowId}
          />
        </div>
      )}

        </div>
      </div>

      {/* API Key Modal */}
      {apiKeyModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Key className="h-6 w-6" />
                  <span>API Key Management</span>
                </h2>
                <p className="text-sm text-purple-100 mt-1">
                  For external access to workflow: {workflowName}
                </p>
              </div>
              <button
                onClick={() => {
                  setApiKeyModalOpen(false);
                  setShowApiKey(false);
                  setApiKeyCopied(false);
                }}
                className="text-white hover:text-purple-200 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">What is an API Key?</p>
                  <p>
                    API keys allow external websites and applications to access your chatbot without admin credentials. 
                    Use this for embedding your chatbot on customer websites or integrating with third-party systems.
                  </p>
                </div>
              </div>

              {/* API Key Status */}
              {apiKeyLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <p className="text-sm text-gray-600 mt-2">Loading API key information...</p>
                </div>
              ) : apiKey ? (
                <>
                  {/* Existing API Key */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800 flex items-center space-x-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        <span>Active API Key</span>
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        apiKey.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {apiKey.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* API Key Display */}
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API Key
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type={showApiKey ? "text" : "password"}
                          value={apiKey.api_key || apiKey.api_key_display || ''}
                          readOnly
                          className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 font-mono text-sm"
                        />
                        <button
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="p-2 hover:bg-gray-200 rounded transition-colors"
                          title={showApiKey ? "Hide" : "Show"}
                        >
                          {showApiKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                        <button
                          onClick={copyApiKey}
                          className={`p-2 rounded transition-colors ${
                            apiKeyCopied 
                              ? 'bg-green-100 text-green-600' 
                              : 'hover:bg-gray-200'
                          }`}
                          title="Copy to clipboard"
                        >
                          {apiKeyCopied ? <CheckCircle2 className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                        </button>
                      </div>
                      {apiKeyCopied && (
                        <p className="text-xs text-green-600 mt-1">✓ Copied to clipboard!</p>
                      )}
                    </div>

                    {/* Key Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Created
                        </label>
                        <p className="text-sm text-gray-600">
                          {new Date(apiKey.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {apiKey.last_used_at && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Last Used
                          </label>
                          <p className="text-sm text-gray-600">
                            {new Date(apiKey.last_used_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {apiKey.usage_count !== undefined && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Requests
                          </label>
                          <p className="text-sm text-gray-600">
                            {apiKey.usage_count.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Usage Example */}
                    <div className="bg-gray-900 text-gray-100 rounded-lg p-4">
                      <label className="block text-sm font-medium mb-2 text-gray-300">
                        Usage Example (React Component)
                      </label>
                      <pre className="text-xs overflow-x-auto">
                        {`<WorkflowChatPreview 
                          workflowId="${workflowId}"
                          apiEndpoint="${window.location.origin}"
                          apiKey="${apiKey.api_key || apiKey.api_key_display || 'YOUR_API_KEY'}"
                        />`}
                      </pre>
                    </div>

                    {/* Warning */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        <strong>⚠️ Security Notice:</strong> Keep this API key secure. 
                        Anyone with this key can use your chatbot. Don't commit it to version control.
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-3">
                      <button
                        onClick={generateApiKey}
                        disabled={apiKeyLoading}
                        className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
                      >
                        Regenerate Key
                      </button>
                      <button
                        onClick={revokeApiKey}
                        disabled={apiKeyLoading}
                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors"
                      >
                        Revoke Key
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* No API Key - Generate New */}
                  <div className="text-center py-8 space-y-4">
                    <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                      <Key className="h-8 w-8 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">
                        No API Key Generated
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Generate an API key to allow external access to this chatbot workflow.
                      </p>
                    </div>
                    <button
                      onClick={generateApiKey}
                      disabled={apiKeyLoading}
                      className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors font-semibold"
                    >
                      Generate API Key
                    </button>
                  </div>
                </>
              )}

              {/* Documentation Link */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    Need help integrating your chatbot?
                  </span>
                  <a
                    href="/docs/api-integration" // Update with your actual docs URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 flex items-center space-x-1"
                  >
                    <span>View Documentation</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Modal */}
      <ValidationModal
        isOpen={validationModal.isOpen}
        errors={validationModal.errors}
        warnings={validationModal.warnings}
        onClose={() => setValidationModal({ isOpen: false, errors: [], warnings: [] })}
        onFix={() => setValidationModal({ isOpen: false, errors: [], warnings: [] })}
        onSaveAnyway={() => { setValidationModal({ isOpen: false, errors: [], warnings: [] }); saveWorkflow(true); }}
      />

      {billTo.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Who is this workflow for?</h3>
              <p className="text-sm text-gray-500">Required — its message flow is billed to this customer.</p>
            </div>
            <select value={billTo.choice} onChange={(e) => setBillTo({ ...billTo, choice: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">Select a customer…</option>
              {billTo.buyers.map((b) => <option key={b.customer_id} value={b.customer_id}>{b.name || b.customer_id}</option>)}
              {billTo.canCreate && <option value="__new__">New customer…</option>}
            </select>
            {billTo.choice === '__new__' && (
              <div className="space-y-2">
                <input autoFocus value={billTo.newName} onChange={(e) => setBillTo({ ...billTo, newName: e.target.value })} placeholder="Customer name" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                {billTo.newName.trim() && (billToMatch ? (
                  <p className="text-xs text-emerald-700">Matches existing customer {billToMatch.name || billToMatch.customer_id} — it will be used.</p>
                ) : (
                  <p className="text-xs text-amber-700">Customer “{billTo.newName.trim()}” does not exist yet and will be created with this name.</p>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => setBillTo({ ...billTo, open: false })} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
              <button onClick={confirmBillTo} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                {billTo.choice === '__new__' && billTo.newName.trim() && !billToMatch ? 'Create customer & save' : 'Save workflow'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm & Toast overlays */}
      <ConfirmDialog />
      <ToastContainer />

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <TemplatePicker
          onSelectBlank={createBlankWorkflow}
          onSelectTemplate={createFromTemplate}
          onClose={() => setShowTemplatePicker(false)}
        />
      )}

      {/* Entity Permission Manager */}
      <EntityPermissionManager
        entityType="chatbot_workflow"
        entityId={workflowId}
        entityName={workflowName}
        isOpen={permissionsOpen}
        onClose={() => setPermissionsOpen(false)}
      />

      {/* Channel Deployment Manager */}
      <ChannelDeploymentManager
        workflowId={workflowId}
        workflowName={workflowName}
        isOpen={channelsOpen}
        onClose={() => setChannelsOpen(false)}
      />

      {/* Job Dashboard Modal */}
      {jobDashboardOpen && workflowId && (
        <JobDashboardModal
          workflowId={workflowId}
          onClose={() => setJobDashboardOpen(false)}
        />
      )}
    </div>
  );
};

// ==================== Job Dashboard Modal ====================
const JobDashboardModal = ({ workflowId, onClose }) => {
  const [campaignNodes, setCampaignNodes] = useState([]);
  const [jobNodes, setJobNodes] = useState([]);
  const [runs, setRuns] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Load campaign nodes + job nodes + job schedules
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [nodesResp, jobsResp] = await Promise.all([
          apiCallJson(`/api/chatbot/workflows/${workflowId}/campaign/nodes`),
          apiCallJson(`/api/chatbot/workflows/${workflowId}/jobs`).catch(() => ({ jobs: [] })),
        ]);
        setCampaignNodes(nodesResp.campaign_nodes || []);
        setJobNodes(nodesResp.job_nodes || []);
        setJobs(jobsResp.jobs || []);
        if (nodesResp.campaign_nodes?.length > 0) {
          setSelectedNode(nodesResp.campaign_nodes[0].node_id);
        }
      } catch (err) {
        console.error('Failed to load job dashboard:', err);
      }
      setLoading(false);
    })();
  }, [workflowId]);

  // Load status + runs for selected node
  useEffect(() => {
    if (!selectedNode) return;
    (async () => {
      try {
        const [statusResp, runsResp] = await Promise.all([
          apiCallJson(`/api/chatbot/workflows/${workflowId}/campaign/${selectedNode}/status`),
          apiCallJson(`/api/chatbot/workflows/${workflowId}/campaign/${selectedNode}/runs?limit=10`),
        ]);
        setStatus(statusResp);
        setRuns(runsResp.runs || []);
      } catch (err) {
        console.error('Failed to load status:', err);
      }
    })();
  }, [workflowId, selectedNode]);

  const handleSync = async () => {
    if (!selectedNode) return;
    setSyncing(true);
    try {
      const resp = await apiCallJson(
        `/api/chatbot/workflows/${workflowId}/campaign/${selectedNode}/writeback?format=json`,
        { method: 'POST' }
      );
      alert(`✅ ${resp.message}`);
    } catch (err) {
      alert(`❌ Sync failed: ${err.message || err}`);
    }
    setSyncing(false);
  };

  const s = status?.summary || {};
  const lr = status?.latest_run || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-800">Campaign Jobs</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5 text-gray-400" /></button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 text-gray-400 animate-spin" />
          </div>
        ) : campaignNodes.length === 0 && jobNodes.length === 0 ? (
          <div className="flex-1 flex items-center justify-center py-12 text-gray-500 text-sm">
            No campaign or job nodes found in this workflow.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {/* Node selector */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-600">Campaign Node:</label>
              <select
                value={selectedNode || ''}
                onChange={(e) => setSelectedNode(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1"
              >
                {campaignNodes.map(n => (
                  <option key={n.node_id} value={n.node_id}>
                    {n.label || n.node_id} ({n.type})
                  </option>
                ))}
              </select>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Run Sync Now'}
              </button>
            </div>

            {/* Summary Cards */}
            {status && (
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Sent', value: s.sent || 0, color: 'text-blue-600 bg-blue-50' },
                  { label: 'Replied', value: s.replied || 0, color: 'text-green-600 bg-green-50' },
                  { label: 'Completed', value: s.completed || 0, color: 'text-emerald-600 bg-emerald-50' },
                  { label: 'Callbacks', value: s.callbacks_pending || 0, color: 'text-orange-600 bg-orange-50' },
                ].map(card => (
                  <div key={card.label} className={`rounded-xl p-3 ${card.color}`}>
                    <div className="text-2xl font-bold">{card.value}</div>
                    <div className="text-xs font-medium opacity-75">{card.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Secondary stats row */}
            {status && (
              <div className="grid grid-cols-5 gap-2 text-center">
                {[
                  { label: 'Total', value: s.total || 0 },
                  { label: 'Failed', value: s.failed || 0 },
                  { label: 'Engaged', value: s.engaged || 0 },
                  { label: 'Written Back', value: s.written_back || 0 },
                  { label: 'Pending WB', value: s.writeback_pending || 0 },
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 rounded-lg p-2">
                    <div className="text-lg font-semibold text-gray-800">{item.value}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">{item.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Latest Run */}
            {lr.status && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Latest Run</h3>
                <div className="flex items-center gap-4 text-sm">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    lr.status === 'completed' ? 'bg-green-100 text-green-700' :
                    lr.status === 'running' ? 'bg-blue-100 text-blue-700 animate-pulse' :
                    'bg-red-100 text-red-700'
                  }`}>{lr.status}</span>
                  <span className="text-gray-600">{lr.sent_count || 0} sent</span>
                  <span className="text-gray-600">{lr.skipped_count || 0} skipped</span>
                  <span className="text-gray-600">{lr.failed_count || 0} failed</span>
                  {lr.started_at && (
                    <span className="text-gray-400 text-xs ml-auto">
                      {new Date(lr.started_at).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Run History */}
            {runs.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Run History</h3>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2 text-right">Sent</th>
                        <th className="px-4 py-2 text-right">Skipped</th>
                        <th className="px-4 py-2 text-right">Failed</th>
                        <th className="px-4 py-2 text-right">Duration</th>
                        <th className="px-4 py-2 text-right">Started</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {runs.map(run => (
                        <tr key={run.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              run.status === 'completed' ? 'bg-green-100 text-green-700' :
                              run.status === 'running' ? 'bg-blue-100 text-blue-700' :
                              'bg-red-100 text-red-700'
                            }`}>{run.status}</span>
                          </td>
                          <td className="px-4 py-2 text-right text-gray-700">{run.sent_count}</td>
                          <td className="px-4 py-2 text-right text-gray-500">{run.skipped_count}</td>
                          <td className="px-4 py-2 text-right text-red-600">{run.failed_count}</td>
                          <td className="px-4 py-2 text-right text-gray-500">
                            {run.duration_seconds != null ? `${run.duration_seconds}s` : '-'}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-400 text-xs">
                            {run.started_at ? new Date(run.started_at).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Workflow Job Nodes */}
            {jobNodes.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Job Nodes in Workflow</h3>
                <div className="space-y-2">
                  {jobNodes.map(jn => {
                    const isSync = jn.type === 'eod_sync_job';
                    const isReminder = jn.type === 'reminder_job';
                    const isCallback = jn.type === 'request_callback';
                    const config = jn.config || {};
                    // Find matching DB schedule if any
                    const dbJob = jobs.find(j => j.job_node_id === jn.node_id);
                    return (
                      <div key={jn.node_id} className={`border rounded-xl p-3 ${
                        isSync ? 'border-indigo-200 bg-indigo-50' :
                        isReminder ? 'border-violet-200 bg-violet-50' :
                        'border-orange-200 bg-orange-50'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isSync ? <RefreshCw className="h-4 w-4 text-indigo-600" /> :
                             isReminder ? <Bell className="h-4 w-4 text-violet-600" /> :
                             <PhoneCall className="h-4 w-4 text-orange-600" />}
                            <span className="text-sm font-medium text-gray-800">{jn.label}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/60 text-gray-600">
                              {jn.type.replace('_', ' ')}
                            </span>
                          </div>
                          {isSync && jn.campaign_node_id && (
                            <button
                              onClick={async () => {
                                setSyncing(true);
                                try {
                                  const f = config.format || 'json';
                                  const col = config.writeback_column || 'response_summary';
                                  const resp = await apiCallJson(
                                    `/api/chatbot/workflows/${workflowId}/campaign/${jn.campaign_node_id}/writeback?format=${f}&writeback_column=${col}`,
                                    { method: 'POST' }
                                  );
                                  alert(`✅ ${resp.message}`);
                                } catch (err) {
                                  alert(`❌ ${err.message || err}`);
                                }
                                setSyncing(false);
                              }}
                              disabled={syncing}
                              className="flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                              <RefreshCw className={`h-3 w-3 ${syncing ? 'animate-spin' : ''}`} />
                              Run Now
                            </button>
                          )}
                        </div>
                        <div className="mt-1.5 text-xs text-gray-600 space-y-0.5">
                          {isSync && (
                            <>
                              <p>Campaign: <span className="font-mono text-gray-800">{jn.campaign_node_id || '—'}</span></p>
                              <p>Schedule: {config.run_at_time || '—'} {config.timezone || ''} | Column: {config.writeback_column || 'response_summary'} | Format: {config.format || 'json'}</p>
                            </>
                          )}
                          {isReminder && (
                            <>
                              <p>Campaign: <span className="font-mono text-gray-800">{jn.campaign_node_id || '—'}</span></p>
                              <p>Template: {config.template_name || '—'} | Every {config.interval_hours || 24}h | Max {config.max_reminders || 3} | Target: {config.target || 'no_reply'}</p>
                            </>
                          )}
                          {isCallback && <p>Message: "{jn.message || 'Thank you! We will call you back shortly.'}"</p>}
                          {dbJob && (
                            <p className="mt-1 text-gray-500">
                              {dbJob.last_run_at ? `Last run: ${new Date(dbJob.last_run_at).toLocaleString()} — ${dbJob.last_run_status}` : 'Never run via scheduler'}
                              {dbJob.next_run_at && ` | Next: ${new Date(dbJob.next_run_at).toLocaleString()}`}
                            </p>
                          )}
                          {!dbJob && (isSync || isReminder) && (
                            <p className="mt-1 text-amber-600">⚠ Not yet registered with the background scheduler</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No nodes at all */}
            {campaignNodes.length === 0 && jobNodes.length === 0 && (
              <div className="text-center text-gray-500 text-sm py-8">
                No campaign or job nodes found in this workflow.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const WorkflowManager = ({ user, onLoadWorkflow, onCreateNew, onManageConnections, onUsageReport }) => {
  const [workflows, setWorkflows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Read/output surfaces opened from a workflow CARD (not the designer): analytics
  // and agent conversations for a workflow, without entering edit mode. Hold the
  // workflow object (or null).
  const [cardAnalytics, setCardAnalytics] = useState(null);
  const [cardAgents, setCardAgents] = useState(null);

  useEffect(() => {
    loadWorkflows();
  }, [filterStatus]);

  const loadWorkflows = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      
      const response = await apiCall(`/api/chatbot/workflows?${params.toString()}`);
      
      if (response.ok) {
        const data = await response.json();
        setWorkflows(data.workflows);
      } else {
        throw new Error('Failed to load workflows');
      }
    } catch (err) {
      console.error('Error loading workflows:', err);
      setError('Failed to load workflows. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteWorkflow = async (workflowId, workflowName) => {
    if (!window.confirm(`Are you sure you want to delete "${workflowName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiCall(`/api/chatbot/workflows/${workflowId}/hard`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadWorkflows();
      } else {
        throw new Error('Failed to delete workflow');
      }
    } catch (err) {
      console.error('Error deleting workflow:', err);
    }
  };

  const duplicateWorkflow = async (workflow) => {
    try {
      const response = await apiCall(`/api/chatbot/workflows/${workflow.id}`);
      
      if (response.ok) {
        const fullWorkflow = await response.json();
        
        // Create duplicate with modified name
        const duplicateResponse = await apiCall('/api/chatbot/workflows', {
          method: 'POST',
          body: JSON.stringify({
            name: `${fullWorkflow.name} (Copy)`,
            description: fullWorkflow.description,
            nodes: fullWorkflow.nodes,
            edges: fullWorkflow.edges,
            status: 'draft'
          })
        });

        if (duplicateResponse.ok) {
          await loadWorkflows();
          
        } else {
          throw new Error('Failed to duplicate workflow');
        }
      }
    } catch (err) {
      console.error('Error duplicating workflow:', err);
      
    }
  };

  const exportWorkflow = async (workflowId) => {
    try {
      const response = await apiCall(`/api/chatbot/workflows/${workflowId}`);
      
      if (response.ok) {
        const workflow = await response.json();
        const dataStr = JSON.stringify(workflow, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        const exportFileDefaultName = `workflow_${workflow.name.replace(/\s+/g, '_')}.json`;

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      }
    } catch (err) {
      console.error('Error exporting workflow:', err);
      
    }
  };

  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (workflow.description && workflow.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Chatbot Workflows"
        icon={MessageSquare}
        actions={
          <>
            <button
              onClick={onCreateNew}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              <span>New Workflow</span>
            </button>
            <button
              onClick={onManageConnections}
              className="flex items-center space-x-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50"
            >
              <ShieldCheck className="h-4 w-4" />
              <span>API Connections</span>
            </button>
            <button
              onClick={onUsageReport}
              className="flex items-center space-x-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50"
            >
              <BarChart3 className="h-4 w-4" />
              <span>Usage Report</span>
            </button>
            <button
              onClick={loadWorkflows}
              disabled={loading}
              className="flex items-center space-x-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </>
        }
      />
      <PageBody>
        {/* Filters and Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search workflows by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredWorkflows.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="max-w-md mx-auto">
              <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {workflows.length === 0 ? 'No workflows yet' : 'No workflows found'}
              </h3>
              <p className="text-gray-500 mb-6">
                {workflows.length === 0 
                  ? "Get started by creating your first chatbot workflow."
                  : "No workflows match your search criteria."
                }
              </p>
              {workflows.length === 0 && (
                <button
                  onClick={onCreateNew}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                >
                  Create First Workflow
                </button>
              )}
            </div>
          </div>
        )}

        {/* Workflows Grid */}
        {!loading && filteredWorkflows.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorkflows.map((workflow) => (
              <div key={workflow.id} className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 text-lg truncate flex-1 mr-3">
                      {workflow.name}
                    </h3>
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(workflow.status)}`}>
                      {workflow.status}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-[40px]">
                    {workflow.description || 'No description provided'}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(workflow.updated_at)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <BarChart3 className="h-3 w-3" />
                        <span>{workflow.steps_count || 0} steps</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 mb-4">
                    <div>Created by: <span className="font-medium">{workflow.created_by}</span></div>
                    {workflow.created_at && (
                      <div>Created: {formatDate(workflow.created_at)}</div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onLoadWorkflow(workflow.id)}
                        className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        <Edit3 className="h-3 w-3" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => {/* Implement test functionality */}}
                        className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200"
                      >
                        <Play className="h-3 w-3" />
                        <span>Test</span>
                      </button>
                    </div>
                    
                    <div className="flex space-x-1">
                      <button
                        onClick={() => setCardAnalytics(workflow)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Analytics"
                      >
                        <BarChart3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setCardAgents(workflow)}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                        title="Conversations / Agents"
                      >
                        <UserCheck className="h-4 w-4" />
                      </button>
                      <div className="w-px h-6 bg-gray-200 mx-0.5 self-center" />
                      <button
                        onClick={() => duplicateWorkflow(workflow)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => exportWorkflow(workflow.id)}
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                        title="Export"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteWorkflow(workflow.id, workflow.name)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {!loading && workflows.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-600">Total Workflows</div>
              <div className="text-2xl font-bold text-gray-900">{workflows.length}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-600">Published</div>
              <div className="text-2xl font-bold text-green-600">
                {workflows.filter(w => w.status === 'published').length}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-600">Drafts</div>
              <div className="text-2xl font-bold text-yellow-600">
                {workflows.filter(w => w.status === 'draft').length}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-600">Archived</div>
              <div className="text-2xl font-bold text-gray-600">
                {workflows.filter(w => w.status === 'archived').length}
              </div>
            </div>
          </div>
        )}
      </PageBody>

      {/* Card-level read/output overlays (opened from a workflow card, not the
          designer): analytics and agent conversations for that workflow. */}
      {cardAnalytics && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <AnalyticsDashboard
            workflowId={cardAnalytics.id}
            workflowName={cardAnalytics.name}
            onClose={() => setCardAnalytics(null)}
          />
        </div>
      )}
      {cardAgents && (
        <div className="fixed inset-0 z-50 bg-black/30 flex justify-end">
          <div className="w-full max-w-[520px] bg-white h-full shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 h-14 border-b border-gray-200 flex-shrink-0">
              <div className="font-semibold text-gray-800 truncate">
                Conversations — {cardAgents.name}
              </div>
              <button onClick={() => setCardAgents(null)}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <AgentHandoffDashboard
                workflowId={cardAgents.id}
                authToken={getCurrentToken()}
                user={user}
              />
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

// Scan every node in the workflow for variable names the user can reference in
// {{...}} templates: variables collected by steps, API response variables, and
// variables named in conditions. Returns grouped, de-duplicated, sorted lists.
// (Campaign contact columns are dynamic at runtime and can't be enumerated here.)
const RESERVED_WORDS = new Set(['and', 'or', 'not', 'in', 'is', 'if', 'else',
  'true', 'false', 'none', 'True', 'False', 'None', 'null']);
function collectWorkflowVariables(nodes) {
  const collected = new Set();
  const response = new Set();
  const customerMemory = new Set();
  const addResponse = (rv, rm) => {
    if (rv) { response.add(`${rv}_ok`); response.add(`${rv}_status`); response.add(`${rv}_response`); }
    Object.keys(rm || {}).forEach(k => k && response.add(k));
  };
  const addPersist = (list) => {
    (list || []).forEach(name => { if (name) { collected.add(name); customerMemory.add(`customer_${name}`); } });
  };
  const scanCondition = (expr) => {
    if (!expr) return;
    // strip quoted literals so string contents aren't picked up as variables
    const cleaned = String(expr).replace(/'[^']*'|"[^"]*"/g, ' ');
    (cleaned.match(/[A-Za-z_][A-Za-z0-9_]*/g) || []).forEach(tok => {
      if (!RESERVED_WORDS.has(tok)) collected.add(tok);
    });
  };
  (nodes || []).forEach(n => {
    const sd = (n.data && n.data.stepData) || {};
    Object.keys(sd.variable_mappings || {}).forEach(k => k && collected.add(k));
    if (sd.save_response_as) collected.add(sd.save_response_as);
    if (sd.file_upload_config && sd.file_upload_config.variable_name)
      collected.add(sd.file_upload_config.variable_name);
    // Sheet/table "Column → Variable" mappings define variables (values = names).
    Object.values((sd.sheet_config && sd.sheet_config.column_mappings) || {}).forEach(v => v && collected.add(v));
    Object.values((sd.table_config && sd.table_config.column_mappings) || {}).forEach(v => v && collected.add(v));
    if (sd.api_config) {
      addResponse(sd.api_config.response_var, sd.api_config.result_mapping);
      addPersist(sd.api_config.persist);
    }
    if (sd.action && sd.action.type === 'api' && sd.action.config)
      addResponse(sd.action.config.response_var, sd.action.config.result_mapping);
    // Router node: embedded source outputs + persisted customer memory + route conditions.
    if (sd.router_config) {
      const rc = sd.router_config;
      if (rc.api_config) addResponse(rc.api_config.response_var, rc.api_config.result_mapping);
      Object.values((rc.sheet_config && rc.sheet_config.column_mappings) || {}).forEach(v => v && collected.add(v));
      Object.values((rc.table_config && rc.table_config.column_mappings) || {}).forEach(v => v && collected.add(v));
      addPersist(rc.persist);
      (rc.routes || []).forEach(r => scanCondition(r && r.when));
    }
    // Sequenced API node: each step's result_mapping + step_<n>_ok + persisted memory.
    if (sd.sequence_config) {
      (sd.sequence_config.steps || []).forEach((st, i) => {
        if (st && st.api) addResponse(st.api.response_var, st.api.result_mapping);
        collected.add(`step_${i + 1}_ok`);
      });
      addPersist(sd.sequence_config.persist);
    }
    // Sync Job API destination: single-call result_mapping, and the per-customer router's
    // per-route result_mapping + persisted customer memory + route conditions.
    const as = sd.job_config && sd.job_config.api_sync;
    if (as) {
      addResponse(as.response_var, as.result_mapping);
      if (as.router) {
        (as.router.routes || []).forEach(rt => {
          if (rt && rt.api) { addResponse(null, rt.api.result_mapping); addPersist(rt.api.persist); }
          scanCondition(rt && rt.when);
        });
        const d = as.router.default;
        if (d && d.api) { addResponse(null, d.api.result_mapping); addPersist(d.api.persist); }
      }
    }
    // conditions may live in several shapes across node types
    if (Array.isArray(sd.conditions)) sd.conditions.forEach(c => scanCondition(c && (c.condition || c.expr)));
    if (sd.condition) scanCondition(sd.condition);
    if (sd.branches && Array.isArray(sd.branches)) sd.branches.forEach(b => scanCondition(b && b.condition));
    if (sd.action && sd.action.type === 'logic' && sd.action.config)
      scanCondition(sd.action.config.condition);
    if (n.data && Array.isArray(n.data.conditions)) n.data.conditions.forEach(c => scanCondition(c && c.condition));
  });
  return {
    collected: [...collected].sort(),
    response: [...response].sort(),
    customerMemory: [...customerMemory].sort(),
    runtime: ['today_date', 'date_plus_2', 'date_plus_3', 'is_office_hours'],
  };
}

// A small "Insert variable" button that drops {{name}} into a target field at the
// cursor. Used across the API config areas so users don't have to remember (or go
// hunt for) variable names across a large workflow.
const VariableInserter = ({ groups, targetRef, value, onChange, note, wrap = true }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const fmt = (name) => (wrap ? `{{${name}}}` : name);

  const insert = (name) => {
    const token = fmt(name);
    const el = targetRef && targetRef.current;
    const v = value || '';
    if (el && typeof el.selectionStart === 'number') {
      const s = el.selectionStart, e = el.selectionEnd;
      const next = v.slice(0, s) + token + v.slice(e);
      onChange(next);
      requestAnimationFrame(() => {
        try { el.focus(); const p = s + token.length; el.setSelectionRange(p, p); } catch (_) {}
      });
    } else {
      onChange(v + token);
    }
    setOpen(false); setQ('');
  };

  const ql = q.trim().toLowerCase();
  const filt = (items) => ql ? items.filter(i => i.toLowerCase().includes(ql)) : items;
  const visible = (groups || []).map(g => ({ ...g, items: filt(g.items || []) })).filter(g => g.items.length);

  return (
    <div className="relative inline-block" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
        title={wrap ? 'Insert a {{variable}} at the cursor' : 'Insert a variable name at the cursor'}
      >
        <span className="font-mono">{wrap ? '{{ }}' : 'x'}</span> Insert variable
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2 right-0">
          <input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Filter variables..."
            className="w-full border border-gray-300 rounded px-2 py-1 text-xs mb-1"
          />
          {visible.length === 0 && (
            <p className="text-[11px] text-gray-400 px-1 py-2">No matching variables.</p>
          )}
          {visible.map(g => (
            <div key={g.label} className="mb-1">
              <div className="text-[10px] uppercase tracking-wide text-gray-400 px-1 mt-1">{g.label}</div>
              {g.items.map(name => (
                <button
                  key={name} type="button" onClick={() => insert(name)}
                  className="block w-full text-left px-2 py-1 text-xs font-mono text-gray-700 hover:bg-indigo-50 rounded"
                >
                  {fmt(name)}
                </button>
              ))}
            </div>
          ))}
          {note && <p className="text-[10px] text-gray-400 px-1 mt-1 border-t border-gray-100 pt-1">{note}</p>}
        </div>
      )}
    </div>
  );
};

// Inline documentation of the {{placeholders}} available in an API config area.
// mode='flow' → API Call node + API action (substitute from session variables).
// mode='sync' → EOD sync API destination (substitute from the conversation record).
const ApiVarsHelp = ({ mode = 'flow' }) => (
  <details className="bg-white border border-gray-200 rounded p-2 text-[11px] text-gray-600">
    <summary className="cursor-pointer font-semibold text-gray-700">
      Available variables — use as {'{{'}name{'}}'} in URL, headers, params, or body
    </summary>
    {mode === 'sync' ? (
      <div className="mt-2 space-y-1">
        <p>The sync builds one record per conversation. Reference its fields:</p>
        <p className="font-mono leading-5">
          {'{{record}}'} · {'{{records}}'} · {'{{phone}}'} · {'{{channel}}'} · {'{{session_id}}'} · {'{{summary}}'} · {'{{workflow_id}}'} · {'{{host_tenant_id}}'} · {'{{synced_at}}'} · {'{{count}}'}
        </p>
        <p><span className="font-mono">{'{{record}}'}</span> / <span className="font-mono">{'{{records}}'}</span> inject the whole object / array (per-record vs bulk);
          <span className="font-mono">{'{{count}}'}</span> is the record count (bulk). Others are per-record fields.</p>
        <p className="text-gray-500">Auth (token) is applied automatically from the selected connection — there is no {'{{token}}'} placeholder.</p>
        <p>Each record also carries the conversation's collected variables, so you can reference them directly (e.g. <span className="font-mono">{'{{order_id}}'}</span>) or the whole set via <span className="font-mono">{'{{variables}}'}</span>.</p>
      </div>
    ) : (
      <div className="mt-2 space-y-1">
        <p>Placeholders resolve from the conversation's collected variables:</p>
        <p><span className="font-medium">Anything this flow captured</span> — step Variable Mappings, and (for campaigns) the contact's sheet/table columns, e.g. <span className="font-mono">{'{{name}}'}</span>, <span className="font-mono">{'{{phone}}'}</span>, <span className="font-mono">{'{{order_id}}'}</span>.</p>
        <p><span className="font-medium">Always available:</span> <span className="font-mono">{'{{today_date}}'}</span> · <span className="font-mono">{'{{date_plus_2}}'}</span> · <span className="font-mono">{'{{date_plus_3}}'}</span> · <span className="font-mono">{'{{is_office_hours}}'}</span>.</p>
        <p><span className="font-medium">After this API Call:</span> <span className="font-mono">{'{{'}<i>prefix</i>_ok{'}}'}</span> plus any fields you map under Response Mapping (prefix = the Response Variable Prefix).</p>
        <p className="text-gray-500">Auth (token) is applied automatically from the selected connection — there is no {'{{token}}'} placeholder.</p>
      </div>
    )}
  </details>
);

// Inline documentation of the variables a Router node produces and routes on.
const RouterVarsHelp = () => (
  <details className="bg-white border border-gray-200 rounded p-2 text-[11px] text-gray-600">
    <summary className="cursor-pointer font-semibold text-gray-700">
      What this Router produces &amp; how routes are evaluated
    </summary>
    <div className="mt-2 space-y-1">
      <p><span className="font-medium">Extracted variables</span> — whatever you map from the
        source (API Response Mapping, or a Sheet/Table column → variable) becomes a
        <span className="font-mono"> {'{{'}name{'}}'}</span> you can use in routes and in every
        downstream node.</p>
      <p><span className="font-medium">Remembered on the customer</span> — each name under
        “Remember against customer” is saved workflow-scoped and loaded back on the customer's
        next contact as <span className="font-mono">customer_&lt;name&gt;</span> (e.g.
        <span className="font-mono"> customer_lead_id</span>). Write-once: an existing value is
        never overwritten.</p>
      <p><span className="font-medium">Source status</span> — with an API source,
        <span className="font-mono"> {'{{'}<i>prefix</i>_ok{'}}'}</span> reflects success; a failed
        API or a not-found Sheet/Table row takes the <span className="font-medium">On source
        error</span> route.</p>
      <p><span className="font-medium">Routes</span> are boolean expressions over these names
        plus the always-available ones (<span className="font-mono">today_date</span> …),
        evaluated top-to-bottom — first match wins; blank <span className="font-mono">when</span>
        is an unconditional catch-all.</p>
      <p className="text-gray-500">All of the above also appear in the “Insert variable” picker in
        other nodes (under <span className="italic">Remembered on the customer</span> and the
        collected/response groups).</p>
    </div>
  </details>
);

const StepProperties = ({ step, stepId, onUpdate, onDelete, nodes, selectedNode, workflowId }) => {
  const [stepData, setStepData] = useState(step);

  const [variableMappingsList, setVariableMappingsList] = useState([]);
  const initializedRef = useRef(false);

  // Available {{variables}} across the whole workflow, for the Insert-variable
  // pickers in the API config areas. Recomputed when the node set changes.
  const flowVars = useMemo(() => collectWorkflowVariables(nodes), [nodes]);
  const flowVarGroups = useMemo(() => ([
    { label: 'Collected in this flow', items: flowVars.collected },
    { label: 'Remembered on the customer', items: flowVars.customerMemory },
    { label: 'API response', items: flowVars.response },
    { label: 'Always available', items: flowVars.runtime },
  ]), [flowVars]);
  const syncVarGroups = useMemo(() => ([
    { label: 'Whole payload', items: ['record', 'records', 'count'] },
    { label: 'Record fields', items: ['phone', 'channel', 'session_id', 'summary', 'workflow_id', 'host_tenant_id', 'synced_at'] },
    // Conversation-collected variables are attached to each record at sync time,
    // so a detached sync job can reference them per record (e.g. {{order_id}}).
    { label: 'Conversation variables', items: flowVars.collected },
    { label: 'Remembered on the customer', items: flowVars.customerMemory },
    { label: 'API response', items: flowVars.response },
  ]), [flowVars]);
  // Refs to the body textareas so the picker can insert at the cursor.
  const apiNodeBodyRef = useRef(null);
  const actionBodyRef = useRef(null);
  const syncBodyRef = useRef(null);
  const msgBodyRef = useRef(null);
  const aiPromptRef = useRef(null);
  const handoffCtxRef = useRef(null);
  const logicCondRef = useRef(null);

  // Reusable authenticated API connections (for the API Call node's auth selector).
  const [apiConnections, setApiConnections] = useState([]);
  useEffect(() => {
    let cancelled = false;
    apiCallJson('/api/chatbot/api-connections')
      .then((data) => { if (!cancelled) setApiConnections(data.connections || []); })
      .catch(() => { if (!cancelled) setApiConnections([]); });
    return () => { cancelled = true; };
  }, []);

  // Workflow's data tables (for the Router "Table" source picker and the Table Read/Write
  // node pickers). Fetched lazily when a table-using node is open; manual entry / upload
  // still works if the fetch is unavailable.
  const [availableTables, setAvailableTables] = useState([]);
  const routerSource = stepData.router_config && stepData.router_config.source;
  const needsTables = (stepData.message_type === 'router' && routerSource === 'table')
    || stepData.message_type === 'table_read'
    || stepData.message_type === 'table_write';
  const refreshTables = useCallback(() => {
    if (!workflowId) return;
    apiCallJson(`/api/chatbot/workflows/${workflowId}/tables`)
      .then((data) => setAvailableTables(data.tables || []))
      .catch(() => { /* leave as-is; manual field / upload remains usable */ });
  }, [workflowId]);
  useEffect(() => {
    if (!workflowId || !needsTables) return;
    let cancelled = false;
    apiCallJson(`/api/chatbot/workflows/${workflowId}/tables`)
      .then((data) => { if (!cancelled) setAvailableTables(data.tables || []); })
      .catch(() => { /* leave empty; the manual field remains usable */ });
    return () => { cancelled = true; };
  }, [workflowId, needsTables]);

  // Update local state when step prop changes
  useEffect(() => {
    setStepData(step || {});
    initializedRef.current = false; // force re-init of list on stepId change
  }, [stepId]);

  useEffect(() => {
    // Only initialize once per stepId (avoid overwriting while editing)
    if (initializedRef.current) return;
    const vm = stepData.variable_mappings || {};
    const list = Object.entries(vm).map(([name, pattern]) => ({
      id: `vm_${Math.random().toString(36).slice(2, 9)}`, // stable-ish id
      name,
      pattern: pattern || ''
    }));
    setVariableMappingsList(list);
    initializedRef.current = true;
  }, [stepData]);

  const updateField = (field, value) => {
    const updated = { ...stepData, [field]: value };
    setStepData(updated);
  };

  const updateFieldUpdate = (field, value) => {
    const updated = { ...stepData, [field]: value };
    onUpdate(updated);
  };

  // Debounced propagate: call onUpdate after 400ms of inactivity
  // Used by sub-item fields (buttons, options, carousel, action config)
  const debounceRef = useRef(null);
  const debouncedPropagate = useCallback((updatedData) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onUpdate(updatedData);
    }, 400);
  }, [onUpdate]);

  // Flush on unmount
  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  // Immediate propagate (for selects / dropdowns that don't need debounce)
  const propagateNow = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onUpdate(stepData);
  }, [onUpdate, stepData]);

  const updateAction = (field, value) => {
    const updated = {
      ...stepData,
      action: {
        ...stepData.action,
        [field]: value
      }
    };
    setStepData(updated);
    // Action type changes (select) propagate immediately
    onUpdate(updated);
  };

  const updateActionConfig = (key, value) => {
    const updated = {
      ...stepData,
      action: {
        ...stepData.action,
        config: {
          ...stepData.action.config,
          [key]: value
        }
      }
    };
    setStepData(updated);
    debouncedPropagate(updated);
  };

  // Config writer for the dedicated API Call step node (stepData.api_config).
  const updateApiConfig = (key, value) => {
    const updated = {
      ...stepData,
      api_config: {
        ...(stepData.api_config || {}),
        [key]: value
      }
    };
    setStepData(updated);
    debouncedPropagate(updated);
  };

  // Config writers for the generic Router node (stepData.router_config).
  const updateRouterConfig = (key, value) => {
    const updated = {
      ...stepData,
      router_config: {
        ...(stepData.router_config || {}),
        [key]: value
      }
    };
    setStepData(updated);
    debouncedPropagate(updated);
  };
  const updateRouterApiConfig = (key, value) => {
    const rc = stepData.router_config || {};
    const updated = {
      ...stepData,
      router_config: {
        ...rc,
        api_config: { ...(rc.api_config || {}), [key]: value }
      }
    };
    setStepData(updated);
    debouncedPropagate(updated);
  };
  const updateRoute = (idx, key, value) => {
    const routes = [...((stepData.router_config || {}).routes || [])];
    routes[idx] = { ...(routes[idx] || {}), [key]: value };
    updateRouterConfig('routes', routes);
  };
  const addRoute = () => {
    const routes = [...((stepData.router_config || {}).routes || [])];
    routes.push({ when: '', to: '' });
    updateRouterConfig('routes', routes);
  };
  const removeRoute = (idx) => {
    const routes = ((stepData.router_config || {}).routes || []).filter((_, i) => i !== idx);
    updateRouterConfig('routes', routes);
  };
  const updateRouterSheetConfig = (key, value) => {
    const rc = stepData.router_config || {};
    const updated = { ...stepData, router_config: { ...rc, sheet_config: { ...(rc.sheet_config || {}), [key]: value } } };
    setStepData(updated); debouncedPropagate(updated);
  };
  const updateRouterTableConfig = (key, value) => {
    const rc = stepData.router_config || {};
    const updated = { ...stepData, router_config: { ...rc, table_config: { ...(rc.table_config || {}), [key]: value } } };
    setStepData(updated); debouncedPropagate(updated);
  };
  // Sync Job → API destination "router" (per-customer, memory-aware routing to inline APIs).
  const _apiSync = () => (stepData.job_config || {}).api_sync || {};
  const _syncRoutes = () => (_apiSync().router || {}).routes || [];
  const updateSyncRouter = (key, value) => {
    const jc = stepData.job_config || {};
    const as = jc.api_sync || {};
    const u = { ...stepData, job_config: { ...jc, api_sync: { ...as, router: { ...(as.router || {}), [key]: value } } } };
    setStepData(u); debouncedPropagate(u);
  };
  const toggleSyncRouter = (on) => {
    const jc = stepData.job_config || {};
    const as = jc.api_sync || {};
    let router;
    if (on) router = as.router || { routes: [{ when: '', api: { url: '', method: 'POST' } }] };
    else { const { router: _drop, ...rest } = as; const u = { ...stepData, job_config: { ...jc, api_sync: rest } }; setStepData(u); onUpdate(u); return; }
    const u = { ...stepData, job_config: { ...jc, api_sync: { ...as, router } } };
    setStepData(u); onUpdate(u);
  };
  const updateSyncRoute = (idx, patch) => {
    const routes = [..._syncRoutes()];
    routes[idx] = { ...(routes[idx] || {}), ...patch };
    updateSyncRouter('routes', routes);
  };
  const updateSyncRouteApi = (idx, key, value) => {
    const routes = [..._syncRoutes()];
    const r = routes[idx] || {};
    routes[idx] = { ...r, api: { ...(r.api || {}), [key]: value } };
    updateSyncRouter('routes', routes);
  };
  const addSyncRoute = () => {
    const routes = [..._syncRoutes(), { when: '', api: { url: '', method: 'POST' } }];
    updateSyncRouter('routes', routes);
  };
  const removeSyncRoute = (idx) => updateSyncRouter('routes', _syncRoutes().filter((_, i) => i !== idx));

  // Sequenced API node (stepData.sequence_config): call APIs in order, threading vars.
  const _seqSteps = () => (stepData.sequence_config || {}).steps || [];
  const updateSequenceConfig = (key, value) => {
    const u = { ...stepData, sequence_config: { ...(stepData.sequence_config || {}), [key]: value } };
    setStepData(u); debouncedPropagate(u);
  };
  const updateSeqStepApi = (idx, key, value) => {
    const steps = [..._seqSteps()];
    const s = steps[idx] || {};
    steps[idx] = { ...s, api: { ...(s.api || {}), [key]: value } };
    updateSequenceConfig('steps', steps);
  };
  const addSeqStep = () => updateSequenceConfig('steps', [..._seqSteps(), { api: { url: '', method: 'POST' } }]);
  const removeSeqStep = (idx) => updateSequenceConfig('steps', _seqSteps().filter((_, i) => i !== idx));

  // Column → variable mapping editor shared by sheet/table sources.
  const updateRouterColMap = (cfgKey, writer, colName, varName, oldCol) => {
    const rc = stepData.router_config || {};
    const cm = { ...((rc[cfgKey] || {}).column_mappings || {}) };
    if (oldCol !== undefined && oldCol !== colName) { const v = cm[oldCol]; delete cm[oldCol]; cm[colName] = v; }
    else { cm[colName] = varName; }
    writer('column_mappings', cm);
  };

  const addButton = () => {
    const newButton = {
      id: generateId('btn'),
      text: 'New Button',
      value: 'button_value',
      wa_text: '',
      next_step: ''
    };
    updateField('buttons', [...(stepData.buttons || []), newButton]);
  };

  const updateButton = (index, field, value) => {
    const buttons = [...stepData.buttons];
    buttons[index] = { ...buttons[index], [field]: value };
    updateField('buttons', buttons);
  };

  const removeButton = (index) => {
    const buttons = stepData.buttons.filter((_, i) => i !== index);
    updateField('buttons', buttons);
  };

  const addOption = () => {
    const newOption = {
      id: generateId('opt'),
      text: 'New Option',
      value: 'option_value'
    };
    updateField('options', [...(stepData.options || []), newOption]);
  };

  const updateOption = (index, field, value) => {
    const options = [...stepData.options];
    options[index] = { ...options[index], [field]: value };
    updateField('options', options);
  };

  const removeOption = (index) => {
    const options = stepData.options.filter((_, i) => i !== index);
    updateField('options', options);
  };

  const addCarouselItem = () => {
    const newItem = {
      id: generateId('carousel'),
      title: 'New Item',
      subtitle: '',
      image_url: '',
      buttons: []
    };
    updateField('carousel_items', [...(stepData.carousel_items || []), newItem]);
  };

  const updateCarouselItem = (index, field, value) => {
    const items = [...(stepData.carousel_items || [])];
    items[index] = { ...items[index], [field]: value };
    updateField('carousel_items', items);
  };

  const removeCarouselItem = (index) => {
    const items = (stepData.carousel_items || []).filter((_, i) => i !== index);
    updateField('carousel_items', items);
  };

  const addCarouselButton = (itemIndex) => {
    const items = [...(stepData.carousel_items || [])];
    const newButton = {
      id: generateId('cbtn'),
      text: 'Button',
      value: 'value',
      next_step: ''
    };
    items[itemIndex].buttons = [...(items[itemIndex].buttons || []), newButton];
    updateField('carousel_items', items);
  };

  const updateCarouselButton = (itemIndex, buttonIndex, field, value) => {
    const items = [...(stepData.carousel_items || [])];
    items[itemIndex].buttons[buttonIndex] = { 
      ...items[itemIndex].buttons[buttonIndex], 
      [field]: value 
    };
    updateField('carousel_items', items);
  };

  const removeCarouselButton = (itemIndex, buttonIndex) => {
    const items = [...(stepData.carousel_items || [])];
    items[itemIndex].buttons = items[itemIndex].buttons.filter((_, i) => i !== buttonIndex);
    updateField('carousel_items', items);
  };

  const addVariableMapping = () => {
    setVariableMappingsList(prev => [
      ...prev,
      { id: generateId('vm'), name: generateId('var'), pattern: '' }
    ]);
  };

  const updateVariableMappingName = (id, newName) => {
    setVariableMappingsList(prev => prev.map(it => it.id === id ? { ...it, name: newName } : it));
  };

  const updateVariableMappingPattern = (id, newPattern) => {
    setVariableMappingsList(prev => prev.map(it => it.id === id ? { ...it, pattern: newPattern } : it));
  };

  const removeVariableMapping = (id) => {
    setVariableMappingsList(prev => prev.filter(it => it.id !== id));
  };

  // Convert list -> object and persist to parent
  const persistVariableMappings = () => {
    const mappingsObj = variableMappingsList.reduce((acc, { name, pattern }) => {
      if (name && name.trim()) acc[name.trim()] = pattern;
      return acc;
    }, {});
    // Only update local stepData once
    const updated = { ...stepData, variable_mappings: mappingsObj };
    setStepData(updated);
    // call parent update (onBlur / Save)
    onUpdate && onUpdate(updated);
  };

  return (
    <div className="flex-1 overflow-y-auto overflow-x-hidden">
      <div className="p-4 space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <label className="block text-xs font-medium text-blue-800 mb-1">Step ID</label>
          <div className="flex items-center space-x-2">
            <code className="flex-1 bg-white px-2 py-1 rounded text-sm text-blue-600 border border-blue-300 truncate">
              {stepId || 'N/A'}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(stepId || '');
                
              }}
              className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
            >
              Copy
            </button>
          </div>
          <p className="text-xs text-blue-600 mt-1">Use this ID to connect steps</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Step Name</label>
          <input
            type="text"
            value={stepData.name}
            onChange={(e) => updateField('name', e.target.value)}
            onBlur={(e) => updateFieldUpdate('name', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Message Type</label>
          <select
            value={stepData.message_type}
            onChange={(e) => updateField('message_type', e.target.value)}
            onBlur={(e) => updateFieldUpdate('message_type', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="text">Text</option>
            <option value="buttons">Buttons</option>
            <option value="options">Options</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
            <option value="carousel">Carousel</option>
            <option value="ai_response">AI Response</option>
            <option value="handoff">Human Handoff</option>
            <option value="file_upload">File Upload</option>
            <option value="delay">Delay / Scheduled</option>
            <option value="ab_test">A/B Test Split</option>
            <option value="sheet_read">Sheet Read (Bulk Send)</option>
            <option value="sheet_write">Sheet Write</option>
            <option value="table_read">Table Read (Local DB)</option>
            <option value="table_write">Table Write (Local DB)</option>
            <option value="request_callback">Request Callback</option>
            <option value="router">Router (Branch / API Router)</option>
            <option value="api_sequence">Sequenced API (chain calls)</option>
            <option value="api_call">API Call (CRM / External)</option>
            <option value="eod_sync_job">Sync Job</option>
            <option value="reminder_job">Reminder Job</option>
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Message</label>
            <VariableInserter
              groups={flowVarGroups} targetRef={msgBodyRef}
              value={stepData.message || ''}
              onChange={(v) => updateFieldUpdate('message', v)}
              note="Campaign contact columns are also available at runtime."
            />
          </div>
          <textarea
            ref={msgBodyRef}
            value={stepData.message}
            onChange={(e) => updateField('message', e.target.value)}
            onBlur={(e) => updateFieldUpdate('message', e.target.value)}
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your message. Use {{variable_name}} for dynamic content."
          />
          <p className="text-xs text-gray-500 mt-1">Use variables like {'{{username}}'} to insert dynamic content</p>
        </div>

        {/* ==================== Send as Template Toggle ==================== */}
        {['text', 'buttons', 'options', 'image'].includes(stepData.message_type) && (
          <div className={`border rounded-lg p-3 space-y-3 transition-colors ${
            stepData.template_config?.enabled ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={stepData.template_config?.enabled || false}
                onChange={(e) => {
                  const updated = {
                    ...stepData,
                    template_config: { ...stepData.template_config, enabled: e.target.checked }
                  };
                  setStepData(updated); onUpdate(updated);
                }}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="text-sm font-medium text-gray-700">Send as WhatsApp Template</span>
            </label>

            {stepData.template_config?.enabled && (
              <div className="space-y-2 pl-6">
                <p className="text-[10px] text-amber-700">
                  Required for bot-initiated messages outside the 24h session window.
                  The template must be pre-approved in WhatsApp Business Manager.
                </p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Template Name</label>
                  <input
                    type="text"
                    value={stepData.template_config?.template_name || ''}
                    onChange={(e) => {
                      const updated = { ...stepData, template_config: { ...stepData.template_config, template_name: e.target.value } };
                      setStepData(updated);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder="welcome_message_v1"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Language</label>
                  <select
                    value={stepData.template_config?.language || 'en'}
                    onChange={(e) => {
                      const updated = { ...stepData, template_config: { ...stepData.template_config, language: e.target.value } };
                      setStepData(updated); onUpdate(updated);
                    }}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="en">English</option>
                    <option value="en_US">English (US)</option>
                    <option value="hi">Hindi</option>
                    <option value="mr">Marathi</option>
                    <option value="ta">Tamil</option>
                    <option value="te">Telugu</option>
                    <option value="bn">Bengali</option>
                    <option value="gu">Gujarati</option>
                    <option value="kn">Kannada</option>
                    <option value="ml">Malayalam</option>
                    <option value="pa">Punjabi</option>
                    <option value="de">German</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                    <option value="ar">Arabic</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Template Parameters (variable names, in order)
                  </label>
                  <input
                    type="text"
                    value={(stepData.template_config?.params || []).join(', ')}
                    onChange={(e) => {
                      const params = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                      const updated = { ...stepData, template_config: { ...stepData.template_config, params: params } };
                      setStepData(updated);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder="user_name, user_city, selected_plan"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Workflow variable names that map to {'{{1}}'}, {'{{2}}'}, {'{{3}}'} etc. in your template.
                    The Message field above is used as fallback when the template fails.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {(stepData.message_type === 'image' || stepData.message_type === 'video' || stepData.message_type === 'audio') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Media URL</label>
            <input
              type="url"
              value={stepData.media_url || ''}
              onChange={(e) => updateField('media_url', e.target.value)}
              onBlur={(e) => updateFieldUpdate('media_url', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={`https://example.com/${stepData.message_type}.${stepData.message_type === 'image' ? 'jpg' : stepData.message_type === 'video' ? 'mp4' : 'mp3'}`}
            />
          </div>
        )}

        {stepData.message_type === 'buttons' && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Buttons</label>
              <button
                onClick={addButton}
                className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
              >
                + Add Button
              </button>
            </div>
            {(stepData.buttons || []).length > 3 && (
              <div className="text-xs bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1.5 rounded-md mb-2 flex items-center gap-1.5">
                <Globe className="h-3 w-3 flex-shrink-0" />
                <span>
                  <strong>{(stepData.buttons || []).length} buttons</strong> — WhatsApp will render this as a
                  List Message (max 10). Web &amp; Telegram show normal buttons.
                </span>
              </div>
            )}
            <div className="space-y-3">
              {(stepData.buttons || []).map((button, index) => (
                <div key={button.id} className="border rounded-lg p-3 space-y-2 bg-gray-50">
                  <input
                    type="text"
                    value={button.text}
                    onChange={(e) => updateButton(index, 'text', e.target.value)}
                  onBlur={propagateNow}
                    placeholder="Button text"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <div className="relative">
                    <input
                      type="text"
                      value={button.wa_text || ''}
                      onChange={(e) => updateButton(index, 'wa_text', e.target.value)}
                      onBlur={propagateNow}
                      placeholder="WhatsApp short label (optional, max 20 chars)"
                      maxLength={20}
                      className={`w-full border rounded px-2 py-1 text-sm pr-12 ${
                        (button.wa_text || '').length > 20
                          ? 'border-red-400 bg-red-50'
                          : button.wa_text
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-300'
                      }`}
                    />
                    <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${
                      (button.wa_text || '').length > 20 ? 'text-red-500 font-medium' : 'text-gray-400'
                    }`}>
                      {(button.wa_text || '').length}/20
                    </span>
                  </div>
                  {button.text && button.text.length > 20 && !button.wa_text && (
                    <div className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Button text exceeds 20 chars — WhatsApp will auto-truncate. Add a short label above.
                    </div>
                  )}
                  <input
                    type="text"
                    value={button.value}
                    onChange={(e) => updateButton(index, 'value', e.target.value)}
                  onBlur={propagateNow}
                    placeholder="Button value"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <select
                    value={button.next_step || ''}
                    onChange={(e) => {
                      const buttons = [...stepData.buttons];
                      buttons[index] = { ...buttons[index], next_step: e.target.value };
                      const updated = { ...stepData, buttons };
                      setStepData(updated);
                      onUpdate(updated);
                    }}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="">-- No next step --</option>
                    {nodes
                      .filter(n => n.id !== selectedNode?.id)
                      .map(node => (
                        <option key={node.id} value={node.id}>
                          {node.data.label}
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={() => removeButton(index)}
                    className="text-xs bg-red-600 text-white px-2 py-1 rounded w-full hover:bg-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {stepData.message_type === 'options' && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Options</label>
              <button
                onClick={addOption}
                className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
              >
                + Add Option
              </button>
            </div>
            <div className="space-y-3">
              {(stepData.options || []).map((option, index) => (
                <div key={option.id} className="border rounded-lg p-3 space-y-2 bg-gray-50">
                  <input
                    type="text"
                    value={option.text}
                    onChange={(e) => updateOption(index, 'text', e.target.value)}
                  onBlur={propagateNow}
                    placeholder="Option text"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <input
                    type="text"
                    value={option.value}
                    onChange={(e) => updateOption(index, 'value', e.target.value)}
                  onBlur={propagateNow}
                    placeholder="Option value"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <select
                    value={option.next_step || ''}
                    onChange={(e) => {
                      const options = [...stepData.options];
                      options[index] = { ...options[index], next_step: e.target.value };
                      const updated = { ...stepData, options };
                      setStepData(updated);
                      onUpdate(updated);
                    }}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="">-- No next step --</option>
                    {nodes
                      .filter(n => n.id !== selectedNode?.id)
                      .map(node => (
                        <option key={node.id} value={node.id}>
                          {node.data.label}
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={() => removeOption(index)}
                    className="text-xs bg-red-600 text-white px-2 py-1 rounded w-full hover:bg-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {stepData.message_type === 'carousel' && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Carousel Items</label>
              <button
                onClick={addCarouselItem}
                className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
              >
                + Add Item
              </button>
            </div>
            <div className="space-y-4">
              {(stepData.carousel_items || []).map((item, itemIndex) => (
                <div key={item.id} className="border-2 border-pink-200 rounded-lg p-3 space-y-2 bg-pink-50">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-pink-800">Item {itemIndex + 1}</span>
                    <button
                      onClick={() => removeCarouselItem(itemIndex)}
                      className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                    >
                      Remove Item
                    </button>
                  </div>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => updateCarouselItem(itemIndex, 'title', e.target.value)}
                  onBlur={propagateNow}
                    placeholder="Title"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-medium"
                  />
                  <input
                    type="text"
                    value={item.subtitle || ''}
                    onChange={(e) => updateCarouselItem(itemIndex, 'subtitle', e.target.value)}
                  onBlur={propagateNow}
                    placeholder="Subtitle (optional)"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <input
                    type="url"
                    value={item.image_url || ''}
                    onChange={(e) => updateCarouselItem(itemIndex, 'image_url', e.target.value)}
                  onBlur={propagateNow}
                    placeholder="Image URL"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  
                  <div className="mt-2 pt-2 border-t border-pink-300">
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs font-medium text-pink-800">Buttons</label>
                      <button
                        onClick={() => addCarouselButton(itemIndex)}
                        className="text-xs bg-pink-600 text-white px-2 py-1 rounded hover:bg-pink-700"
                      >
                        + Button
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(item.buttons || []).map((button, btnIndex) => (
                        <div key={button.id} className="bg-white border rounded p-2 space-y-1">
                          <input
                            type="text"
                            value={button.text}
                            onChange={(e) => updateCarouselButton(itemIndex, btnIndex, 'text', e.target.value)}
                          onBlur={propagateNow}
                            placeholder="Button text"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                          <input
                            type="text"
                            value={button.value}
                            onChange={(e) => updateCarouselButton(itemIndex, btnIndex, 'value', e.target.value)}
                          onBlur={propagateNow}
                            placeholder="Button value"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                          <select
                            value={button.next_step || ''}
                            onChange={(e) => {
                              const items = [...(stepData.carousel_items || [])];
                              items[itemIndex].buttons[btnIndex] = { 
                                ...items[itemIndex].buttons[btnIndex], 
                                next_step: e.target.value 
                              };
                              const updated = { ...stepData, carousel_items: items };
                              setStepData(updated);
                              onUpdate(updated);
                            }}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          >
                            <option value="">-- No next step --</option>
                            {nodes
                              .filter(n => n.id !== selectedNode?.id)
                              .map(node => (
                                <option key={node.id} value={node.id}>
                                  {node.data.label}
                                </option>
                              ))}
                          </select>
                          <button
                            onClick={() => removeCarouselButton(itemIndex, btnIndex)}
                            className="text-xs bg-red-500 text-white px-2 py-1 rounded w-full hover:bg-red-600"
                          >
                            Remove Button
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* WhatsApp Flow Configuration */}
        {stepData.message_type === 'whatsapp_flow' && (
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
              <ClipboardList className="h-4 w-4 mr-2 text-teal-600" />
              WhatsApp Flow Configuration
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Flow ID (from Meta Business Manager)</label>
                <input
                  type="text"
                  value={stepData.flow_id || ''}
                  onChange={(e) => updateField('flow_id', e.target.value)}
                  onBlur={(e) => updateFieldUpdate('flow_id', e.target.value)}
                  placeholder="e.g. 1234567890"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">CTA Button Text</label>
                <input
                  type="text"
                  value={stepData.flow_cta || 'Open Form'}
                  onChange={(e) => updateField('flow_cta', e.target.value)}
                  onBlur={(e) => updateFieldUpdate('flow_cta', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Initial Screen ID</label>
                <input
                  type="text"
                  value={stepData.screen_id || 'SCREEN_1'}
                  onChange={(e) => updateField('screen_id', e.target.value)}
                  onBlur={(e) => updateFieldUpdate('screen_id', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Flow Action</label>
                <select
                  value={stepData.flow_action || 'navigate'}
                  onChange={(e) => { updateField('flow_action', e.target.value); updateFieldUpdate('flow_action', e.target.value); }}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="navigate">Navigate (start at screen)</option>
                  <option value="data_exchange">Data Exchange (dynamic)</option>
                </select>
              </div>
              <div className="text-xs text-gray-500 bg-teal-50 p-2 rounded">
                Flow responses will be mapped to workflow variables via the Variable Mappings section below.
              </div>
            </div>
          </div>
        )}

        {/* ==================== AI Response Config ==================== */}
        {stepData.message_type === 'ai_response' && (
          <div className="border rounded-lg p-4 bg-violet-50 space-y-3">
            <h4 className="text-sm font-semibold text-violet-800 flex items-center">
              <Bot className="h-4 w-4 mr-2" />
              AI Response Configuration
            </h4>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">LLM Provider</label>
              <select
                value={stepData.ai_config?.provider || 'anthropic'}
                onChange={(e) => {
                  const updated = { ...stepData, ai_config: { ...stepData.ai_config, provider: e.target.value } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="anthropic">Anthropic (Claude)</option>
                <option value="openai">OpenAI (GPT)</option>
                <option value="gemini">Google Gemini</option>
                <option value="bedrock">AWS Bedrock</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
              <input
                type="text"
                value={stepData.ai_config?.model || ''}
                onChange={(e) => {
                  const updated = { ...stepData, ai_config: { ...stepData.ai_config, model: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={(e) => {
                  const updated = { ...stepData, ai_config: { ...stepData.ai_config, model: e.target.value } };
                  onUpdate(updated);
                }}
                placeholder="claude-sonnet-4-20250514"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-600">System Prompt</label>
                <VariableInserter
                  groups={flowVarGroups} targetRef={aiPromptRef}
                  value={stepData.ai_config?.system_prompt || ''}
                  onChange={(v) => {
                    const updated = { ...stepData, ai_config: { ...stepData.ai_config, system_prompt: v } };
                    setStepData(updated); onUpdate(updated);
                  }}
                />
              </div>
              <textarea
                ref={aiPromptRef}
                value={stepData.ai_config?.system_prompt || ''}
                onChange={(e) => {
                  const updated = { ...stepData, ai_config: { ...stepData.ai_config, system_prompt: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={(e) => {
                  const updated = { ...stepData, ai_config: { ...stepData.ai_config, system_prompt: e.target.value } };
                  onUpdate(updated);
                }}
                rows={4}
                placeholder="You are a helpful customer service assistant. Use {{variable}} for context."
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Temperature</label>
                <input
                  type="number"
                  step="0.1" min="0" max="2"
                  value={stepData.ai_config?.temperature ?? 0.7}
                  onChange={(e) => {
                    const updated = { ...stepData, ai_config: { ...stepData.ai_config, temperature: parseFloat(e.target.value) || 0.7 } };
                    setStepData(updated); onUpdate(updated);
                  }}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Tokens</label>
                <input
                  type="number"
                  min="50" max="4096"
                  value={stepData.ai_config?.max_tokens ?? 1024}
                  onChange={(e) => {
                    const updated = { ...stepData, ai_config: { ...stepData.ai_config, max_tokens: parseInt(e.target.value) || 1024 } };
                    setStepData(updated); onUpdate(updated);
                  }}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fallback Message (if LLM fails)</label>
              <input
                type="text"
                value={stepData.ai_config?.fallback_message || ''}
                onChange={(e) => {
                  const updated = { ...stepData, ai_config: { ...stepData.ai_config, fallback_message: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={(e) => {
                  const updated = { ...stepData, ai_config: { ...stepData.ai_config, fallback_message: e.target.value } };
                  onUpdate(updated);
                }}
                placeholder="I'm sorry, I couldn't process that right now."
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="text-xs text-gray-500 bg-violet-100 p-2 rounded">
              The AI response is stored as <code className="bg-violet-200 px-1 rounded">{'{{ai_response}}'}</code>. Use it in the Message field above or leave it as the default.
            </div>
          </div>
        )}

        {/* ==================== Human Handoff Config ==================== */}
        {stepData.message_type === 'handoff' && (
          <div className="border rounded-lg p-4 bg-amber-50 space-y-3">
            <h4 className="text-sm font-semibold text-amber-800 flex items-center">
              <UserCheck className="h-4 w-4 mr-2" />
              Human Handoff Configuration
            </h4>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
              <input
                type="text"
                value={stepData.handoff_config?.department || ''}
                onChange={(e) => {
                  const updated = { ...stepData, handoff_config: { ...stepData.handoff_config, department: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={(e) => {
                  const updated = { ...stepData, handoff_config: { ...stepData.handoff_config, department: e.target.value } };
                  onUpdate(updated);
                }}
                placeholder="e.g. sales, support, billing"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
              <select
                value={stepData.handoff_config?.priority || 'normal'}
                onChange={(e) => {
                  const updated = { ...stepData, handoff_config: { ...stepData.handoff_config, priority: e.target.value } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-600">Reason / Context (optional)</label>
                <VariableInserter
                  groups={flowVarGroups} targetRef={handoffCtxRef}
                  value={stepData.handoff_config?.reason || ''}
                  onChange={(v) => {
                    const updated = { ...stepData, handoff_config: { ...stepData.handoff_config, reason: v } };
                    setStepData(updated); onUpdate(updated);
                  }}
                />
              </div>
              <textarea
                ref={handoffCtxRef}
                value={stepData.handoff_config?.reason || ''}
                onChange={(e) => {
                  const updated = { ...stepData, handoff_config: { ...stepData.handoff_config, reason: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={(e) => {
                  const updated = { ...stepData, handoff_config: { ...stepData.handoff_config, reason: e.target.value } };
                  onUpdate(updated);
                }}
                rows={2}
                placeholder="User needs help with {{topic}}. Use variables for context."
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="text-xs text-gray-500 bg-amber-100 p-2 rounded">
              When this step is reached, the user will be transferred to a live agent. Messages will be routed to agents via the Handoff Dashboard until the agent resolves the session. The workflow continues from the next connected node after resolution.
            </div>
          </div>
        )}

        {/* ==================== File Upload Config ==================== */}
        {stepData.message_type === 'file_upload' && (
          <div className="border rounded-lg p-4 bg-cyan-50 space-y-3">
            <h4 className="text-sm font-semibold text-cyan-800 flex items-center">
              <Paperclip className="h-4 w-4 mr-2" />
              File Upload Configuration
            </h4>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Variable Name</label>
              <input
                type="text"
                value={stepData.file_upload_config?.variable_name || ''}
                onChange={(e) => {
                  const updated = { ...stepData, file_upload_config: { ...stepData.file_upload_config, variable_name: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={(e) => {
                  const updated = { ...stepData, file_upload_config: { ...stepData.file_upload_config, variable_name: e.target.value } };
                  onUpdate(updated);
                }}
                placeholder="uploaded_file"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Allowed File Types (comma-separated MIME types)</label>
              <input
                type="text"
                value={(stepData.file_upload_config?.allowed_types || []).join(', ')}
                onChange={(e) => {
                  const types = e.target.value.split(',').map(t => t.trim()).filter(Boolean);
                  const updated = { ...stepData, file_upload_config: { ...stepData.file_upload_config, allowed_types: types } };
                  setStepData(updated);
                }}
                onBlur={(e) => {
                  const types = e.target.value.split(',').map(t => t.trim()).filter(Boolean);
                  const updated = { ...stepData, file_upload_config: { ...stepData.file_upload_config, allowed_types: types } };
                  onUpdate(updated);
                }}
                placeholder="image/jpeg, image/png, application/pdf"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max File Size (MB)</label>
              <input
                type="number"
                min="1" max="50"
                value={stepData.file_upload_config?.max_size_mb ?? 10}
                onChange={(e) => {
                  const updated = { ...stepData, file_upload_config: { ...stepData.file_upload_config, max_size_mb: parseInt(e.target.value) || 10 } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="text-xs text-gray-500 bg-cyan-100 p-2 rounded">
              The uploaded file's metadata (filename, type, URL) is stored in <code className="bg-cyan-200 px-1 rounded">{'{{uploaded_file}}'}</code> (or your custom variable name).
            </div>
          </div>
        )}

        {/* ==================== Sheet Read Config ==================== */}
        {stepData.message_type === 'sheet_read' && (
          <div className="border rounded-lg p-4 bg-lime-50 space-y-3">
            <h4 className="text-sm font-semibold text-lime-800 flex items-center">
              <Download className="h-4 w-4 mr-2" />
              Sheet Read — Bulk Welcome Messages
            </h4>
            <p className="text-xs text-lime-700">
              Reads phone numbers from a spreadsheet and sends a template message to each.
              Customers who reply enter the chatbot flow.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Source Type</label>
              <select
                value={stepData.sheet_config?.source_type || 'google_sheets'}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, source_type: e.target.value } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="google_sheets">Google Sheets</option>
                <option value="csv_url">CSV URL</option>
                <option value="excel_url">Excel URL</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sheet / File URL</label>
              <input
                type="text"
                value={stepData.sheet_config?.source_url || ''}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, source_url: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
              />
              {/* Upload file option — always visible */}
              <div className="mt-2">
                  <label className="flex items-center gap-2 px-3 py-1.5 bg-white border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-lime-400 hover:bg-lime-50 transition-colors">
                    <Upload className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-xs text-gray-600">Upload CSV / Excel file</span>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.tsv"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const resp = await apiCall(`/api/chatbot/workflows/${workflowId}/sheet/upload`, {
                            method: 'POST',
                            body: formData,
                            headers: { 'Content-Type': undefined },
                          });
                          if (!resp.ok) {
                            const err = await resp.json().catch(() => ({}));
                            alert(`Upload failed: ${err.detail || resp.status}`);
                            return;
                          }
                          const result = await resp.json();
                          // Auto-fill the source_url and source_type
                          const sourceType = file.name.endsWith('.csv') || file.name.endsWith('.tsv') ? 'csv_url' : 'excel_url';
                          const updated = {
                            ...stepData,
                            sheet_config: {
                              ...stepData.sheet_config,
                              source_url: result.url,
                              source_type: sourceType,
                              _upload_preview: result.preview,
                              _upload_filename: result.filename,
                            }
                          };
                          setStepData(updated);
                          onUpdate(updated);
                        } catch (err) {
                          alert(`Upload error: ${err.message}`);
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {stepData.sheet_config?._upload_filename && (
                    <p className="text-[10px] text-lime-700 mt-1">
                      Uploaded: {stepData.sheet_config._upload_filename}
                    </p>
                  )}
                  {stepData.sheet_config?._upload_preview?.headers?.length > 0 && (
                    <div className="mt-2 p-2 bg-white border border-gray-200 rounded text-[10px]">
                      <span className="font-medium text-gray-600">Columns detected: </span>
                      <span className="text-gray-500">
                        {stepData.sheet_config._upload_preview.headers.join(', ')}
                      </span>
                      <span className="text-gray-400 ml-1">
                        ({stepData.sheet_config._upload_preview.total_rows} rows)
                      </span>
                    </div>
                  )}
                </div>
            </div>
            {stepData.sheet_config?.source_type === 'google_sheets' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sheet/Tab Name (optional)</label>
                  <input
                    type="text"
                    value={stepData.sheet_config?.sheet_name || ''}
                    onChange={(e) => {
                      const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, sheet_name: e.target.value } };
                      setStepData(updated);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder="Sheet1"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Service Account JSON (for private sheets)</label>
                  <textarea
                    value={stepData.sheet_config?.google_credentials_json || ''}
                    onChange={(e) => {
                      const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, google_credentials_json: e.target.value } };
                      setStepData(updated);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    rows={3}
                    placeholder='{"type": "service_account", ...}'
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono resize-none"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Leave blank for published/public sheets</p>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-lime-50 px-2 text-[10px] text-gray-500">OR</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">OAuth Credentials (for sheets shared with your Google account)</label>
                  <textarea
                    value={stepData.sheet_config?.oauth_credentials || ''}
                    onChange={(e) => {
                      const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, oauth_credentials: e.target.value } };
                      setStepData(updated);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    rows={3}
                    placeholder='{"client_id": "...", "client_secret": "...", "refresh_token": "..."}'
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono resize-none"
                  />
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <button
                      type="button"
                      onClick={async () => {
                        // Set up callback — auto-fills ALL sheet nodes in the workflow
                        window.__googleOAuthCallback = (credsJson) => {
                          try {
                            // Update current node
                            const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, oauth_credentials: credsJson } };
                            setStepData(updated);
                            onUpdate(updated);
                            
                            // Propagate to all other sheet_read/sheet_write nodes
                            if (nodes) {
                              nodes.forEach(n => {
                                const sd = n.data?.stepData;
                                if (sd && (sd.message_type === 'sheet_read' || sd.message_type === 'sheet_write') && n.id !== stepId) {
                                  const nodeUpdated = {
                                    ...sd,
                                    sheet_config: { ...sd.sheet_config, oauth_credentials: credsJson }
                                  };
                                  // Use onUpdate with the node's data to propagate
                                  // This works because onUpdate calls setNodes internally
                                  n.data = { ...n.data, stepData: nodeUpdated };
                                }
                              });
                            }
                          } catch(e) { console.error('OAuth callback error:', e); }
                        };

                        try {
                          const resp = await apiCallJson('/api/chatbot/google/auth');
                          if (resp.auth_url) {
                            window.open(resp.auth_url, 'google_oauth', 'width=500,height=600');
                          }
                        } catch (err) {
                          alert(`Google Sheets integration not available: ${err.message || err}`);
                        }
                      }}
                      className="text-[11px] text-blue-600 hover:text-blue-800 underline"
                    >
                      Connect Google Account →
                    </button>
                    <span className="text-[10px] text-gray-400">
                      Log in with Google to grant Sheets access. Auto-fills on completion.
                    </span>
                  </div>
                </div>
              </>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone Column Name</label>
              <input
                type="text"
                value={stepData.sheet_config?.phone_column || ''}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, phone_column: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="Auto-detect (Mobile, Phone, Contact No, etc.)"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Template Name (for bot-initiated message)</label>
              <input
                type="text"
                value={stepData.sheet_config?.template_name || ''}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, template_name: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="welcome_message_v1"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Template Language Code</label>
              <div className="flex items-center gap-2">
                <select
                  value={stepData.sheet_config?.template_language || 'en_US'}
                  onChange={(e) => {
                    const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, template_language: e.target.value } };
                    setStepData(updated); onUpdate(updated);
                  }}
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="en">en</option>
                  <option value="en_US">en_US</option>
                  <option value="en_GB">en_GB</option>
                  <option value="hi">hi</option>
                  <option value="mr">mr</option>
                  <option value="ta">ta</option>
                  <option value="te">te</option>
                  <option value="bn">bn</option>
                  <option value="gu">gu</option>
                  <option value="kn">kn</option>
                  <option value="ml">ml</option>
                  <option value="pa">pa</option>
                  <option value="ur">ur</option>
                  <option value="de">de</option>
                  <option value="fr">fr</option>
                  <option value="es">es</option>
                  <option value="es_AR">es_AR</option>
                  <option value="es_MX">es_MX</option>
                  <option value="pt_BR">pt_BR</option>
                  <option value="pt_PT">pt_PT</option>
                  <option value="ar">ar</option>
                  <option value="id">id</option>
                  <option value="ms">ms</option>
                  <option value="zh_CN">zh_CN</option>
                  <option value="zh_TW">zh_TW</option>
                  <option value="ja">ja</option>
                  <option value="ko">ko</option>
                  <option value="ru">ru</option>
                  <option value="tr">tr</option>
                  <option value="it">it</option>
                  <option value="nl">nl</option>
                  <option value="pl">pl</option>
                  <option value="th">th</option>
                  <option value="vi">vi</option>
                </select>
                <input
                  type="text"
                  value={stepData.sheet_config?.template_language || ''}
                  onChange={(e) => {
                    const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, template_language: e.target.value } };
                    setStepData(updated);
                  }}
                  onBlur={() => onUpdate(stepData)}
                  placeholder="or type: en_US"
                  className="w-24 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Must exactly match the language code used when creating the template in Meta Business Manager</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Template Params (column names, comma-separated)</label>
              <input
                type="text"
                value={(stepData.sheet_config?.template_params || []).join(', ')}
                onChange={(e) => {
                  const params = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, template_params: params } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="Name, City"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <p className="text-[10px] text-gray-400 mt-1">Column values used as {'{{1}}'}, {'{{2}}'}, etc. in the template</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Batch Size</label>
                <input
                  type="number"
                  min="1" max="500"
                  value={stepData.sheet_config?.batch_size ?? 50}
                  onChange={(e) => {
                    const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, batch_size: parseInt(e.target.value) || 50 } };
                    setStepData(updated); onUpdate(updated);
                  }}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Filter Column (optional)</label>
                <input
                  type="text"
                  value={stepData.sheet_config?.filter_column || ''}
                  onChange={(e) => {
                    const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, filter_column: e.target.value } };
                    setStepData(updated);
                  }}
                  onBlur={() => onUpdate(stepData)}
                  placeholder="Status"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
            </div>
            <div className="text-xs text-lime-700 bg-lime-100 p-2 rounded space-y-1">
              <p><strong>How it works:</strong></p>
              <p>1. Reads phone numbers from the sheet</p>
              <p>2. Sends a WhatsApp template message to each number (opens 24h window)</p>
              <p>3. When a customer replies, they enter the chatbot flow at the next step</p>
              <p>4. Row data from the sheet is available as variables: {'{{col_Name}}'}, {'{{col_City}}'}, etc.</p>
            </div>

            {/* Template Quick Reply Button Mapping */}
            {stepData.sheet_config?.template_name && (
              <div className="border-t border-lime-200 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-lime-800">Template Quick Reply Buttons → Workflow Nodes</label>
                  <button
                    onClick={() => {
                      const btns = [...(stepData.sheet_config?.template_buttons || []), { payload: '', next_step: '' }];
                      const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, template_buttons: btns } };
                      setStepData(updated); onUpdate(updated);
                    }}
                    className="text-xs px-2 py-1 bg-lime-600 text-white rounded hover:bg-lime-700"
                  >+ Add Button</button>
                </div>
                <p className="text-[10px] text-lime-700 mb-2">
                  Map each Quick Reply button payload (from Meta template) to a workflow node.
                  When the customer taps the button, they enter the flow at that node.
                </p>
                {(stepData.sheet_config?.template_buttons || []).map((tb, idx) => (
                  <div key={idx} className="flex flex-col gap-1 mb-2 p-2 bg-white rounded border border-lime-200">
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={tb.payload || ''}
                        onChange={(e) => {
                          const btns = [...(stepData.sheet_config?.template_buttons || [])];
                          btns[idx] = { ...btns[idx], payload: e.target.value };
                          const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, template_buttons: btns } };
                          setStepData(updated);
                        }}
                        onBlur={() => onUpdate(stepData)}
                        placeholder="Button payload"
                        className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-xs font-mono"
                      />
                      <button
                        onClick={() => {
                          const btns = [...(stepData.sheet_config?.template_buttons || [])];
                          btns.splice(idx, 1);
                          const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, template_buttons: btns } };
                          setStepData(updated); onUpdate(updated);
                        }}
                        className="text-red-400 hover:text-red-600 flex-shrink-0"
                      ><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400 text-[10px] flex-shrink-0">→ routes to</span>
                      <select
                        value={tb.next_step || ''}
                        onChange={(e) => {
                          const btns = [...(stepData.sheet_config?.template_buttons || [])];
                          btns[idx] = { ...btns[idx], next_step: e.target.value };
                          const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, template_buttons: btns } };
                          setStepData(updated); onUpdate(updated);
                        }}
                        className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-xs"
                      >
                        <option value="">— Target node —</option>
                        {nodes.filter(n => n.id !== selectedNode?.id).map(n => (
                          <option key={n.id} value={n.id}>
                            {n.data?.label || n.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Auto-Schedule Campaign */}
            <div className="border-t border-lime-200 pt-3 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-lime-800">Auto-Schedule Campaign</label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={stepData.sheet_config?.auto_schedule?.enabled || false}
                    onChange={(e) => {
                      const u = { ...stepData, sheet_config: { ...stepData.sheet_config,
                        auto_schedule: { ...(stepData.sheet_config?.auto_schedule || {}), enabled: e.target.checked }
                      }};
                      setStepData(u); onUpdate(u);
                    }}
                    className="rounded border-gray-300 text-lime-600 focus:ring-lime-500"
                  />
                  <span className="text-xs text-gray-600">Enable</span>
                </label>
              </div>
              {stepData.sheet_config?.auto_schedule?.enabled && (
                <div className="space-y-2 p-2 bg-lime-100 rounded">
                  <div>
                    <label className="block text-[10px] font-medium text-gray-600 mb-1">Run every</label>
                    <div className="flex items-center gap-2">
                      <select
                        value={stepData.sheet_config?.auto_schedule?.interval_minutes || 1440}
                        onChange={(e) => {
                          const u = { ...stepData, sheet_config: { ...stepData.sheet_config,
                            auto_schedule: { ...(stepData.sheet_config?.auto_schedule || {}), interval_minutes: parseInt(e.target.value) }
                          }};
                          setStepData(u); onUpdate(u);
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-xs"
                      >
                        <option value={5}>5 minutes</option>
                        <option value={10}>10 minutes</option>
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={120}>2 hours</option>
                        <option value={240}>4 hours</option>
                        <option value={360}>6 hours</option>
                        <option value={480}>8 hours</option>
                        <option value={720}>12 hours</option>
                        <option value={1440}>24 hours (daily)</option>
                        <option value={2880}>48 hours</option>
                        <option value={10080}>Weekly</option>
                      </select>
                      <span className="text-xs text-gray-500">
                        {(() => { const m = stepData.sheet_config?.auto_schedule?.interval_minutes || 1440; return m < 60 ? `${m}m` : m < 1440 ? `${m/60}h` : `${m/1440}d`; })()}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-600 mb-1">Start time (24h)</label>
                    <input
                      type="text"
                      value={stepData.sheet_config?.auto_schedule?.start_time || '09:00'}
                      onChange={(e) => {
                        const u = { ...stepData, sheet_config: { ...stepData.sheet_config,
                          auto_schedule: { ...(stepData.sheet_config?.auto_schedule || {}), start_time: e.target.value }
                        }};
                        setStepData(u);
                      }}
                      onBlur={() => onUpdate(stepData)}
                      placeholder="09:00"
                      className="w-24 border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </div>
                  <p className="text-[10px] text-lime-700">
                    Re-reads the sheet every {(() => { const m = stepData.sheet_config?.auto_schedule?.interval_minutes || 1440; return m < 60 ? `${m} minutes` : m < 1440 ? `${m/60} hours` : `${m/1440} days`; })()}
                    starting at {stepData.sheet_config?.auto_schedule?.start_time || '09:00'}.
                    New numbers are sent templates. Numbers already contacted (24h window) are skipped.
                  </p>
                </div>
              )}
            </div>

            {/* Run Campaign Button */}
            {workflowId && (
              <div className="border-t border-lime-200 pt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <select
                    value={stepData.sheet_config?.campaign_channel || 'whatsapp_interakt'}
                    onChange={(e) => {
                      const u = { ...stepData, sheet_config: { ...stepData.sheet_config, campaign_channel: e.target.value } };
                      setStepData(u); onUpdate(u);
                    }}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm flex-1"
                  >
                    <option value="whatsapp">WhatsApp (Cloud API)</option>
                    <option value="whatsapp_interakt">WhatsApp (Interakt)</option>
                    <option value="whatsapp_flow">WhatsApp Flows</option>
                    <option value="jbm_rcs">JBM RCS</option>
                    <option value="telegram">Telegram</option>
                  </select>
                  <button
                    onClick={async () => {
                      const channel = stepData.sheet_config?.campaign_channel || 'whatsapp_interakt';
                      const forceCheckbox = document.getElementById(`campaign_force_${stepId}`);
                      const forcePush = forceCheckbox?.checked || false;
                      
                      const msg = forcePush
                        ? `FORCE PUSH: Re-send template to ALL contacts (including previously contacted) via ${channel}?\n\nOnly contacts whose last send was >24h ago will receive the message.`
                        : `Send template messages to NEW contacts in the sheet via ${channel}?\n\nAlready contacted numbers will be skipped.`;
                      if (!window.confirm(msg)) return;
                      
                      try {
                        const resp = await apiCallJson(`/api/chatbot/workflows/${workflowId}/campaign/trigger`, {
                          method: 'POST',
                          body: JSON.stringify({ node_id: stepId, channel, force_push: forcePush }),
                        });
                        alert(`✅ Campaign triggered!\n\nTemplate: ${resp.template_name}\nBatch size: ${resp.batch_size}\n\n${resp.message}`);
                      } catch (err) {
                        alert(`❌ Campaign failed: ${err.message || err}`);
                      }
                    }}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-lime-600 text-white text-sm font-medium rounded-lg hover:bg-lime-700 transition-colors whitespace-nowrap"
                  >
                    <Send className="h-3.5 w-3.5" />
                    Run Campaign
                  </button>
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    id={`campaign_force_${stepId}`}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-xs text-gray-600">Force push (re-send to previously contacted, respects 24h Meta policy)</span>
                </label>
                <p className="text-[10px] text-gray-500">
                  New contacts are always sent. Previously contacted are skipped unless force push is checked.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ==================== Sheet Write Config ==================== */}
        {stepData.message_type === 'sheet_write' && (
          <div className="border rounded-lg p-4 bg-rose-50 space-y-3">
            <h4 className="text-sm font-semibold text-rose-800 flex items-center">
              <Upload className="h-4 w-4 mr-2" />
              Sheet Write — Save Data to Spreadsheet
            </h4>
            <p className="text-xs text-rose-700">
              Writes collected variables to a spreadsheet row. Use after capturing
              customer data (name, phone, preferences, etc.).
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Source Type</label>
              <select
                value={stepData.sheet_config?.source_type || 'google_sheets'}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, source_type: e.target.value } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="google_sheets">Google Sheets</option>
              </select>
              <p className="text-[10px] text-gray-400 mt-1">Write requires Google Sheets with service account or OAuth credentials</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sheet URL</label>
              <input
                type="text"
                value={stepData.sheet_config?.source_url || ''}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, source_url: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sheet/Tab Name (optional)</label>
              <input
                type="text"
                value={stepData.sheet_config?.sheet_name || ''}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, sheet_name: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="Sheet1"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Service Account JSON</label>
              <textarea
                value={stepData.sheet_config?.google_credentials_json || ''}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, google_credentials_json: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                rows={3}
                placeholder='{"type": "service_account", ...}'
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono resize-none"
              />
              <p className="text-[10px] text-gray-400 mt-1">Paste the full JSON key file from Google Cloud Console</p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-rose-50 px-2 text-[10px] text-gray-500">OR</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">OAuth Credentials (for sheets shared with your Google account)</label>
              <textarea
                value={stepData.sheet_config?.oauth_credentials || ''}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, oauth_credentials: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                rows={3}
                placeholder='{"client_id": "...", "client_secret": "...", "refresh_token": "..."}'
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono resize-none"
              />
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <button
                  type="button"
                  onClick={async () => {
                    // Set up callback — auto-fills ALL sheet nodes in the workflow
                    window.__googleOAuthCallback = (credsJson) => {
                      try {
                        const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, oauth_credentials: credsJson } };
                        setStepData(updated);
                        onUpdate(updated);
                        
                        // Propagate to all other sheet_read/sheet_write nodes
                        if (nodes) {
                          nodes.forEach(n => {
                            const sd = n.data?.stepData;
                            if (sd && (sd.message_type === 'sheet_read' || sd.message_type === 'sheet_write') && n.id !== stepId) {
                              n.data = { ...n.data, stepData: { ...sd, sheet_config: { ...sd.sheet_config, oauth_credentials: credsJson } } };
                            }
                          });
                        }
                      } catch(e) { console.error('OAuth callback error:', e); }
                    };

                    try {
                      const resp = await apiCallJson('/api/chatbot/google/auth');
                      if (resp.auth_url) {
                        window.open(resp.auth_url, 'google_oauth', 'width=500,height=600');
                      }
                    } catch (err) {
                      alert(`Google Sheets integration not available: ${err.message || err}`);
                    }
                  }}
                  className="text-[11px] text-blue-600 hover:text-blue-800 underline"
                >
                  Connect Google Account →
                </button>
                <span className="text-[10px] text-gray-400">
                  Log in with Google to grant Sheets access. Auto-fills on completion.
                </span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Write Mode</label>
              <select
                value={stepData.sheet_config?.write_mode || 'append'}
                onChange={(e) => {
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, write_mode: e.target.value } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="append">Append (new row)</option>
                <option value="update">Update (find row by phone, update columns)</option>
              </select>
            </div>
            {stepData.sheet_config?.write_mode === 'update' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Match Column</label>
                  <input
                    type="text"
                    value={stepData.sheet_config?.match_column || ''}
                    onChange={(e) => {
                      const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, match_column: e.target.value } };
                      setStepData(updated);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder="Phone"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Match Variable</label>
                  <input
                    type="text"
                    value={stepData.sheet_config?.match_variable || ''}
                    onChange={(e) => {
                      const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, match_variable: e.target.value } };
                      setStepData(updated);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder="user_phone"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Column → Variable Mappings
              </label>
              <p className="text-[10px] text-gray-400 mb-2">
                Map sheet column names to workflow variables. One per line: ColumnName=variable_name
              </p>
              <textarea
                value={
                  Object.entries(stepData.sheet_config?.column_mappings || {})
                    .map(([col, varName]) => `${col}=${varName}`)
                    .join('\n')
                }
                onChange={(e) => {
                  const mappings = {};
                  e.target.value.split('\n').forEach(line => {
                    const [col, varName] = line.split('=').map(s => s.trim());
                    if (col && varName) mappings[col] = varName;
                  });
                  const updated = { ...stepData, sheet_config: { ...stepData.sheet_config, column_mappings: mappings } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                rows={4}
                placeholder={"Name=user_name\nPhone=user_phone\nCity=user_city\nPreference=selected_plan"}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono resize-none"
              />
            </div>
            <div className="text-xs text-rose-700 bg-rose-100 p-2 rounded space-y-1">
              <p><strong>How it works:</strong></p>
              <p>1. When this step executes, it reads the mapped workflow variables</p>
              <p>2. Writes them as a new row (append) or updates an existing row (update)</p>
              <p>3. Execution continues to the next step automatically</p>
            </div>
          </div>
        )}

        {/* ==================== Table Read Config (Local DB) ==================== */}
        {stepData.message_type === 'table_read' && (
          <div className="border rounded-lg p-4 bg-sky-50 space-y-3">
            <h4 className="text-sm font-semibold text-sky-800 flex items-center">
              <Database className="h-4 w-4 mr-2" />
              Table Read — Bulk Send from Local Database
            </h4>
            <p className="text-xs text-sky-700">
              Reads contacts from a local PostgreSQL table (uploaded CSV/Excel).
              No Google account needed.
            </p>

            {/* Upload file to create table */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Upload CSV/Excel to create table</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={stepData.table_config?.table_name || ''}
                  onChange={(e) => {
                    const updated = { ...stepData, table_config: { ...stepData.table_config, table_name: e.target.value } };
                    setStepData(updated);
                  }}
                  onBlur={() => onUpdate(stepData)}
                  placeholder="Table name (e.g. leads, contacts)"
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <label className="flex items-center gap-1 px-3 py-1.5 bg-white border border-dashed border-sky-300 rounded-lg cursor-pointer hover:bg-sky-50 transition-colors">
                  <Upload className="h-3.5 w-3.5 text-sky-500" />
                  <span className="text-xs text-sky-600">Upload</span>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls,.tsv"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const tableName = stepData.table_config?.table_name || file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 40);
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('table_name', tableName);
                      try {
                        const resp = await apiCall(`/api/chatbot/workflows/${workflowId}/tables`, {
                          method: 'POST',
                          body: formData,
                          headers: { 'Content-Type': undefined },
                        });
                        if (!resp.ok) {
                          const err = await resp.json().catch(() => ({}));
                          alert(`Upload failed: ${err.detail || resp.status}`);
                          return;
                        }
                        const result = await resp.json();
                        const cols = (result.columns || []).map(c => c.name || c.original_name);
                        const updated = {
                          ...stepData,
                          table_config: {
                            ...stepData.table_config,
                            table_name: result.table_name,
                            pg_table_name: result.pg_table_name,
                            _columns: cols,
                            _row_count: result.row_count,
                          }
                        };
                        setStepData(updated);
                        onUpdate(updated);
                        refreshTables();
                      } catch (err) {
                        alert(`Upload error: ${err.message}`);
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              {stepData.table_config?.pg_table_name && (
                <div className="mt-2 p-2 bg-white border border-sky-200 rounded text-[10px]">
                  <span className="font-medium text-sky-700">Table: </span>
                  <span className="font-mono text-gray-600">{stepData.table_config.table_name}</span>
                  {stepData.table_config._columns && (
                    <span className="text-gray-400 ml-2">
                      ({stepData.table_config._columns.join(', ')}) — {stepData.table_config._row_count || '?'} rows
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Or select existing table */}
            {workflowId && availableTables.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Or select existing table</label>
                <select
                  value={stepData.table_config?.pg_table_name || ''}
                  onChange={(e) => {
                    const t = availableTables.find(x => x.pg_table_name === e.target.value);
                    const cols = t ? (t.columns || []).map(c => c.name || c.original_name) : [];
                    const updated = { ...stepData, table_config: {
                      ...stepData.table_config,
                      table_name: t ? t.table_name : '',
                      pg_table_name: e.target.value,
                      _columns: cols,
                      _row_count: t ? t.row_count : undefined,
                    } };
                    setStepData(updated); onUpdate(updated);
                  }}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="">— select a table —</option>
                  {availableTables.map((t) => (
                    <option key={t.pg_table_name} value={t.pg_table_name}>
                      {(t.display_name || t.table_name)}{typeof t.row_count === 'number' ? ` (${t.row_count} rows)` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone Column Name</label>
              <input
                type="text"
                value={stepData.table_config?.phone_column || ''}
                onChange={(e) => {
                  const updated = { ...stepData, table_config: { ...stepData.table_config, phone_column: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="Auto-detect (mobile, phone, contact, etc.)"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Template Name</label>
              <input
                type="text"
                value={stepData.table_config?.template_name || ''}
                onChange={(e) => {
                  const updated = { ...stepData, table_config: { ...stepData.table_config, template_name: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="welcome_message_v1"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Template Language</label>
              <select
                value={stepData.table_config?.template_language || 'en_US'}
                onChange={(e) => {
                  const updated = { ...stepData, table_config: { ...stepData.table_config, template_language: e.target.value } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="en">en</option>
                <option value="en_US">en_US</option>
                <option value="hi">hi</option>
                <option value="mr">mr</option>
                <option value="ta">ta</option>
                <option value="te">te</option>
                <option value="bn">bn</option>
                <option value="gu">gu</option>
                <option value="de">de</option>
                <option value="fr">fr</option>
                <option value="es">es</option>
                <option value="ar">ar</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Template Params (column names, comma-separated)</label>
              <input
                type="text"
                value={(stepData.table_config?.template_params || []).join(', ')}
                onChange={(e) => {
                  const params = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  const updated = { ...stepData, table_config: { ...stepData.table_config, template_params: params } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="name, city (maps to {{1}}, {{2}} in template)"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>

            {/* Export button */}
            {stepData.table_config?.pg_table_name && workflowId && (
              <div className="flex items-center gap-2 pt-2 border-t border-sky-200">
                <a
                  href={`/api/chatbot/workflows/${workflowId}/tables/${stepData.table_config.table_name}/export?format=csv`}
                  target="_blank" rel="noopener"
                  className="text-xs text-sky-600 hover:text-sky-800 underline"
                >
                  Export CSV
                </a>
                <span className="text-gray-300">|</span>
                <a
                  href={`/api/chatbot/workflows/${workflowId}/tables/${stepData.table_config.table_name}/export?format=xlsx`}
                  target="_blank" rel="noopener"
                  className="text-xs text-sky-600 hover:text-sky-800 underline"
                >
                  Export Excel
                </a>
              </div>
            )}

            <div className="p-3 bg-sky-100 rounded-lg text-[10px] text-sky-800 space-y-1">
              <p className="font-semibold">How it works:</p>
              <p>1. Upload a CSV/Excel → creates a local database table</p>
              <p>2. Reads phone numbers and sends a WhatsApp template to each</p>
              <p>3. When a customer replies, they enter the chatbot flow</p>
              <p>4. Row data is available as variables: {'{{col_name}}'}, {'{{col_city}}'}, etc.</p>
              <p>5. No Google account or OAuth needed</p>
            </div>
          </div>
        )}

        {/* ==================== Table Write Config (Local DB) ==================== */}
        {stepData.message_type === 'table_write' && (
          <div className="border rounded-lg p-4 bg-teal-50 space-y-3">
            <h4 className="text-sm font-semibold text-teal-800 flex items-center">
              <DatabaseBackup className="h-4 w-4 mr-2" />
              Table Write — Save Data to Local Database
            </h4>
            <p className="text-xs text-teal-700">
              Writes collected conversation variables to a local database table.
              No Google account needed.
            </p>

            {/* Select or create table */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Table Name</label>
              <input
                type="text"
                value={stepData.table_config?.table_name || ''}
                onChange={(e) => {
                  const updated = { ...stepData, table_config: { ...stepData.table_config, table_name: e.target.value } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="Table name (e.g. responses, bookings)"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
              {workflowId && availableTables.length > 0 && (
                <select
                  value={stepData.table_config?.pg_table_name || ''}
                  onChange={(e) => {
                    const t = availableTables.find(x => x.pg_table_name === e.target.value);
                    const cols = t ? (t.columns || []).map(c => c.name || c.original_name) : [];
                    const updated = { ...stepData, table_config: {
                      ...stepData.table_config,
                      table_name: t ? t.table_name : (stepData.table_config?.table_name || ''),
                      pg_table_name: e.target.value,
                      _columns: cols,
                    } };
                    setStepData(updated); onUpdate(updated);
                  }}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm mt-1"
                >
                  <option value="">— or pick an existing table —</option>
                  {availableTables.map((t) => (
                    <option key={t.pg_table_name} value={t.pg_table_name}>
                      {(t.display_name || t.table_name)}{typeof t.row_count === 'number' ? ` (${t.row_count} rows)` : ''}
                    </option>
                  ))}
                </select>
              )}
              {stepData.table_config?._columns && (
                <p className="text-[10px] text-teal-600 mt-1">
                  Columns: {stepData.table_config._columns.join(', ')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Write Mode</label>
              <select
                value={stepData.table_config?.write_mode || 'append'}
                onChange={(e) => {
                  const updated = { ...stepData, table_config: { ...stepData.table_config, write_mode: e.target.value } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="append">Append (new row)</option>
                <option value="update">Update (find matching row)</option>
              </select>
            </div>

            {stepData.table_config?.write_mode === 'update' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Match Column</label>
                  <input
                    type="text"
                    value={stepData.table_config?.match_column || ''}
                    onChange={(e) => {
                      const updated = { ...stepData, table_config: { ...stepData.table_config, match_column: e.target.value } };
                      setStepData(updated);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder="phone"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Match Variable</label>
                  <input
                    type="text"
                    value={stepData.table_config?.match_variable || ''}
                    onChange={(e) => {
                      const updated = { ...stepData, table_config: { ...stepData.table_config, match_variable: e.target.value } };
                      setStepData(updated);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder="user_phone"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Column Mappings (table_column → workflow_variable)
              </label>
              <textarea
                value={Object.entries(stepData.table_config?.column_mappings || {}).map(([k, v]) => `${k}=${v}`).join('\n')}
                onChange={(e) => {
                  const mappings = {};
                  e.target.value.split('\n').forEach(line => {
                    const [col, varName] = line.split('=').map(s => s.trim());
                    if (col && varName) mappings[col] = varName;
                  });
                  const updated = { ...stepData, table_config: { ...stepData.table_config, column_mappings: mappings } };
                  setStepData(updated);
                }}
                onBlur={() => onUpdate(stepData)}
                rows={4}
                placeholder={'name=user_name\nphone=user_phone\ncity=user_city\nstatus=selected_plan'}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono resize-none"
              />
              <p className="text-[10px] text-gray-400 mt-1">One mapping per line: column_name=variable_name</p>
            </div>

            {/* Export button */}
            {stepData.table_config?.pg_table_name && workflowId && (
              <div className="flex items-center gap-2 pt-2 border-t border-teal-200">
                <a
                  href={`/api/chatbot/workflows/${workflowId}/tables/${stepData.table_config.table_name}/export?format=csv`}
                  target="_blank" rel="noopener"
                  className="text-xs text-teal-600 hover:text-teal-800 underline"
                >
                  Export CSV
                </a>
                <span className="text-gray-300">|</span>
                <a
                  href={`/api/chatbot/workflows/${workflowId}/tables/${stepData.table_config.table_name}/export?format=xlsx`}
                  target="_blank" rel="noopener"
                  className="text-xs text-teal-600 hover:text-teal-800 underline"
                >
                  Export Excel
                </a>
              </div>
            )}
          </div>
        )}

        {/* ==================== Request Callback Config ==================== */}
        {stepData.message_type === 'request_callback' && (
          <div className="border rounded-lg p-4 bg-orange-50 space-y-3">
            <h4 className="text-sm font-semibold text-orange-800 flex items-center">
              <PhoneCall className="h-4 w-4 mr-2" />
              Request Callback
            </h4>
            <p className="text-xs text-orange-700">
              When a customer reaches this node, they are marked as requesting a callback
              in the campaign memory. The admin can view pending callbacks from the campaign report.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Confirmation Message</label>
              <textarea
                value={stepData.message || 'Thank you! We will call you back shortly.'}
                onChange={(e) => { const u = { ...stepData, message: e.target.value }; setStepData(u); }}
                onBlur={() => onUpdate(stepData)}
                rows={3}
                placeholder="Thank you! Our team will call you back within 30 minutes."
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="p-2 bg-orange-100 rounded text-[10px] text-orange-800">
              <p className="font-semibold">What happens:</p>
              <p>1. Contact is marked as "callback requested" in campaign memory</p>
              <p>2. The confirmation message above is sent to the customer</p>
              <p>3. Admin sees the pending callback in Campaign Report → Callbacks tab</p>
              <p>4. After handling, admin marks it as done</p>
            </div>
          </div>
        )}

        {/* ==================== API Call Step Config ==================== */}
        {stepData.message_type === 'api_call' && (
          <div className="border rounded-lg p-4 bg-blue-50 space-y-3">
            <h4 className="text-sm font-semibold text-blue-800 flex items-center">
              <Code className="h-4 w-4 mr-2" />
              API Call — CRM / External Integration
            </h4>
            <p className="text-xs text-blue-700">
              A standalone step that calls an external/CRM API with authentication, captures
              the response into variables, then continues. No message is required — downstream
              nodes can branch on the result.
            </p>

            <input
              type="url"
              value={stepData.api_config?.url || ''}
              onChange={(e) => updateApiConfig('url', e.target.value)}
              onBlur={propagateNow}
              placeholder="API Endpoint URL  ·  supports {{variables}}"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <select
              value={stepData.api_config?.method || 'GET'}
              onChange={(e) => updateApiConfig('method', e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>

            {/* Authentication */}
            <div className="bg-white border border-blue-100 rounded p-2 space-y-2">
              <div className="flex items-center text-xs font-semibold text-blue-800">
                <ShieldCheck className="h-3 w-3 mr-1" />
                Authentication
              </div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Saved Connection</label>
              <select
                value={stepData.api_config?.connection_id || ''}
                onChange={(e) => updateApiConfig('connection_id', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="">— None (public / inline auth) —</option>
                {apiConnections.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.auth_type})</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500">
                Manage connections under <span className="font-medium">API Connections</span>.
                A saved connection supplies OAuth2 / login-token auth with automatic token
                caching and refresh.
              </p>
            </div>

            <textarea
              value={stepData.api_config?.headers || ''}
              onChange={(e) => updateApiConfig('headers', e.target.value)}
              onBlur={propagateNow}
              placeholder='Extra Headers (JSON): {"X-Trace": "{{session_id}}"}'
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
              rows={2}
            />
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-600">Request Body (JSON)</label>
              <VariableInserter
                groups={flowVarGroups} targetRef={apiNodeBodyRef}
                value={stepData.api_config?.body || ''}
                onChange={(v) => { updateApiConfig('body', v); }}
                note="Campaign contact columns are also available at runtime."
              />
            </div>
            <textarea
              ref={apiNodeBodyRef}
              value={stepData.api_config?.body || ''}
              onChange={(e) => updateApiConfig('body', e.target.value)}
              onBlur={propagateNow}
              placeholder='Request Body (JSON): {"orderId": "{{order_id}}"}'
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
              rows={3}
            />

            <ApiVarsHelp mode="flow" />

            {/* Response mapping */}
            <div className="bg-white border border-blue-100 rounded p-2 space-y-2">
              <div className="flex items-center text-xs font-semibold text-blue-800">
                <DatabaseBackup className="h-3 w-3 mr-1" />
                Response Mapping
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Response Variable Prefix</label>
                <input
                  type="text"
                  value={stepData.api_config?.response_var || ''}
                  onChange={(e) => updateApiConfig('response_var', e.target.value)}
                  onBlur={propagateNow}
                  placeholder="api  →  sets {{api_status}}, {{api_ok}}, {{api_response}}"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <label className="block text-xs font-medium text-gray-600">Map response fields → variables</label>
              {Object.entries(stepData.api_config?.result_mapping || {}).map(([varName, path], idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <input
                    type="text"
                    value={varName}
                    onChange={(e) => {
                      const rm = { ...(stepData.api_config?.result_mapping || {}) };
                      const val = rm[varName];
                      delete rm[varName];
                      rm[e.target.value] = val;
                      updateApiConfig('result_mapping', rm);
                    }}
                    onBlur={propagateNow}
                    placeholder="variable"
                    className="w-1/3 border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <span className="text-gray-400 text-xs">←</span>
                  <input
                    type="text"
                    value={path}
                    onChange={(e) => {
                      const rm = { ...(stepData.api_config?.result_mapping || {}) };
                      rm[varName] = e.target.value;
                      updateApiConfig('result_mapping', rm);
                    }}
                    onBlur={propagateNow}
                    placeholder="data.items[0].id"
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const rm = { ...(stepData.api_config?.result_mapping || {}) };
                      delete rm[varName];
                      updateApiConfig('result_mapping', rm);
                      propagateNow();
                    }}
                    className="text-gray-400 hover:text-red-600 p-1"
                    title="Remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const rm = { ...(stepData.api_config?.result_mapping || {}) };
                  let base = 'field', name = base, i = 1;
                  while (Object.prototype.hasOwnProperty.call(rm, name)) { name = `${base}${i++}`; }
                  rm[name] = '';
                  updateApiConfig('result_mapping', rm);
                  propagateNow();
                }}
                className="flex items-center gap-1 text-xs text-blue-600 font-medium"
              >
                <Plus className="h-3 w-3" /> Add field mapping
              </button>
            </div>

            {/* Remember against customer (write-once) + idempotency */}
            <div className="bg-white border border-blue-100 rounded p-2 space-y-2">
              <div className="flex items-center text-xs font-semibold text-blue-800">
                <DatabaseBackup className="h-3 w-3 mr-1" />
                Remember against customer (write-once)
              </div>
              <input
                type="text"
                value={(stepData.api_config?.persist || []).join(', ')}
                onChange={(e) => updateApiConfig(
                  'persist',
                  e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                )}
                onBlur={propagateNow}
                placeholder="lead_id, crm_id  (mapped variables to save)"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
              />
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Only call once per customer — idempotency key (optional)
                </label>
                <input
                  type="text"
                  value={stepData.api_config?.idempotency_key || ''}
                  onChange={(e) => updateApiConfig('idempotency_key', e.target.value)}
                  onBlur={propagateNow}
                  placeholder="lead_id  →  skip this call if the customer already has it"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                />
              </div>
              <p className="text-[11px] text-gray-500">
                Saved workflow-scoped as <span className="font-mono">customer_&lt;name&gt;</span>.
                With an idempotency key set, concurrent first-contacts are serialized (per-customer
                lock) so this API is called <span className="font-medium">at most once</span> per
                customer — the guard against duplicate leads.
              </p>
            </div>

            {/* Optional status messages */}
            <div className="bg-white border border-blue-100 rounded p-2 space-y-2">
              <div className="flex items-center text-xs font-semibold text-blue-800">
                <MessageSquare className="h-3 w-3 mr-1" />
                Optional Status Message
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">On Success (optional)</label>
                <input
                  type="text"
                  value={stepData.api_config?.success_message || ''}
                  onChange={(e) => updateApiConfig('success_message', e.target.value)}
                  onBlur={propagateNow}
                  placeholder="Done! Order status: {{order_status}}"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">On Failure (optional)</label>
                <input
                  type="text"
                  value={stepData.api_config?.failure_message || ''}
                  onChange={(e) => updateApiConfig('failure_message', e.target.value)}
                  onBlur={propagateNow}
                  placeholder="Sorry, we couldn't reach the system. Please try later."
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <p className="text-[11px] text-gray-500">
                Leave both blank for a silent step. Downstream conditional edges can branch on{' '}
                <span className="font-mono">{'{{'}{stepData.api_config?.response_var || 'api'}_ok{'}}'}</span>.
              </p>
            </div>
          </div>
        )}

        {/* ==================== Router Node Config ==================== */}
        {stepData.message_type === 'router' && (
          <div className="border rounded-lg p-4 bg-fuchsia-50 space-y-3">
            <h4 className="text-sm font-semibold text-fuchsia-800 flex items-center">
              <GitBranch className="h-4 w-4 mr-2" />
              Router — Conditional Branch (N paths)
            </h4>
            <p className="text-xs text-fuchsia-700">
              Sends the conversation down one of several paths by evaluating conditions
              top-to-bottom (first match wins). Optionally call an API first and branch on
              its response, and persist extracted ids against the customer so a returning
              customer is routed differently from a new lead.
            </p>

            <RouterVarsHelp />

            {/* Optional embedded source */}
            <div className="bg-white border border-fuchsia-100 rounded p-2 space-y-2">
              <div className="flex items-center text-xs font-semibold text-fuchsia-800">
                <Code className="h-3 w-3 mr-1" />
                Source (optional)
              </div>
              <select
                value={stepData.router_config?.source || 'none'}
                onChange={(e) => updateRouterConfig('source', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="none">None — route on existing variables</option>
                <option value="api">API — call, extract, then route</option>
                <option value="sheet">Sheet — look up a row, then route</option>
                <option value="table">Table (Local DB) — look up a row, then route</option>
              </select>

              {stepData.router_config?.source === 'api' && (
                <div className="space-y-2 pt-1">
                  <input
                    type="url"
                    value={stepData.router_config?.api_config?.url || ''}
                    onChange={(e) => updateRouterApiConfig('url', e.target.value)}
                    onBlur={propagateNow}
                    placeholder="API Endpoint URL  ·  supports {{variables}}"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <div className="flex gap-2">
                    <select
                      value={stepData.router_config?.api_config?.method || 'GET'}
                      onChange={(e) => updateRouterApiConfig('method', e.target.value)}
                      className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                    <select
                      value={stepData.router_config?.api_config?.connection_id || ''}
                      onChange={(e) => updateRouterApiConfig('connection_id', e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">— Auth: None / inline —</option>
                      {apiConnections.map((c) => (
                        <option key={c.id} value={c.id}>{c.name} ({c.auth_type})</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    value={stepData.router_config?.api_config?.body || ''}
                    onChange={(e) => updateRouterApiConfig('body', e.target.value)}
                    onBlur={propagateNow}
                    placeholder='Request Body (JSON): {"leadId": "{{customer_lead_id}}"}'
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    rows={2}
                  />
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Response variable prefix</label>
                    <input
                      type="text"
                      value={stepData.router_config?.api_config?.response_var || ''}
                      onChange={(e) => updateRouterApiConfig('response_var', e.target.value)}
                      onBlur={propagateNow}
                      placeholder="api  →  sets {{api_ok}} used by the error path"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <label className="block text-xs font-medium text-gray-600">Extract response fields → variables (dotted path → name)</label>
                  {Object.entries(stepData.router_config?.api_config?.result_mapping || {}).map(([varName, path], idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <input
                        type="text"
                        value={varName}
                        onChange={(e) => {
                          const rm = { ...(stepData.router_config?.api_config?.result_mapping || {}) };
                          const val = rm[varName]; delete rm[varName]; rm[e.target.value] = val;
                          updateRouterApiConfig('result_mapping', rm);
                        }}
                        placeholder="lead_id"
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                      />
                      <span className="text-gray-400 text-xs">←</span>
                      <input
                        type="text"
                        value={path}
                        onChange={(e) => {
                          const rm = { ...(stepData.router_config?.api_config?.result_mapping || {}) };
                          rm[varName] = e.target.value;
                          updateRouterApiConfig('result_mapping', rm);
                        }}
                        placeholder="data.lead.id"
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const rm = { ...(stepData.router_config?.api_config?.result_mapping || {}) };
                          delete rm[varName];
                          updateRouterApiConfig('result_mapping', rm);
                        }}
                        className="text-red-500 text-xs px-1"
                      >✕</button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const rm = { ...(stepData.router_config?.api_config?.result_mapping || {}) };
                      rm[`field_${Object.keys(rm).length + 1}`] = '';
                      updateRouterApiConfig('result_mapping', rm);
                    }}
                    className="text-xs text-fuchsia-700 font-medium"
                  >+ Add field</button>
                  <ApiVarsHelp mode="flow" />
                </div>
              )}

              {stepData.router_config?.source === 'sheet' && (
                <div className="space-y-2 pt-1">
                  <select
                    value={stepData.router_config?.sheet_config?.source_type || 'csv_url'}
                    onChange={(e) => updateRouterSheetConfig('source_type', e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="csv_url">CSV URL</option>
                    <option value="google_sheet">Google Sheet</option>
                    <option value="excel_url">Excel URL</option>
                  </select>
                  <input
                    type="text"
                    value={stepData.router_config?.sheet_config?.source_url || ''}
                    onChange={(e) => updateRouterSheetConfig('source_url', e.target.value)}
                    onBlur={propagateNow}
                    placeholder="Sheet / CSV URL"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  {stepData.router_config?.sheet_config?.source_type === 'google_sheet' && (
                    <input
                      type="text"
                      value={stepData.router_config?.sheet_config?.sheet_name || ''}
                      onChange={(e) => updateRouterSheetConfig('sheet_name', e.target.value)}
                      onBlur={propagateNow}
                      placeholder="Tab / sheet name (optional)"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={stepData.router_config?.sheet_config?.filter_column || ''}
                      onChange={(e) => updateRouterSheetConfig('filter_column', e.target.value)}
                      onBlur={propagateNow}
                      placeholder="match column (e.g. phone)"
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                    <input
                      type="text"
                      value={stepData.router_config?.sheet_config?.filter_value || ''}
                      onChange={(e) => updateRouterSheetConfig('filter_value', e.target.value)}
                      onBlur={propagateNow}
                      placeholder="match value  ·  {{variables}}"
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    />
                  </div>
                  <label className="block text-xs font-medium text-gray-600">Map columns → variables</label>
                  {Object.entries(stepData.router_config?.sheet_config?.column_mappings || {}).map(([col, varName], idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <input type="text" value={col}
                        onChange={(e) => updateRouterColMap('sheet_config', updateRouterSheetConfig, e.target.value, varName, col)}
                        placeholder="column" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono" />
                      <span className="text-gray-400 text-xs">→</span>
                      <input type="text" value={varName}
                        onChange={(e) => updateRouterColMap('sheet_config', updateRouterSheetConfig, col, e.target.value)}
                        placeholder="var_name" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono" />
                      <button type="button" onClick={() => {
                        const cm = { ...(stepData.router_config?.sheet_config?.column_mappings || {}) }; delete cm[col];
                        updateRouterSheetConfig('column_mappings', cm);
                      }} className="text-red-500 text-xs px-1">✕</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => {
                    const cm = { ...(stepData.router_config?.sheet_config?.column_mappings || {}) };
                    cm[`col_${Object.keys(cm).length + 1}`] = '';
                    updateRouterSheetConfig('column_mappings', cm);
                  }} className="text-xs text-fuchsia-700 font-medium">+ Add mapping</button>
                </div>
              )}

              {stepData.router_config?.source === 'table' && (
                <div className="space-y-2 pt-1">
                  {availableTables.length > 0 ? (
                    <select
                      value={stepData.router_config?.table_config?.pg_table_name || ''}
                      onChange={(e) => {
                        const t = availableTables.find(x => x.pg_table_name === e.target.value);
                        const rc = stepData.router_config || {};
                        const updated = { ...stepData, router_config: { ...rc, table_config: {
                          ...(rc.table_config || {}),
                          pg_table_name: e.target.value,
                          table_name: t ? (t.table_name || t.display_name || '') : (rc.table_config?.table_name || ''),
                        } } };
                        setStepData(updated); onUpdate(updated);
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">— select a table —</option>
                      {availableTables.map((t) => (
                        <option key={t.pg_table_name} value={t.pg_table_name}>
                          {(t.display_name || t.table_name)}{typeof t.row_count === 'number' ? ` (${t.row_count} rows)` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={stepData.router_config?.table_config?.pg_table_name || ''}
                      onChange={(e) => updateRouterTableConfig('pg_table_name', e.target.value)}
                      onBlur={propagateNow}
                      placeholder="table (no tables found — enter pg_table_name, or upload one first)"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    />
                  )}
                  <datalist id="rtr-table-cols">
                    {((availableTables.find(t => t.pg_table_name === (stepData.router_config?.table_config?.pg_table_name)) || {}).columns || [])
                      .map((c, i) => <option key={i} value={c.name || c.original_name} />)}
                  </datalist>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      list="rtr-table-cols"
                      value={stepData.router_config?.table_config?.filter_column || ''}
                      onChange={(e) => updateRouterTableConfig('filter_column', e.target.value)}
                      onBlur={propagateNow}
                      placeholder="match column (e.g. phone)"
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                    <input
                      type="text"
                      value={stepData.router_config?.table_config?.filter_value || ''}
                      onChange={(e) => updateRouterTableConfig('filter_value', e.target.value)}
                      onBlur={propagateNow}
                      placeholder="match value  ·  {{variables}}"
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    />
                  </div>
                  <label className="block text-xs font-medium text-gray-600">Map columns → variables</label>
                  {Object.entries(stepData.router_config?.table_config?.column_mappings || {}).map(([col, varName], idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <input type="text" value={col}
                        list="rtr-table-cols"
                        onChange={(e) => updateRouterColMap('table_config', updateRouterTableConfig, e.target.value, varName, col)}
                        placeholder="column" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono" />
                      <span className="text-gray-400 text-xs">→</span>
                      <input type="text" value={varName}
                        onChange={(e) => updateRouterColMap('table_config', updateRouterTableConfig, col, e.target.value)}
                        placeholder="var_name" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono" />
                      <button type="button" onClick={() => {
                        const cm = { ...(stepData.router_config?.table_config?.column_mappings || {}) }; delete cm[col];
                        updateRouterTableConfig('column_mappings', cm);
                      }} className="text-red-500 text-xs px-1">✕</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => {
                    const cm = { ...(stepData.router_config?.table_config?.column_mappings || {}) };
                    cm[`col_${Object.keys(cm).length + 1}`] = '';
                    updateRouterTableConfig('column_mappings', cm);
                  }} className="text-xs text-fuchsia-700 font-medium">+ Add mapping</button>
                </div>
              )}

              {stepData.router_config?.source !== 'none' && (
                <div className="pt-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Only fetch once per customer — idempotency key (optional)
                  </label>
                  <input
                    type="text"
                    value={stepData.router_config?.idempotency_key || ''}
                    onChange={(e) => updateRouterConfig('idempotency_key', e.target.value)}
                    onBlur={propagateNow}
                    placeholder="lead_id  →  skip the fetch if the customer already has it"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Serializes concurrent first-contacts (per-customer lock) so the source runs
                    at most once per customer.
                  </p>
                </div>
              )}
            </div>

            {/* Persist to durable per-customer memory (write-once) */}
            <div className="bg-white border border-fuchsia-100 rounded p-2 space-y-2">
              <div className="flex items-center text-xs font-semibold text-fuchsia-800">
                <DatabaseBackup className="h-3 w-3 mr-1" />
                Remember against customer (write-once)
              </div>
              <input
                type="text"
                value={(stepData.router_config?.persist || []).join(', ')}
                onChange={(e) => updateRouterConfig(
                  'persist',
                  e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                )}
                onBlur={propagateNow}
                placeholder="lead_id, order_id  (variable names to save)"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
              />
              <p className="text-[11px] text-gray-500">
                Saved workflow-scoped, per-customer. Loaded on the next contact as{' '}
                <span className="font-mono">customer_&lt;name&gt;</span> (e.g.{' '}
                <span className="font-mono">customer_lead_id</span>). Write-once: an existing
                value is never overwritten.
              </p>
            </div>

            {/* Routes */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-600">Routes (first match wins)</label>
              {(stepData.router_config?.routes || []).map((route, idx) => (
                <div key={idx} className="border border-fuchsia-100 rounded p-2 bg-white space-y-1">
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] text-gray-400 w-8">#{idx + 1}</span>
                    <input
                      type="text"
                      value={route.when || ''}
                      onChange={(e) => updateRoute(idx, 'when', e.target.value)}
                      onBlur={propagateNow}
                      placeholder={'when  ·  e.g.  customer_lead_id != ""'}
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    />
                    <button type="button" onClick={() => removeRoute(idx)} className="text-red-500 text-xs px-1">✕</button>
                  </div>
                  <div className="flex items-center gap-1 pl-9">
                    <span className="text-gray-400 text-xs">→ go to</span>
                    <select
                      value={route.to || ''}
                      onChange={(e) => updateRoute(idx, 'to', e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">— select step —</option>
                      {nodes.filter(n => n.id !== selectedNode?.id).map(n => (
                        <option key={n.id} value={n.id}>{n.data.label} ({n.id})</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
              <button type="button" onClick={addRoute} className="text-xs text-fuchsia-700 font-medium">+ Add route</button>
              <p className="text-[11px] text-gray-500">
                Leave <span className="font-mono">when</span> blank for an unconditional
                catch-all (place it last).
              </p>
            </div>

            {/* Fallbacks */}
            <div className="grid grid-cols-1 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Default (no route matched)</label>
                <select
                  value={stepData.router_config?.default_to || ''}
                  onChange={(e) => updateRouterConfig('default_to', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="">— none —</option>
                  {nodes.filter(n => n.id !== selectedNode?.id).map(n => (
                    <option key={n.id} value={n.id}>{n.data.label} ({n.id})</option>
                  ))}
                </select>
              </div>
              {stepData.router_config?.source !== 'none' && stepData.router_config?.source && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">On source error / not found</label>
                  <select
                    value={stepData.router_config?.error_to || ''}
                    onChange={(e) => updateRouterConfig('error_to', e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="">— none (fall through to Default) —</option>
                    {nodes.filter(n => n.id !== selectedNode?.id).map(n => (
                      <option key={n.id} value={n.id}>{n.data.label} ({n.id})</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== Sequenced API Node Config ==================== */}
        {stepData.message_type === 'api_sequence' && (
          <div className="border rounded-lg p-4 bg-teal-50 space-y-3">
            <h4 className="text-sm font-semibold text-teal-800 flex items-center">
              <ListOrdered className="h-4 w-4 mr-2" />
              Sequenced API — chain calls (#1 → #2 → …)
            </h4>
            <p className="text-xs text-teal-700">
              Calls each step in order. A step can use variables extracted by earlier steps as{' '}
              <span className="font-mono">{'{{'}name{'}}'}</span> in its URL/body — e.g. step 1
              creates a record and extracts <span className="font-mono">lead_id</span>, step 2 posts
              to <span className="font-mono">.../{'{{'}lead_id{'}}'}</span>. Each step also sets{' '}
              <span className="font-mono">step_&lt;n&gt;_ok</span> for later branching.
            </p>

            <label className="flex items-center gap-2 text-xs font-medium text-teal-800">
              <input type="checkbox"
                checked={(stepData.sequence_config?.stop_on_error) !== false}
                onChange={(e) => updateSequenceConfig('stop_on_error', e.target.checked)} />
              Stop the chain if a step fails
            </label>

            {_seqSteps().map((st, idx) => (
              <div key={idx} className="border border-teal-100 rounded p-2 bg-white space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-teal-800">Step #{idx + 1}</span>
                  <button type="button" onClick={() => removeSeqStep(idx)} className="text-red-500 text-xs px-1">✕</button>
                </div>
                <div className="flex gap-1">
                  <select value={st.api?.method || 'POST'}
                    onChange={(e) => updateSeqStepApi(idx, 'method', e.target.value)}
                    className="w-20 border border-gray-300 rounded px-1 py-1 text-xs">
                    <option>POST</option><option>GET</option><option>PUT</option><option>PATCH</option><option>DELETE</option>
                  </select>
                  <input type="text" value={st.api?.url || ''}
                    onChange={(e) => updateSeqStepApi(idx, 'url', e.target.value)}
                    onBlur={propagateNow}
                    placeholder="API URL · supports {{vars from earlier steps}}, {{customer_lead_id}}, {{phone}}"
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                </div>
                <select value={st.api?.connection_id || ''}
                  onChange={(e) => updateSeqStepApi(idx, 'connection_id', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs">
                  <option value="">— Auth: None / inline —</option>
                  {apiConnections.map((c) => (<option key={c.id} value={c.id}>{c.name} ({c.auth_type})</option>))}
                </select>
                <textarea value={st.api?.body || ''}
                  onChange={(e) => updateSeqStepApi(idx, 'body', e.target.value)}
                  onBlur={propagateNow}
                  placeholder='Body (JSON): {"leadId":"{{lead_id}}"}'
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" rows={2} />
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Response variable prefix (for step_ok / error)</label>
                  <input type="text" value={st.api?.response_var || ''}
                    onChange={(e) => updateSeqStepApi(idx, 'response_var', e.target.value)}
                    onBlur={propagateNow}
                    placeholder="e.g. create  →  {{create_ok}}"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                </div>
                <label className="block text-[10px] text-gray-500">Extract response → variables for later steps (dotted path → name)</label>
                {Object.entries(st.api?.result_mapping || {}).map(([nm, pth], j) => (
                  <div key={j} className="flex items-center gap-1">
                    <input type="text" value={nm}
                      onChange={(e) => { const rm = { ...(st.api?.result_mapping || {}) }; const v = rm[nm]; delete rm[nm]; rm[e.target.value] = v; updateSeqStepApi(idx, 'result_mapping', rm); }}
                      placeholder="lead_id" className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs font-mono" />
                    <span className="text-gray-400 text-xs">←</span>
                    <input type="text" value={pth}
                      onChange={(e) => { const rm = { ...(st.api?.result_mapping || {}) }; rm[nm] = e.target.value; updateSeqStepApi(idx, 'result_mapping', rm); }}
                      placeholder="data[0].details.id" className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs font-mono" />
                    <button type="button" onClick={() => { const rm = { ...(st.api?.result_mapping || {}) }; delete rm[nm]; updateSeqStepApi(idx, 'result_mapping', rm); }} className="text-red-500 text-xs px-1">✕</button>
                  </div>
                ))}
                <button type="button" onClick={() => { const rm = { ...(st.api?.result_mapping || {}) }; rm[`field_${Object.keys(rm).length + 1}`] = ''; updateSeqStepApi(idx, 'result_mapping', rm); }} className="text-[11px] text-teal-700">+ field</button>
              </div>
            ))}
            <button type="button" onClick={addSeqStep} className="text-xs text-teal-700 font-medium">+ Add step</button>

            <div className="bg-white border border-teal-100 rounded p-2 space-y-2">
              <div className="flex items-center text-xs font-semibold text-teal-800">
                <DatabaseBackup className="h-3 w-3 mr-1" />
                Remember against customer (write-once, optional)
              </div>
              <input type="text"
                value={(stepData.sequence_config?.persist || []).join(', ')}
                onChange={(e) => updateSequenceConfig('persist', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                onBlur={propagateNow}
                placeholder="lead_id  (extracted names to save as customer_<name>)"
                className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
            </div>
          </div>
        )}

        {/* ==================== Sync Job Config ==================== */}
        {stepData.message_type === 'eod_sync_job' && (
          <div className="border rounded-lg p-4 bg-indigo-50 space-y-3">
            <h4 className="text-sm font-semibold text-indigo-800 flex items-center">
              <RefreshCw className="h-4 w-4 mr-2" />
               Sync Job — Regular Response Writeback
            </h4>
            <p className="text-xs text-indigo-700">
              Runs once daily at a configured time. Collects all customer interactions
              from today and writes response summaries back to the source sheet/table.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Run every</label>
              <select
                value={stepData.job_config?.interval_minutes || 1440}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, interval_minutes: parseInt(e.target.value), interval_hours: parseInt(e.target.value) / 60 } };
                  setStepData(u); onUpdate(u);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={240}>4 hours</option>
                <option value={360}>6 hours</option>
                <option value={720}>12 hours</option>
                <option value={1440}>24 hours (daily)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start time (24h)</label>
              <input
                type="text"
                value={stepData.job_config?.run_at_time || '23:00'}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, run_at_time: e.target.value } };
                  setStepData(u);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="23:00"
                className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
              <select
                value={stepData.job_config?.timezone || 'Asia/Kolkata'}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, timezone: e.target.value } };
                  setStepData(u); onUpdate(u);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="UTC">UTC</option>
                <option value="US/Eastern">US/Eastern</option>
                <option value="Europe/London">Europe/London</option>
                <option value="Europe/Berlin">Europe/Berlin</option>
                <option value="Asia/Dubai">Asia/Dubai</option>
                <option value="Asia/Singapore">Asia/Singapore</option>
              </select>
            </div>
            <div className="border-t border-indigo-200 pt-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Sync source (what to sync)</label>
              <select
                value={stepData.job_config?.sync_source || 'campaign'}
                onChange={(e) => {
                  const src = e.target.value;
                  const jc = { ...stepData.job_config, sync_source: src };
                  // Conversations-source is an API push (no source row to write
                  // back to) — pin the destination so the API config is honored.
                  if (src === 'conversations') jc.sync_destination = 'api';
                  const u = { ...stepData, job_config: jc };
                  setStepData(u); onUpdate(u);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="campaign">Campaign contacts (from a sheet/table campaign node)</option>
                <option value="conversations">All conversations (every customer who messaged — no campaign needed)</option>
              </select>
              <p className="text-[11px] text-gray-500 mt-1">
                {(stepData.job_config?.sync_source || 'campaign') === 'conversations'
                  ? 'Every conversation active in the lookback window is a sync input — organic inbound included. Writes to an API destination (there is no source row to write back to).'
                  : 'Sync the contacts seeded by a campaign node and write the summary back to that sheet/table.'}
              </p>
            </div>

            {(stepData.job_config?.sync_source || 'campaign') === 'conversations' ? (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Lookback window (hours)</label>
                <input
                  type="number" min="1"
                  value={stepData.job_config?.lookback_hours ?? 24}
                  onChange={(e) => {
                    const u = { ...stepData, job_config: { ...stepData.job_config, lookback_hours: e.target.value === '' ? '' : Number(e.target.value) } };
                    setStepData(u);
                  }}
                  onBlur={() => onUpdate(stepData)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <label className="flex items-center gap-2 mt-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={stepData.job_config?.only_unsynced !== false}
                    onChange={(e) => {
                      const u = { ...stepData, job_config: { ...stepData.job_config, only_unsynced: e.target.checked } };
                      setStepData(u); onUpdate(u);
                    }}
                  />
                  Only sync conversations with new activity since last sync (incremental)
                </label>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Campaign Node (source sheet_read/table_read)</label>
                <select
                  value={stepData.job_config?.campaign_node_id || ''}
                  onChange={(e) => {
                    const u = { ...stepData, job_config: { ...stepData.job_config, campaign_node_id: e.target.value } };
                    setStepData(u); onUpdate(u);
                  }}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="">— Select campaign node —</option>
                  {nodes.filter(n => {
                    const mt = n.data?.stepData?.message_type;
                    return mt === 'sheet_read' || mt === 'table_read';
                  }).map(n => (
                    <option key={n.id} value={n.id}>
                      {n.data?.label || n.id} ({n.data?.stepData?.message_type})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Writeback Column Name</label>
              <input
                type="text"
                value={stepData.job_config?.writeback_column || 'response_summary'}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, writeback_column: e.target.value } };
                  setStepData(u);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="response_summary"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Format</label>
              <select
                value={stepData.job_config?.format || 'json'}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, format: e.target.value } };
                  setStepData(u); onUpdate(u);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="json">JSON</option>
                <option value="text">Text (one message per line)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Include messages from</label>
              <select
                value={stepData.job_config?.message_filter || 'all'}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, message_filter: e.target.value } };
                  setStepData(u); onUpdate(u);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="all">Bot + User messages</option>
                <option value="user_only">User messages only</option>
              </select>
            </div>

            {/* ── Sync destination: source (sheet/table), API, or both ── */}
            <div className="border-t border-indigo-200 pt-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Sync destination</label>
              {(stepData.job_config?.sync_source || 'campaign') === 'conversations' ? (
                <div className="text-sm text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-1">
                  API (conversations are pushed to the external API)
                </div>
              ) : (
                <select
                  value={stepData.job_config?.sync_destination || 'source'}
                  onChange={(e) => {
                    const u = { ...stepData, job_config: { ...stepData.job_config, sync_destination: e.target.value } };
                    setStepData(u); onUpdate(u);
                  }}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="source">Source only (write back to sheet/table)</option>
                  <option value="api">API only (call out to an external API)</option>
                  <option value="both">Both (sheet/table + API)</option>
                </select>
              )}
              <p className="text-[11px] text-gray-500 mt-1">
                The same per-conversation summaries are sent to the chosen destination(s).
              </p>
            </div>

            {(['api', 'both'].includes(stepData.job_config?.sync_destination)
              || (stepData.job_config?.sync_source || 'campaign') === 'conversations') && (
              <div className="border border-indigo-200 rounded-lg p-3 bg-white space-y-2">
                <h5 className="text-xs font-semibold text-indigo-800">API destination</h5>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Auth connection</label>
                  <select
                    value={stepData.job_config?.api_sync?.connection_id || ''}
                    onChange={(e) => {
                      const u = { ...stepData, job_config: { ...stepData.job_config,
                        api_sync: { ...(stepData.job_config?.api_sync || {}), connection_id: e.target.value } } };
                      setStepData(u); onUpdate(u);
                    }}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="">— No auth —</option>
                    {apiConnections.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} ({c.auth_type})</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Reuses the same API Connections as the API Call node (secrets stay server-side).
                  </p>
                </div>
                {!(stepData.job_config?.api_sync?.router) && (
                  <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Endpoint URL</label>
                  <input
                    type="text"
                    value={stepData.job_config?.api_sync?.url || ''}
                    onChange={(e) => {
                      const u = { ...stepData, job_config: { ...stepData.job_config,
                        api_sync: { ...(stepData.job_config?.api_sync || {}), url: e.target.value } } };
                      setStepData(u);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder="https://crm.example.com/api/conversations"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Method</label>
                    <select
                      value={stepData.job_config?.api_sync?.method || 'POST'}
                      onChange={(e) => {
                        const u = { ...stepData, job_config: { ...stepData.job_config,
                          api_sync: { ...(stepData.job_config?.api_sync || {}), method: e.target.value } } };
                        setStepData(u); onUpdate(u);
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Mode</label>
                    <select
                      value={stepData.job_config?.api_sync?.mode || 'per_record'}
                      onChange={(e) => {
                        const u = { ...stepData, job_config: { ...stepData.job_config,
                          api_sync: { ...(stepData.job_config?.api_sync || {}), mode: e.target.value } } };
                        setStepData(u); onUpdate(u);
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="per_record">Per record (one call each)</option>
                      <option value="bulk">Bulk (one call, all records)</option>
                    </select>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500">
                  {(stepData.job_config?.api_sync?.mode || 'per_record') === 'bulk'
                    ? 'Bulk: a single request with a "records" array of all conversation summaries.'
                    : 'Per record: one request per conversation, body = the record.'}
                </p>
                  </>
                )}

                {/* Request config (parity with the API Call node). The connection
                    above is only auth; these define the actual request. */}
                <div className="border-t border-indigo-100 pt-2 space-y-2">
                  {!(stepData.job_config?.api_sync?.router) && (
                  <>
                  <div className="text-[11px] font-semibold text-indigo-800">Request configuration</div>
                  <textarea
                    value={stepData.job_config?.api_sync?.headers || ''}
                    onChange={(e) => {
                      const u = { ...stepData, job_config: { ...stepData.job_config,
                        api_sync: { ...(stepData.job_config?.api_sync || {}), headers: e.target.value } } };
                      setStepData(u);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder='Extra Headers (JSON): {"X-Source": "ccan"}'
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    rows={2}
                  />
                  <textarea
                    value={stepData.job_config?.api_sync?.params || ''}
                    onChange={(e) => {
                      const u = { ...stepData, job_config: { ...stepData.job_config,
                        api_sync: { ...(stepData.job_config?.api_sync || {}), params: e.target.value } } };
                      setStepData(u);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder='Query Params (JSON): {"tenant": "acme"}'
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    rows={2}
                  />
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-gray-600">Body template (JSON, optional)</label>
                    <VariableInserter
                      groups={syncVarGroups} targetRef={syncBodyRef}
                      value={stepData.job_config?.api_sync?.body || ''}
                      onChange={(v) => {
                        const u = { ...stepData, job_config: { ...stepData.job_config,
                          api_sync: { ...(stepData.job_config?.api_sync || {}), body: v } } };
                        setStepData(u); onUpdate(u);
                      }}
                    />
                  </div>
                  <textarea
                    ref={syncBodyRef}
                    value={stepData.job_config?.api_sync?.body || ''}
                    onChange={(e) => {
                      const u = { ...stepData, job_config: { ...stepData.job_config,
                        api_sync: { ...(stepData.job_config?.api_sync || {}), body: e.target.value } } };
                      setStepData(u);
                    }}
                    onBlur={() => onUpdate(stepData)}
                    placeholder={(stepData.job_config?.api_sync?.mode || 'per_record') === 'bulk'
                      ? 'Body template (JSON, optional): {"source":"ccan","items":"{{records}}"}'
                      : 'Body template (JSON, optional): {"source":"ccan","data":"{{record}}"}'}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    rows={3}
                  />
                  <p className="text-[11px] text-gray-500">
                    Optional. Leave blank to send {(stepData.job_config?.api_sync?.mode || 'per_record') === 'bulk'
                      ? 'a default { records: [...] } envelope'
                      : 'the record object directly'}.
                  </p>
                  <ApiVarsHelp mode="sync" />
                  {(stepData.job_config?.api_sync?.mode || 'per_record') === 'bulk' && (
                    <input
                      type="text"
                      value={stepData.job_config?.api_sync?.records_key || ''}
                      onChange={(e) => {
                        const u = { ...stepData, job_config: { ...stepData.job_config,
                          api_sync: { ...(stepData.job_config?.api_sync || {}), records_key: e.target.value } } };
                        setStepData(u);
                      }}
                      onBlur={() => onUpdate(stepData)}
                      placeholder="Records array key for default envelope (default: records)"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    />
                  )}
                  </>
                  )}

                  {/* Per-customer Router (memory-aware API selection) */}
                  <div className="border-t border-indigo-200 pt-2 mt-1 space-y-2">
                    <label className="flex items-center gap-2 text-xs font-semibold text-indigo-800">
                      <input type="checkbox"
                        checked={!!_apiSync().router}
                        onChange={(e) => toggleSyncRouter(e.target.checked)} />
                      <GitBranch className="h-3 w-3" />
                      Per-customer Router (call different APIs based on stored customer values)
                    </label>
                    {_apiSync().router && (
                      <div className="space-y-2 pl-1">
                        <p className="text-[11px] text-indigo-700">
                          Per record, routes are checked top-to-bottom (first match wins). A route’s
                          extracted fields are saved against the customer (write-once) as{' '}
                          <span className="font-mono">customer_&lt;name&gt;</span>, so the next run can
                          route differently — e.g. API 1 → save <span className="font-mono">lead_id</span>,
                          then API 2 with <span className="font-mono">{'{{'}customer_lead_id{'}}'}</span>.
                          The single-API fields above are ignored while this is on.
                        </p>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Only-once key (idempotency, optional)</label>
                          <input type="text"
                            value={_apiSync().router?.idempotency_key || ''}
                            onChange={(e) => updateSyncRouter('idempotency_key', e.target.value)}
                            onBlur={() => onUpdate(stepData)}
                            placeholder="lead_id — the route that saves it runs at most once per customer"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                        </div>
                        {_syncRoutes().map((rt, idx) => (
                          <div key={idx} className="border border-indigo-100 rounded p-2 bg-white space-y-1">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-gray-400 w-6">#{idx + 1}</span>
                              <input type="text" value={rt.when || ''}
                                onChange={(e) => updateSyncRoute(idx, { when: e.target.value })}
                                onBlur={() => onUpdate(stepData)}
                                placeholder={'when · e.g. customer_lead_id == ""  (blank = catch-all)'}
                                className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                              <button type="button" onClick={() => removeSyncRoute(idx)} className="text-red-500 text-xs px-1">✕</button>
                            </div>
                            <div className="flex gap-1 pl-7">
                              <select value={rt.api?.method || 'POST'}
                                onChange={(e) => updateSyncRouteApi(idx, 'method', e.target.value)}
                                className="w-20 border border-gray-300 rounded px-1 py-1 text-xs">
                                <option>POST</option><option>GET</option><option>PUT</option><option>PATCH</option>
                              </select>
                              <input type="text" value={rt.api?.url || ''}
                                onChange={(e) => updateSyncRouteApi(idx, 'url', e.target.value)}
                                onBlur={() => onUpdate(stepData)}
                                placeholder="API URL · supports {{customer_lead_id}}, {{phone}}"
                                className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                            </div>
                            <textarea value={rt.api?.body || ''}
                              onChange={(e) => updateSyncRouteApi(idx, 'body', e.target.value)}
                              onBlur={() => onUpdate(stepData)}
                              placeholder='Body (JSON): {"leadId":"{{customer_lead_id}}","phone":"{{phone}}"}'
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono"
                              rows={3} />
                            <div className="pl-7 space-y-1">
                              <label className="block text-[10px] text-gray-500">Extract response → save against customer (dotted path → name)</label>
                              {Object.entries(rt.api?.result_mapping || {}).map(([nm, pth], j) => (
                                <div key={j} className="flex items-center gap-1">
                                  <input type="text" value={nm}
                                    onChange={(e) => { const rm = { ...(rt.api?.result_mapping || {}) }; const v = rm[nm]; delete rm[nm]; rm[e.target.value] = v; updateSyncRouteApi(idx, 'result_mapping', rm); }}
                                    placeholder="lead_id" className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs font-mono" />
                                  <span className="text-gray-400 text-xs">←</span>
                                  <input type="text" value={pth}
                                    onChange={(e) => { const rm = { ...(rt.api?.result_mapping || {}) }; rm[nm] = e.target.value; updateSyncRouteApi(idx, 'result_mapping', rm); }}
                                    placeholder="data.lead.id" className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-xs font-mono" />
                                  <button type="button" onClick={() => { const rm = { ...(rt.api?.result_mapping || {}) }; delete rm[nm]; updateSyncRouteApi(idx, 'result_mapping', rm); }} className="text-red-500 text-xs px-1">✕</button>
                                </div>
                              ))}
                              <button type="button" onClick={() => { const rm = { ...(rt.api?.result_mapping || {}) }; rm[`field_${Object.keys(rm).length + 1}`] = ''; updateSyncRouteApi(idx, 'result_mapping', rm); }} className="text-[11px] text-indigo-700">+ field</button>
                              <input type="text"
                                value={(rt.api?.persist || []).join(', ')}
                                onChange={(e) => updateSyncRouteApi(idx, 'persist', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                onBlur={() => onUpdate(stepData)}
                                placeholder="save these names against the customer: lead_id"
                                className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs font-mono" />
                            </div>
                          </div>
                        ))}
                        <button type="button" onClick={addSyncRoute} className="text-xs text-indigo-700 font-medium">+ Add route</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Run Sync + Status */}
            {workflowId && (stepData.job_config?.campaign_node_id
              || (stepData.job_config?.sync_source || 'campaign') === 'conversations') && (
              <div className="border-t border-indigo-200 pt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      const dest = stepData.job_config?.sync_destination || 'source';
                      const destLabel = dest === 'api' ? 'an external API'
                        : dest === 'both' ? 'the source sheet/table AND an external API'
                        : 'the source sheet/table';
                      if (!window.confirm(`Run sync now? This will write conversation summaries to ${destLabel}.`)) return;
                      try {
                        // Target the sync-job node itself so its destination + api_sync
                        // config is honored (mirrors the scheduled run exactly).
                        const resp = await apiCallJson(
                          `/api/chatbot/workflows/${workflowId}/campaign/${selectedNode?.id}/writeback?format=${stepData.job_config?.format || 'json'}&writeback_column=${stepData.job_config?.writeback_column || 'response_summary'}`,
                          { method: 'POST' }
                        );
                        alert(`✅ Sync triggered!\n${resp.message}${resp.api_enabled ? `\nAPI mode: ${resp.api_mode}` : ''}`);
                      } catch (err) {
                        alert(`❌ Sync failed: ${err.message || err}`);
                      }
                    }}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Run Sync Now
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const resp = await apiCallJson(
                          `/api/chatbot/workflows/${workflowId}/campaign/${stepData.job_config.campaign_node_id}/status`
                        );
                        const s = resp.summary || {};
                        const r = resp.latest_run || {};
                        alert(
                          `Campaign Status:\n` +
                          `Total: ${s.total || 0} | Sent: ${s.sent || 0} | Failed: ${s.failed || 0}\n` +
                          `Replied: ${s.replied || 0} | Engaged: ${s.engaged || 0} | Completed: ${s.completed || 0}\n` +
                          `Callbacks pending: ${s.callbacks_pending || 0}\n` +
                          `Written back: ${s.written_back || 0} | Pending writeback: ${s.writeback_pending || 0}\n\n` +
                          `Latest run: ${r.status || 'none'} — ${r.sent_count || 0} sent, ${r.skipped_count || 0} skipped`
                        );
                      } catch (err) {
                        alert(`Error: ${err.message || err}`);
                      }
                    }}
                    style={{ display: (stepData.job_config?.sync_source || 'campaign') === 'conversations' ? 'none' : undefined }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-indigo-300 text-indigo-700 text-sm rounded-lg hover:bg-indigo-50 transition-colors"
                  >
                    <BarChart3 className="h-3.5 w-3.5" />
                    Status
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== Reminder Job Config ==================== */}
        {stepData.message_type === 'reminder_job' && (
          <div className="border rounded-lg p-4 bg-violet-50 space-y-3">
            <h4 className="text-sm font-semibold text-violet-800 flex items-center">
              <Bell className="h-4 w-4 mr-2" />
              Reminder / Follow-up Job
            </h4>
            <p className="text-xs text-violet-700">
              Sends reminder messages to contacts who haven't responded or completed the flow.
              Runs at regular intervals.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Campaign Node</label>
              <select
                value={stepData.job_config?.campaign_node_id || ''}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, campaign_node_id: e.target.value } };
                  setStepData(u); onUpdate(u);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="">— Select campaign node —</option>
                {nodes.filter(n => {
                  const mt = n.data?.stepData?.message_type;
                  return mt === 'sheet_read' || mt === 'table_read';
                }).map(n => (
                  <option key={n.id} value={n.id}>
                    {n.data?.label || n.id} ({n.data?.stepData?.message_type})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reminder Template Name</label>
              <input
                type="text"
                value={stepData.job_config?.template_name || ''}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, template_name: e.target.value } };
                  setStepData(u);
                }}
                onBlur={() => onUpdate(stepData)}
                placeholder="followup_reminder_v1"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Template Language</label>
              <select
                value={stepData.job_config?.template_language || 'en_US'}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, template_language: e.target.value } };
                  setStepData(u); onUpdate(u);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="en">en</option>
                <option value="en_US">en_US</option>
                <option value="hi">hi</option>
                <option value="mr">mr</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Interval</label>
                <select
                  value={stepData.job_config?.interval_minutes || stepData.job_config?.interval_hours * 60 || 1440}
                  onChange={(e) => {
                    const mins = parseInt(e.target.value);
                    const u = { ...stepData, job_config: { ...stepData.job_config, interval_minutes: mins, interval_hours: mins / 60 } };
                    setStepData(u); onUpdate(u);
                  }}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value={5}>5 min</option>
                  <option value={10}>10 min</option>
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={240}>4 hours</option>
                  <option value={360}>6 hours</option>
                  <option value={720}>12 hours</option>
                  <option value={1440}>24 hours</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Reminders</label>
                <input
                  type="number"
                  value={stepData.job_config?.max_reminders || 3}
                  onChange={(e) => {
                    const u = { ...stepData, job_config: { ...stepData.job_config, max_reminders: parseInt(e.target.value) || 3 } };
                    setStepData(u);
                  }}
                  onBlur={() => onUpdate(stepData)}
                  min={1} max={10}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Send reminders to</label>
              <select
                value={stepData.job_config?.target || 'no_reply'}
                onChange={(e) => {
                  const u = { ...stepData, job_config: { ...stepData.job_config, target: e.target.value } };
                  setStepData(u); onUpdate(u);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="no_reply">Contacts who haven't replied</option>
                <option value="not_completed">Contacts who haven't completed the flow</option>
                <option value="all_active">All active contacts (not completed)</option>
              </select>
            </div>
            <div className="p-2 bg-violet-100 rounded text-[10px] text-violet-800">
              <p className="font-semibold">Schedule:</p>
              <p>First reminder: {stepData.job_config?.interval_hours || 24}h after initial template send</p>
              <p>Max {stepData.job_config?.max_reminders || 3} reminders per contact, then stops</p>
              <p>Respects 24h WhatsApp window (sends template, not free-form)</p>
            </div>

            {/* Status */}
            {workflowId && stepData.job_config?.campaign_node_id && (
              <div className="border-t border-violet-200 pt-3">
                <button
                  onClick={async () => {
                    try {
                      const resp = await apiCallJson(
                        `/api/chatbot/workflows/${workflowId}/campaign/${stepData.job_config.campaign_node_id}/status`
                      );
                      const s = resp.summary || {};
                      alert(
                        `Campaign Status:\n` +
                        `Total: ${s.total || 0} | Replied: ${s.replied || 0} | No reply: ${(s.total||0) - (s.replied||0)}\n` +
                        `Engaged: ${s.engaged || 0} | Completed: ${s.completed || 0}\n` +
                        `Pending writeback: ${s.writeback_pending || 0}`
                      );
                    } catch (err) {
                      alert(`Error: ${err.message || err}`);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-violet-300 text-violet-700 text-sm rounded-lg hover:bg-violet-50 transition-colors"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  Campaign Status
                </button>
              </div>
            )}
          </div>
        )}

        {/* ==================== Delay / Scheduled Config ==================== */}
        {stepData.message_type === 'delay' && (
          <div className="border rounded-lg p-4 bg-rose-50 space-y-3">
            <h4 className="text-sm font-semibold text-rose-800 flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              Delay Configuration
            </h4>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Delay (seconds)</label>
              <input
                type="number"
                min="1" max="604800" step="1"
                value={stepData.delay_config?.delay_seconds ?? 3}
                onChange={(e) => {
                  const val = Math.min(604800, Math.max(1, parseInt(e.target.value) || 1));
                  const updated = { ...stepData, delay_config: { ...stepData.delay_config, delay_seconds: val } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
              {/* Human-readable display */}
              {(() => {
                const secs = stepData.delay_config?.delay_seconds ?? 3;
                const parts = [];
                if (secs >= 86400) parts.push(`${Math.floor(secs / 86400)}d`);
                if (secs >= 3600) parts.push(`${Math.floor((secs % 86400) / 3600)}h`);
                if (secs >= 60) parts.push(`${Math.floor((secs % 3600) / 60)}m`);
                if (secs < 60 || secs % 60 !== 0) parts.push(`${secs % 60}s`);
                return (
                  <div className="text-xs text-rose-600 mt-1 font-medium">
                    ⏱ {parts.join(' ')}
                    {secs > 300 && (
                      <span className="ml-2 text-amber-600 font-normal">
                        (scheduled via Redis — survives restarts)
                      </span>
                    )}
                  </div>
                );
              })()}
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <span className="text-xs text-gray-500 mr-1">Quick:</span>
                {[
                  { label: '1s', val: 1 },
                  { label: '3s', val: 3 },
                  { label: '5s', val: 5 },
                  { label: '10s', val: 10 },
                  { label: '30s', val: 30 },
                  { label: '1m', val: 60 },
                  { label: '5m', val: 300 },
                  { label: '30m', val: 1800 },
                  { label: '1h', val: 3600 },
                  { label: '6h', val: 21600 },
                  { label: '24h', val: 86400 },
                ].map(({ label, val }) => (
                  <button
                    key={val}
                    onClick={() => {
                      const updated = { ...stepData, delay_config: { ...stepData.delay_config, delay_seconds: val } };
                      setStepData(updated); onUpdate(updated);
                    }}
                    className={`text-xs px-2 py-0.5 rounded border ${(stepData.delay_config?.delay_seconds ?? 3) === val ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-gray-300 text-gray-600 hover:bg-rose-50'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={stepData.delay_config?.show_typing ?? true}
                onChange={(e) => {
                  const updated = { ...stepData, delay_config: { ...stepData.delay_config, show_typing: e.target.checked } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="rounded border-gray-300"
              />
              <label className="text-xs text-gray-700">Show typing indicator during delay</label>
              {(stepData.delay_config?.delay_seconds ?? 3) > 300 && (
                <span className="text-xs text-gray-400 italic">(ignored for scheduled delays)</span>
              )}
            </div>
            <div className="text-xs text-gray-500 bg-rose-100 p-2 rounded">
              {(stepData.delay_config?.delay_seconds ?? 3) <= 300
                ? 'The bot will pause for the specified duration before sending the message and advancing. Leave the message blank for a silent delay.'
                : 'Delays longer than 5 minutes are scheduled as background jobs (Redis). The message will be delivered at the scheduled time even if the server restarts.'}
            </div>
          </div>
        )}

        {/* ==================== A/B Test Config ==================== */}
        {stepData.message_type === 'ab_test' && (
          <div className="border rounded-lg p-4 bg-indigo-50 space-y-3">
            <h4 className="text-sm font-semibold text-indigo-800 flex items-center">
              <GitBranch className="h-4 w-4 mr-2" />
              A/B Test Configuration
            </h4>
            <p className="text-xs text-gray-500">Split traffic between variants. Percentages must total 100%. Each user is deterministically assigned (same user always sees the same variant).</p>
            <div className="space-y-2">
              {(stepData.ab_config?.variants || []).map((variant, idx) => (
                <div key={idx} className="bg-white border border-indigo-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-indigo-600 w-5">{String.fromCharCode(65 + idx)}</span>
                    <input
                      type="text"
                      value={variant.name || ''}
                      onChange={(e) => {
                        const variants = [...(stepData.ab_config?.variants || [])];
                        variants[idx] = { ...variants[idx], name: e.target.value };
                        const updated = { ...stepData, ab_config: { ...stepData.ab_config, variants } };
                        setStepData(updated);
                      }}
                      onBlur={() => onUpdate({ ...stepData })}
                      placeholder="Variant name"
                      className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min="0" max="100"
                        value={variant.percentage ?? 50}
                        onChange={(e) => {
                          const variants = [...(stepData.ab_config?.variants || [])];
                          variants[idx] = { ...variants[idx], percentage: parseInt(e.target.value) || 0 };
                          const updated = { ...stepData, ab_config: { ...stepData.ab_config, variants } };
                          setStepData(updated); onUpdate(updated);
                        }}
                        className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center"
                      />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                    {(stepData.ab_config?.variants || []).length > 2 && (
                      <button
                        onClick={() => {
                          const variants = (stepData.ab_config?.variants || []).filter((_, i) => i !== idx);
                          const updated = { ...stepData, ab_config: { ...stepData.ab_config, variants } };
                          setStepData(updated); onUpdate(updated);
                        }}
                        className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-red-100 text-red-600 rounded hover:bg-red-200 text-xs"
                      >×</button>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Route to step</label>
                    <select
                      value={variant.next_step || ''}
                      onChange={(e) => {
                        const variants = [...(stepData.ab_config?.variants || [])];
                        variants[idx] = { ...variants[idx], next_step: e.target.value };
                        const updated = { ...stepData, ab_config: { ...stepData.ab_config, variants } };
                        setStepData(updated); onUpdate(updated);
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    >
                      <option value="">-- Select target step --</option>
                      {nodes.filter(n => n.id !== selectedNode?.id).map(n => (
                        <option key={n.id} value={n.id}>{n.data.label} ({n.id})</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            {/* Percentage total indicator */}
            {(() => {
              const total = (stepData.ab_config?.variants || []).reduce((s, v) => s + (v.percentage || 0), 0);
              return (
                <div className={`text-xs font-medium px-2 py-1 rounded ${total === 100 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  Total: {total}% {total !== 100 && `(must be 100%)`}
                </div>
              );
            })()}
            {(stepData.ab_config?.variants || []).length < 5 && (
              <button
                onClick={() => {
                  const variants = [...(stepData.ab_config?.variants || [])];
                  variants.push({ name: `Variant ${String.fromCharCode(65 + variants.length)}`, percentage: 0, next_step: '' });
                  const updated = { ...stepData, ab_config: { ...stepData.ab_config, variants } };
                  setStepData(updated); onUpdate(updated);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
              >+ Add Variant</button>
            )}
          </div>
        )}

        {stepData.variable_mappings && Object.keys(stepData.variable_mappings).length > 0 && (
          <div className="border rounded-lg p-4 bg-emerald-50 space-y-3">
            <h4 className="text-sm font-semibold text-emerald-800 flex items-center">
              <ShieldCheck className="h-4 w-4 mr-2" />
              Input Validation Rules
            </h4>
            <p className="text-xs text-gray-500">
              Add validation for each variable. The bot will re-prompt the user with the error message if validation fails.
            </p>
            {Object.keys(stepData.variable_mappings).map((varName) => (
              <div key={varName} className="border border-emerald-200 rounded-lg p-3 space-y-2 bg-white">
                <div className="text-xs font-semibold text-emerald-700">{varName}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500">Regex Pattern</label>
                    <input
                      type="text"
                      value={stepData.validation_rules?.[varName]?.pattern || ''}
                      onChange={(e) => {
                        const rules = { ...stepData.validation_rules, [varName]: { ...stepData.validation_rules?.[varName], pattern: e.target.value } };
                        setStepData({ ...stepData, validation_rules: rules });
                      }}
                      onBlur={() => onUpdate({ ...stepData })}
                      placeholder="^[a-zA-Z ]+$"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Error Message</label>
                    <input
                      type="text"
                      value={stepData.validation_rules?.[varName]?.error_message || ''}
                      onChange={(e) => {
                        const rules = { ...stepData.validation_rules, [varName]: { ...stepData.validation_rules?.[varName], error_message: e.target.value } };
                        setStepData({ ...stepData, validation_rules: rules });
                      }}
                      onBlur={() => onUpdate({ ...stepData })}
                      placeholder="Please enter a valid name"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500">Min Length</label>
                    <input
                      type="number" min="0"
                      value={stepData.validation_rules?.[varName]?.min_length || ''}
                      onChange={(e) => {
                        const rules = { ...stepData.validation_rules, [varName]: { ...stepData.validation_rules?.[varName], min_length: parseInt(e.target.value) || 0 } };
                        setStepData({ ...stepData, validation_rules: rules }); onUpdate({ ...stepData, validation_rules: rules });
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Max Length</label>
                    <input
                      type="number" min="0"
                      value={stepData.validation_rules?.[varName]?.max_length || ''}
                      onChange={(e) => {
                        const rules = { ...stepData.validation_rules, [varName]: { ...stepData.validation_rules?.[varName], max_length: parseInt(e.target.value) || 0 } };
                        setStepData({ ...stepData, validation_rules: rules }); onUpdate({ ...stepData, validation_rules: rules });
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center space-x-1 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={stepData.validation_rules?.[varName]?.required || false}
                        onChange={(e) => {
                          const rules = { ...stepData.validation_rules, [varName]: { ...stepData.validation_rules?.[varName], required: e.target.checked } };
                          setStepData({ ...stepData, validation_rules: rules }); onUpdate({ ...stepData, validation_rules: rules });
                        }}
                      />
                      <span>Required</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
            <Zap className="h-4 w-4 mr-2 text-yellow-600" />
            Action Configuration
          </h4>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action Type</label>
              <select
                value={stepData.action?.type || 'none'}
                onChange={(e) => updateAction('type', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="none">No Action</option>
                <option value="api">API Call</option>
                <option value="webhook">Webhook (Push Data)</option>
                <option value="database">Database Query</option>
                <option value="logic">Logic/Condition</option>
                <option value="hardcoded">Hardcoded Response</option>
              </select>
            </div>

            {stepData.action?.type === 'api' && (
              <div className="space-y-2 bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center text-sm font-medium text-blue-800 mb-2">
                  <Code className="h-4 w-4 mr-2" />
                  API Call Configuration
                </div>

                <input
                  type="url"
                  value={stepData.action.config?.url || ''}
                  onChange={(e) => updateActionConfig('url', e.target.value)}
                  onBlur={propagateNow}
                  placeholder="API Endpoint URL  ·  supports {{variables}}"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <select
                  value={stepData.action.config?.method || 'GET'}
                  onChange={(e) => updateActionConfig('method', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </select>

                {/* ---- Authentication ---- */}
                <div className="bg-white border border-blue-100 rounded p-2 space-y-2">
                  <div className="flex items-center text-xs font-semibold text-blue-800">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Authentication
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Saved Connection
                    </label>
                    <select
                      value={stepData.action.config?.connection_id || ''}
                      onChange={(e) => updateActionConfig('connection_id', e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">— None (public / inline auth) —</option>
                      {apiConnections.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.auth_type})
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Manage connections under <span className="font-medium">API Connections</span>.
                      A saved connection supplies OAuth2 / login-token auth with automatic
                      token caching and refresh.
                    </p>
                  </div>

                  {!stepData.action.config?.connection_id && (
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">
                        Inline Auth (no saved connection)
                      </label>
                      <select
                        value={stepData.action.config?.auth?.auth_type || 'none'}
                        onChange={(e) => updateActionConfig('auth', {
                          ...(stepData.action.config?.auth || {}),
                          auth_type: e.target.value,
                        })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        <option value="none">No Auth</option>
                        <option value="bearer_static">Static Bearer Token</option>
                        <option value="basic">Basic Auth</option>
                        <option value="api_key">API Key</option>
                      </select>
                      {stepData.action.config?.auth?.auth_type === 'bearer_static' && (
                        <input
                          type="password"
                          value={stepData.action.config?.auth?.static_token || ''}
                          onChange={(e) => updateActionConfig('auth', {
                            ...(stepData.action.config?.auth || {}), static_token: e.target.value,
                          })}
                          onBlur={propagateNow}
                          placeholder="Bearer token"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      )}
                      {stepData.action.config?.auth?.auth_type === 'basic' && (
                        <div className="grid grid-cols-2 gap-1">
                          <input
                            type="text"
                            value={stepData.action.config?.auth?.username || ''}
                            onChange={(e) => updateActionConfig('auth', {
                              ...(stepData.action.config?.auth || {}), username: e.target.value,
                            })}
                            onBlur={propagateNow}
                            placeholder="username"
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          <input
                            type="password"
                            value={stepData.action.config?.auth?.password || ''}
                            onChange={(e) => updateActionConfig('auth', {
                              ...(stepData.action.config?.auth || {}), password: e.target.value,
                            })}
                            onBlur={propagateNow}
                            placeholder="password"
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                        </div>
                      )}
                      {stepData.action.config?.auth?.auth_type === 'api_key' && (
                        <div className="space-y-1">
                          <input
                            type="password"
                            value={stepData.action.config?.auth?.api_key || ''}
                            onChange={(e) => updateActionConfig('auth', {
                              ...(stepData.action.config?.auth || {}), api_key: e.target.value,
                            })}
                            onBlur={propagateNow}
                            placeholder="API key value"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          <input
                            type="text"
                            value={stepData.action.config?.auth?.api_key_name || ''}
                            onChange={(e) => updateActionConfig('auth', {
                              ...(stepData.action.config?.auth || {}), api_key_name: e.target.value,
                            })}
                            onBlur={propagateNow}
                            placeholder="Header name (e.g. X-API-Key)"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <textarea
                  value={stepData.action.config?.headers || ''}
                  onChange={(e) => updateActionConfig('headers', e.target.value)}
                  onBlur={propagateNow}
                  placeholder='Extra Headers (JSON): {"X-Trace": "{{session_id}}"}'
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                  rows={2}
                />
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-600">Request Body (JSON)</label>
                  <VariableInserter
                    groups={flowVarGroups} targetRef={actionBodyRef}
                    value={stepData.action.config?.body || ''}
                    onChange={(v) => { updateActionConfig('body', v); }}
                    note="Campaign contact columns are also available at runtime."
                  />
                </div>
                <textarea
                  ref={actionBodyRef}
                  value={stepData.action.config?.body || ''}
                  onChange={(e) => updateActionConfig('body', e.target.value)}
                  onBlur={propagateNow}
                  placeholder='Request Body (JSON): {"orderId": "{{order_id}}"}'
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                  rows={3}
                />

                <ApiVarsHelp mode="flow" />

                {/* ---- Response capture ---- */}
                <div className="bg-white border border-blue-100 rounded p-2 space-y-2">
                  <div className="flex items-center text-xs font-semibold text-blue-800">
                    <DatabaseBackup className="h-3 w-3 mr-1" />
                    Response Mapping
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Response Variable Prefix
                    </label>
                    <input
                      type="text"
                      value={stepData.action.config?.response_var || ''}
                      onChange={(e) => updateActionConfig('response_var', e.target.value)}
                      onBlur={propagateNow}
                      placeholder="api  →  sets {{api_status}}, {{api_ok}}, {{api_response}}"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <label className="block text-xs font-medium text-gray-600">
                    Map response fields → variables
                  </label>
                  {Object.entries(stepData.action.config?.result_mapping || {}).map(([varName, path], idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <input
                        type="text"
                        value={varName}
                        onChange={(e) => {
                          const rm = { ...(stepData.action.config?.result_mapping || {}) };
                          const val = rm[varName];
                          delete rm[varName];
                          rm[e.target.value] = val;
                          updateActionConfig('result_mapping', rm);
                        }}
                        onBlur={propagateNow}
                        placeholder="variable"
                        className="w-1/3 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                      <span className="text-gray-400 text-xs">←</span>
                      <input
                        type="text"
                        value={path}
                        onChange={(e) => {
                          const rm = { ...(stepData.action.config?.result_mapping || {}) };
                          rm[varName] = e.target.value;
                          updateActionConfig('result_mapping', rm);
                        }}
                        onBlur={propagateNow}
                        placeholder="data.items[0].id"
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const rm = { ...(stepData.action.config?.result_mapping || {}) };
                          delete rm[varName];
                          updateActionConfig('result_mapping', rm);
                          propagateNow();
                        }}
                        className="text-gray-400 hover:text-red-600 p-1"
                        title="Remove"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const rm = { ...(stepData.action.config?.result_mapping || {}) };
                      let base = 'field', name = base, i = 1;
                      while (Object.prototype.hasOwnProperty.call(rm, name)) { name = `${base}${i++}`; }
                      rm[name] = '';
                      updateActionConfig('result_mapping', rm);
                      propagateNow();
                    }}
                    className="flex items-center gap-1 text-xs text-blue-600 font-medium"
                  >
                    <Plus className="h-3 w-3" /> Add field mapping
                  </button>
                  <p className="text-[11px] text-gray-500">
                    Downstream nodes can branch or reply using the captured variables,
                    e.g. <span className="font-mono">Status: {'{{order_status}}'}</span>.
                  </p>
                </div>
              </div>
            )}

            {stepData.action?.type === 'webhook' && (
              <div className="space-y-2 bg-orange-50 p-3 rounded-lg">
                <div className="flex items-center text-sm font-medium text-orange-800 mb-2">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Webhook Configuration
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Webhook URL</label>
                  <input
                    type="url"
                    value={stepData.action.config?.url || ''}
                    onChange={(e) => updateActionConfig('url', e.target.value)}
                    onBlur={propagateNow}
                    placeholder="https://your-crm.com/api/leads"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">HTTP Method</label>
                  <select
                    value={stepData.action.config?.method || 'POST'}
                    onChange={(e) => updateActionConfig('method', e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="PATCH">PATCH</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Custom Headers (JSON, optional)</label>
                  <textarea
                    value={stepData.action.config?.headers || ''}
                    onChange={(e) => updateActionConfig('headers', e.target.value)}
                    onBlur={propagateNow}
                    placeholder='{"Authorization": "Bearer YOUR_KEY", "X-Source": "chatbot"}'
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    rows={2}
                  />
                </div>
                <div className="text-xs text-gray-500 bg-orange-100 p-2 rounded space-y-1">
                  <p>
                    <strong>Payload:</strong> All captured session variables are automatically sent as JSON.
                    The payload includes <code className="bg-orange-200 px-1 rounded">_user_id</code>,{' '}
                    <code className="bg-orange-200 px-1 rounded">_channel</code>,{' '}
                    <code className="bg-orange-200 px-1 rounded">_timestamp</code>, and every variable
                    captured by previous nodes (e.g. <code className="bg-orange-200 px-1 rounded">contact_name</code>,{' '}
                    <code className="bg-orange-200 px-1 rounded">contact_phone</code>).
                  </p>
                  <p>The webhook fires asynchronously and does not block the conversation flow.</p>
                </div>
              </div>
            )}

            {stepData.action?.type === 'database' && (
              <div className="space-y-2 bg-green-50 p-3 rounded-lg">
                <div className="flex items-center text-sm font-medium text-green-800 mb-2">
                  <Database className="h-4 w-4 mr-2" />
                  Database Configuration
                </div>
                <input
                  type="text"
                  value={stepData.action.config?.table || ''}
                  onChange={(e) => updateActionConfig('table', e.target.value)}
                onBlur={propagateNow}
                  placeholder="Table Name"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <select
                  value={stepData.action.config?.operation || 'SELECT'}
                  onChange={(e) => updateActionConfig('operation', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="SELECT">SELECT</option>
                  <option value="INSERT">INSERT</option>
                  <option value="UPDATE">UPDATE</option>
                  <option value="DELETE">DELETE</option>
                </select>
                <textarea
                  value={stepData.action.config?.query || ''}
                  onChange={(e) => updateActionConfig('query', e.target.value)}
                onBlur={propagateNow}
                  placeholder="SQL Query or conditions"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  rows={3}
                />
              </div>
            )}

            {stepData.action?.type === 'logic' && (
              <div className="space-y-2 bg-purple-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center text-sm font-medium text-purple-800">
                    <Code className="h-4 w-4 mr-2" />
                    Logic Configuration
                  </div>
                  <VariableInserter
                    wrap={false}
                    groups={flowVarGroups} targetRef={logicCondRef}
                    value={stepData.action.config?.condition || ''}
                    onChange={(v) => updateActionConfig('condition', v)}
                    note="Conditions use bare variable names (no braces), e.g. status == 'active' and age > 18."
                  />
                </div>
                <textarea
                  ref={logicCondRef}
                  value={stepData.action.config?.condition || ''}
                  onChange={(e) => updateActionConfig('condition', e.target.value)}
                onBlur={propagateNow}
                  placeholder="Condition: e.g., user_age > 18"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  rows={2}
                />
                <select
                  value={stepData.action.config?.true_step || ''}
                  onChange={(e) => updateActionConfig('true_step', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="">-- Select step if true --</option>
                  {nodes
                    .filter(n => n.id !== selectedNode?.id)
                    .map(node => (
                      <option key={node.id} value={node.id}>
                        {node.data.label}
                      </option>
                    ))}
                </select>
                <select
                  value={stepData.action.config?.false_step || ''}
                  onChange={(e) => updateActionConfig('false_step', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="">-- Select step if false --</option>
                  {nodes
                    .filter(n => n.id !== selectedNode?.id)
                    .map(node => (
                      <option key={node.id} value={node.id}>
                        {node.data.label}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {stepData.action?.type === 'hardcoded' && (
              <div className="space-y-2 bg-yellow-50 p-3 rounded-lg">
                <div className="flex items-center text-sm font-medium text-yellow-800 mb-2">
                  <FileText className="h-4 w-4 mr-2" />
                  Hardcoded Response
                </div>
                <textarea
                  value={stepData.action.config?.response || ''}
                  onChange={(e) => updateActionConfig('response', e.target.value)}
                onBlur={propagateNow}
                  placeholder="Enter the hardcoded response"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  rows={4}
                />
              </div>
            )}
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">Variable Mappings</label>
            <button
              onClick={addVariableMapping}
              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
            >
              + Add Mapping
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-2">Capture user input and store as variables</p>
          <div className="space-y-2">
            {variableMappingsList.map(({ id, name, pattern }) => (
              <div key={id} className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => updateVariableMappingName(id, e.target.value)}
                  onBlur={() => persistVariableMappings()}
                  placeholder="Variable"
                  className="min-w-0 flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <input
                  type="text"
                  value={pattern}
                  onChange={(e) => updateVariableMappingPattern(id, e.target.value)}
                  onBlur={() => persistVariableMappings()}
                  placeholder="Regex"
                  className="min-w-0 flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <button
                  onClick={() => { removeVariableMapping(id); setTimeout(persistVariableMappings, 0); }}
                  className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Next Step ID</label>
          <select
            value={stepData.next_step || ''}
            onChange={(e) => {
              const updated = { ...stepData, next_step: e.target.value };
              setStepData(updated);
              onUpdate(updated);
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">-- None--</option>
            {nodes
              .filter(n => n.id !== selectedNode?.id)
              .map(node => (
                <option key={node.id} value={node.id}>
                  {node.data.label} ({node.id})
                </option>
              ))}
          </select>
        </div>
      </div>

      <div className="p-4 border-t bg-gray-50">
        <button
          onClick={onDelete}
          className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 flex items-center justify-center space-x-2"
        >
          <Trash2 className="h-4 w-4" />
          <span>Delete Step</span>
        </button>
      </div>
    </div>
  );
};

const ChatPreview = ({ workflowId, nodes, edges }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [currentStep, setCurrentStep] = useState(null);
  const [variables, setVariables] = useState({});
  const [waitingForInput, setWaitingForInput] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (nodes && nodes.length > 0) {
      restartConversation();
    }
  }, [nodes]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  const restartConversation = () => {
    setMessages([]);
    setVariables({});
    setInputMessage('');
    setWaitingForInput(false);
    
    const startNode = nodes.find(node => 
      !edges.some(edge => edge.target === node.id)
    );
    
    if (startNode) {
      processStep(startNode);
    }
  };

  const processStep = async (node) => {
    if (!node) return;

    const stepData = node.data.stepData;
    setCurrentStep(node);

    // Process message with variable substitution
    let processedMessage = stepData.message || '';
    Object.keys(variables).forEach(varName => {
      const regex = new RegExp(`{{\\s*${varName}\\s*}}`, 'g');
      processedMessage = processedMessage.replace(regex, variables[varName]);
    });

    // Execute action if present
    if (stepData.action && stepData.action.type !== 'none') {
      const actionResult = await executeAction(stepData.action);
      if (actionResult) {
        // Merge action results into variables
        Object.keys(actionResult).forEach(key => {
          // Replace variables in message with action results
          const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
          processedMessage = processedMessage.replace(regex, actionResult[key]);
        });
        setVariables(prev => ({ ...prev, ...actionResult }));
      }
    }

    const botMessage = {
      type: 'bot',
      content: processedMessage,
      message_type: stepData.message_type,
      buttons: stepData.buttons || [],
      options: stepData.options || [],
      carousel_items: stepData.carousel_items || [],
      media_url: stepData.media_url,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, botMessage]);

    // Determine if we need to wait for user input
    const needsInput = (
      stepData.message_type === 'buttons' ||
      stepData.message_type === 'options' ||
      stepData.message_type === 'carousel' ||
      (stepData.message_type === 'text'/* && 
       stepData.variable_mappings && 
       Object.keys(stepData.variable_mappings).length > 0*/)
    );

    if (needsInput) {
      setWaitingForInput(true);
    } else {
      // Auto-advance to next step
      setWaitingForInput(false);
      if (stepData.next_step) {
        const nextNode = nodes.find(n => n.id === stepData.next_step || n.data.label === stepData.next_step);
        if (nextNode) {
          setTimeout(() => processStep(nextNode), 800);
        }
      }
    }
  };

  const executeAction = async (action) => {
    const result = {};
    
    try {
      switch (action.type) {
        case 'hardcoded':
          result.response = action.config?.response || '';
          break;
          
        case 'api':
          if (action.config?.url) {
            const headers = action.config.headers ? JSON.parse(action.config.headers) : {};
            const response = await fetch(action.config.url, {
              method: action.config.method || 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...headers
              },
              body: action.config.method !== 'GET' && action.config.body ? action.config.body : undefined
            });
            result.api_response = await response.json();
          }
          break;
          
        case 'logic':
          if (action.config?.condition) {
            try {
              const conditionResult = evaluateCondition(action.config.condition, variables);
              result.condition_result = conditionResult;
              result.next_step_override = conditionResult ? action.config.true_step : action.config.false_step;
            } catch (e) {
              console.error('Condition evaluation error:', e);
            }
          }
          break;
          
        case 'database':
          result.db_mock = `Database ${action.config?.operation || 'QUERY'} on ${action.config?.table || 'table'}`;
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error('Action execution error:', error);
      result.error = error.message;
    }
    
    return result;
  };

  const evaluateCondition = (condition, vars) => {
    if (!condition || !condition.trim()) return false;
    
    // Replace variable names with their values
    let evalString = condition;
    
    Object.keys(vars).forEach(key => {
      const value = vars[key];
      const regex = new RegExp(`\\b${key}\\b`, 'g');
      
      // If value is a number, use it as-is, otherwise wrap in quotes
      if (typeof value === 'number' || !isNaN(value)) {
        evalString = evalString.replace(regex, value);
      } else {
        evalString = evalString.replace(regex, `"${value}"`);
      }
    });
    
    try {
      // Use Function constructor for safer evaluation
      return new Function('return ' + evalString)();
    } catch (e) {
      console.error('Condition evaluation error:', e, 'Condition:', evalString);
      return false;
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !waitingForInput) return;

    const userMessage = {
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    if (currentStep) {
      const stepData = currentStep.data.stepData;
      
      // Capture variables from user input
      const newVars = { ...variables };
      if (stepData.variable_mappings && Object.keys(stepData.variable_mappings).length > 0) {
        Object.keys(stepData.variable_mappings).forEach(varName => {
          const pattern = stepData.variable_mappings[varName];
          if (pattern && pattern.trim()) {
            try {
              const regex = new RegExp(pattern);
              if (regex.test(inputMessage.trim())) {
                newVars[varName] = inputMessage.trim();
              }
            } catch (e) {
              // If regex is invalid, just store the value
              newVars[varName] = inputMessage.trim();
            }
          } else {
            // No pattern specified, store the raw input
            newVars[varName] = inputMessage.trim();
          }
        });
        setVariables(newVars);
      }

      // Process logic action to determine next step
      let nextStepId = stepData.next_step;
      
      if (stepData.action && stepData.action.type === 'logic' && stepData.action.config?.condition) {
        try {
          const conditionResult = evaluateCondition(stepData.action.config.condition, newVars);
          nextStepId = conditionResult ? stepData.action.config.true_step : stepData.action.config.false_step;
        } catch (e) {
          console.error('Condition evaluation error:', e);
        }
      }

      // Move to next step
      if (nextStepId) {
        const nextNode = nodes.find(n => n.id === nextStepId || n.data.label === nextStepId);
        if (nextNode) {
          setWaitingForInput(false);
          setInputMessage('');
          setTimeout(() => processStep(nextNode), 500);
          return;
        }
      }
      
      // No next step, conversation ends
      setWaitingForInput(false);
    }

    setInputMessage('');
  };

  const handleButtonClick = (button) => {
    const userMessage = {
      type: 'user',
      content: button.text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Store button value in variables
    const newVars = { ...variables, last_button_value: button.value, last_button_text: button.text };
    setVariables(newVars);

    setWaitingForInput(false);

    // Determine next step (button.next_step takes priority over step's next_step)
    let nextStepId = button.next_step || currentStep?.data.stepData.next_step;

    // Check for logic action that might override next step
    if (currentStep?.data.stepData.action?.type === 'logic' && currentStep.data.stepData.action.config?.condition) {
      try {
        const conditionResult = evaluateCondition(currentStep.data.stepData.action.config.condition, newVars);
        nextStepId = conditionResult ? currentStep.data.stepData.action.config.true_step : currentStep.data.stepData.action.config.false_step;
      } catch (e) {
        console.error('Condition evaluation error:', e);
      }
    }

    if (nextStepId) {
      const nextNode = nodes.find(n => n.id === nextStepId || n.data.label === nextStepId);
      if (nextNode) {
        setTimeout(() => processStep(nextNode), 500);
      }
    }
  };

  const handleOptionClick = (option) => {
    const userMessage = {
      type: 'user',
      content: option.text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Store option value in variables
    const newVars = { ...variables, last_option_value: option.value, last_option_text: option.text };
    setVariables(newVars);

    setWaitingForInput(false);

    // Determine next step
    let nextStepId = currentStep?.data.stepData.next_step;

    // Check for logic action
    if (currentStep?.data.stepData.action?.type === 'logic' && currentStep.data.stepData.action.config?.condition) {
      try {
        const conditionResult = evaluateCondition(currentStep.data.stepData.action.config.condition, newVars);
        nextStepId = conditionResult ? currentStep.data.stepData.action.config.true_step : currentStep.data.stepData.action.config.false_step;
      } catch (e) {
        console.error('Condition evaluation error:', e);
      }
    }

    if (nextStepId) {
      const nextNode = nodes.find(n => n.id === nextStepId || n.data.label === nextStepId);
      if (nextNode) {
        setTimeout(() => processStep(nextNode), 500);
      }
    }
  };

  return (
    <div className="flex flex-col h-full max-h-full bg-white">
      <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex-shrink-0">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-semibold">Live Preview</h3>
            <p className="text-xs text-blue-100">Test your chatbot flow</p>
          </div>
          <button
            onClick={restartConversation}
            className="text-sm bg-blue-800 hover:bg-blue-900 px-3 py-1 rounded flex items-center space-x-1"
          >
            <RotateCcw className="h-3 w-3" />
            <span>Restart</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 scroll-smooth" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        {messages.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            <div className="mb-2">
              <CheckCircle className="h-12 w-12 mx-auto text-gray-400" />
            </div>
            <p className="text-sm">Click "Restart" to begin testing</p>
          </div>
        )}
        
        {messages.map((message, index) => (
          <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-xs lg:max-w-md rounded-2xl px-4 py-3 ${
              message.type === 'user' 
                ? 'bg-blue-600 text-white rounded-br-sm' 
                : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-200'
            }`}>
              {message.media_url && (
                <div className="mb-2 rounded-lg overflow-hidden">
                  {message.message_type === 'image' && (
                    <img 
                      src={message.media_url} 
                      alt="Chat media" 
                      className="max-w-full h-auto"
                    />
                  )}
                  {message.message_type === 'video' && (
                    <video 
                      src={message.media_url} 
                      controls 
                      className="max-w-full h-auto"
                    />
                  )}
                  {message.message_type === 'audio' && (
                    <audio 
                      src={message.media_url} 
                      controls 
                      className="w-full"
                    />
                  )}
                </div>
              )}
              
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              
              {message.buttons && message.buttons.length > 0 && (
                <div className="mt-3 space-y-2">
                  {message.buttons.map((button, btnIndex) => (
                    <button
                      key={btnIndex}
                      onClick={() => handleButtonClick(button)}
                      className="block w-full text-left bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border-2 border-blue-300 rounded-lg px-3 py-2 text-sm font-medium hover:from-blue-100 hover:to-blue-200 transition-all"
                    >
                      {button.text}
                    </button>
                  ))}
                </div>
              )}
              
              {message.options && message.options.length > 0 && (
                <div className="mt-3 space-y-2">
                  {message.options.map((option, optIndex) => (
                    <button
                      key={optIndex}
                      onClick={() => handleOptionClick(option)}
                      className="block w-full text-left bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50 hover:border-gray-400 transition-all"
                    >
                      {option.text}
                    </button>
                  ))}
                </div>
              )}

              {message.carousel_items && message.carousel_items.length > 0 && (
                <div className="mt-3 flex overflow-x-auto space-x-3 pb-2">
                  {message.carousel_items.map((item, carIndex) => (
                    <div 
                      key={carIndex}
                      className="flex-shrink-0 w-64 bg-white border border-gray-300 rounded-lg overflow-hidden shadow-sm"
                    >
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.title}
                          className="w-full h-40 object-cover"
                        />
                      )}
                      <div className="p-3">
                        <h4 className="font-semibold text-sm text-gray-800">{item.title}</h4>
                        {item.subtitle && (
                          <p className="text-xs text-gray-600 mt-1">{item.subtitle}</p>
                        )}
                        {item.buttons && item.buttons.length > 0 && (
                          <div className="mt-3 space-y-1">
                            {item.buttons.map((btn, btnIdx) => (
                              <button
                                key={btnIdx}
                                onClick={() => handleButtonClick(btn)}
                                className="block w-full text-center bg-blue-50 text-blue-700 border border-blue-300 rounded px-2 py-1 text-xs font-medium hover:bg-blue-100 transition-all"
                              >
                                {btn.text}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className={`text-xs mt-2 ${message.type === 'user' ? 'text-blue-200' : 'text-gray-400'}`}>
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}
        
        <div ref={messagesEndRef} />
      </div>

      {Object.keys(variables).length > 0 && (
        <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200 flex-shrink-0">
          <p className="text-xs font-semibold text-yellow-800 mb-1">Variables:</p>
          <div className="text-xs text-yellow-700 space-x-2">
            {Object.entries(variables).map(([key, value]) => (
              <span key={key} className="inline-block bg-yellow-100 px-2 py-1 rounded">
                <strong>{key}:</strong> {value}
              </span>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={
              waitingForInput 
                ? currentStep?.data.stepData.variable_mappings && Object.keys(currentStep.data.stepData.variable_mappings).length > 0
                  ? `Enter your ${Object.keys(currentStep.data.stepData.variable_mappings)[0]}...`
                  : "Type your message..."
                : "Waiting for bot..."
            }
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={!waitingForInput}
          />
          <button
            type="submit"
            disabled={!waitingForInput || !inputMessage.trim()}
            className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </form>
    </div>
  );
};

// ==================== Translation Editor ====================
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pt', name: 'Português' },
  { code: 'it', name: 'Italiano' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'ar', name: 'العربية' },
  { code: 'zh', name: '中文' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'ru', name: 'Русский' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'nl', name: 'Nederlands' },
];

const TranslationEditor = ({ workflowId, nodes, translations, setTranslations, defaultLanguage, setDefaultLanguage }) => {
  const [activeLangs, setActiveLangs] = useState([defaultLanguage || 'en']);
  const [savingTranslations, setSavingTranslations] = useState(false);
  const [transSearchTerm, setTransSearchTerm] = useState('');
  const [addLangOpen, setAddLangOpen] = useState(false);

  // Load translations from backend
  useEffect(() => {
    if (!workflowId) return;
    const loadTrans = async () => {
      try {
        const token = getCurrentToken();
        const resp = await apiCallJson(`/api/chatbot/workflows/${workflowId}/translations`, {
          method: 'GET',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (resp && resp.translations) {
          setTranslations(resp.translations);
          setDefaultLanguage(resp.default_language || 'en');
          // Extract active languages
          const langs = new Set([resp.default_language || 'en']);
          Object.values(resp.translations).forEach(entry => {
            Object.keys(entry).forEach(l => langs.add(l));
          });
          setActiveLangs(Array.from(langs));
        }
      } catch (err) {
        console.error('Failed to load translations:', err);
      }
    };
    loadTrans();
  }, [workflowId]);

  // Save translations to backend
  const saveTranslations = async () => {
    setSavingTranslations(true);
    try {
      const token = getCurrentToken();
      await apiCallJson(`/api/chatbot/workflows/${workflowId}/translations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ translations, default_language: defaultLanguage })
      });
    } catch (err) {
      console.error('Failed to save translations:', err);
    }
    setSavingTranslations(false);
  };

  // Get all translatable message keys from nodes
  const messageKeys = nodes
    .filter(n => n.data?.stepData?.message)
    .map(n => ({
      key: n.data.stepData.name || n.data.label || n.id,
      message: n.data.stepData.message,
      type: n.data.stepData.message_type
    }));

  const filteredKeys = messageKeys.filter(k =>
    !transSearchTerm || k.key.toLowerCase().includes(transSearchTerm.toLowerCase()) ||
    k.message.toLowerCase().includes(transSearchTerm.toLowerCase())
  );

  const updateTranslation = (key, lang, value) => {
    setTranslations(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [lang]: value }
    }));
  };

  const addLanguage = (langCode) => {
    if (!activeLangs.includes(langCode)) {
      setActiveLangs(prev => [...prev, langCode]);
    }
    setAddLangOpen(false);
  };

  const removeLanguage = (langCode) => {
    if (langCode === defaultLanguage) return; // Can't remove default
    setActiveLangs(prev => prev.filter(l => l !== langCode));
  };

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm text-indigo-800 flex items-center space-x-2">
            <span>Web</span>
            <span>Translations</span>
          </h3>
          <button
            onClick={saveTranslations}
            disabled={savingTranslations}
            className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {savingTranslations ? 'Saving...' : 'Save Translations'}
          </button>
        </div>

        {/* Default language selector */}
        <div className="flex items-center space-x-2 text-xs">
          <label className="text-gray-600">Default:</label>
          <select
            value={defaultLanguage}
            onChange={(e) => setDefaultLanguage(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-xs"
          >
            {LANGUAGES.map(l => (
              <option key={l.code} value={l.code}>{l.name}</option>
            ))}
          </select>
        </div>

        {/* Active language tags */}
        <div className="flex flex-wrap gap-1 mt-2">
          {activeLangs.map(lang => (
            <span key={lang} className="inline-flex items-center text-xs bg-white border border-indigo-200 rounded-full px-2 py-0.5">
              {LANGUAGES.find(l => l.code === lang)?.name || lang}
              {lang !== defaultLanguage && (
                <button onClick={() => removeLanguage(lang)} className="ml-1 text-gray-400 hover:text-red-500">×</button>
              )}
            </span>
          ))}
          <div className="relative">
            <button
              onClick={() => setAddLangOpen(!addLangOpen)}
              className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-0.5 border border-dashed border-indigo-300 rounded-full"
            >
              + Add
            </button>
            {addLangOpen && (
              <div className="absolute z-20 mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg p-1 max-h-48 overflow-y-auto w-40">
                {LANGUAGES.filter(l => !activeLangs.includes(l.code)).map(l => (
                  <button
                    key={l.code}
                    onClick={() => addLanguage(l.code)}
                    className="w-full text-left text-xs px-2 py-1.5 hover:bg-indigo-50 rounded"
                  >
                    {l.name} ({l.code})
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mt-2 relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
          <input
            type="text"
            value={transSearchTerm}
            onChange={(e) => setTransSearchTerm(e.target.value)}
            placeholder="Search nodes..."
            className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Translation entries */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {filteredKeys.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            {nodes.length === 0 ? 'No workflow nodes yet' : 'No matching nodes'}
          </div>
        ) : (
          filteredKeys.map(({ key, message, type }) => (
            <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div className="text-xs font-semibold text-gray-700 truncate flex-1">{key}</div>
                <span className="text-xs bg-gray-200 text-gray-500 rounded px-1.5 py-0.5 ml-2">{type}</span>
              </div>
              {/* Default language original text */}
              <div className="px-3 py-2 bg-blue-50 border-b border-blue-100">
                <div className="text-xs text-blue-600 font-medium mb-1">
                  {LANGUAGES.find(l => l.code === defaultLanguage)?.name || defaultLanguage} (original)
                </div>
                <div className="text-xs text-gray-700 whitespace-pre-wrap">{message}</div>
              </div>
              {/* Other languages */}
              {activeLangs.filter(l => l !== defaultLanguage).map(lang => (
                <div key={lang} className="px-3 py-2 border-b border-gray-100 last:border-b-0">
                  <label className="text-xs text-gray-500 mb-1 block">
                    {LANGUAGES.find(l => l.code === lang)?.name || lang}
                  </label>
                  <textarea
                    value={translations[key]?.[lang] || ''}
                    onChange={(e) => updateTranslation(key, lang, e.target.value)}
                    rows={2}
                    placeholder={`Translate: "${message.slice(0, 60)}${message.length > 60 ? '...' : ''}"`}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-indigo-500 resize-none"
                  />
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex-shrink-0">
        <div className="text-xs text-gray-500">
          {messageKeys.length} translatable messages • {activeLangs.length} languages
        </div>
      </div>
    </>
  );
};

// ==================== Template Picker Modal ====================
const TEMPLATE_ICONS = {
  headset: 'CS', target: 'LC', help: 'FAQ', calendar: 'AB', star: 'FB'
};
const TEMPLATE_CATEGORY_COLORS = {
  support: 'bg-blue-100 text-blue-700',
  marketing: 'bg-green-100 text-green-700',
  business: 'bg-amber-100 text-amber-700'
};

const TemplatePicker = ({ onSelectBlank, onSelectTemplate, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiCallJson('/api/chatbot/workflow-templates');
        setTemplates(data.templates || []);
      } catch (e) {
        console.error('Failed to load templates:', e);
      }
      setLoading(false);
    })();
  }, []);

  const handleSelect = async (templateId) => {
    setLoadingTemplate(true);
    try {
      const data = await apiCallJson(`/api/chatbot/workflow-templates/${templateId}`);
      const tpl = data.template;
      onSelectTemplate(tpl.nodes, tpl.edges, tpl.name);
    } catch (e) {
      console.error('Failed to load template:', e);
    }
    setLoadingTemplate(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Create New Workflow</h2>
              <p className="text-sm text-gray-500 mt-0.5">Start from scratch or use a template</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5 text-gray-400" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Blank option */}
          <button
            onClick={onSelectBlank}
            className="w-full mb-4 p-5 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
                <Plus className="h-6 w-6 text-gray-400 group-hover:text-blue-600" />
              </div>
              <div>
                <div className="font-semibold text-gray-900 group-hover:text-blue-700">Blank Workflow</div>
                <div className="text-sm text-gray-500">Start with an empty canvas</div>
              </div>
            </div>
          </button>

          {/* Templates */}
          {loading ? (
            <div className="text-center py-8 text-gray-400 text-sm">Loading templates...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {templates.map(tpl => (
                <button
                  key={tpl.id}
                  onClick={() => handleSelect(tpl.id)}
                  disabled={loadingTemplate}
                  className={`p-4 border rounded-xl text-left hover:shadow-md transition-all ${
                    selectedId === tpl.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                  } disabled:opacity-50`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-100 to-blue-50 flex items-center justify-center text-xl flex-shrink-0">
                      {TEMPLATE_ICONS[tpl.icon] || tpl.icon?.charAt(0)?.toUpperCase() || 'W'}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 text-sm">{tpl.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{tpl.description}</div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${TEMPLATE_CATEGORY_COLORS[tpl.category] || 'bg-gray-100 text-gray-600'}`}>
                          {tpl.category}
                        </span>
                        <span className="text-[10px] text-gray-400">{tpl.node_count} nodes</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Version History Panel ====================
const VersionHistoryPanel = ({ workflowId, currentNodes, currentEdges, onRollback }) => {
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [rolling, setRolling] = useState(false);

  // Load version list
  const loadVersions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCallJson(`/api/chatbot/workflows/${workflowId}/versions`);
      setVersions(data.versions || []);
      setCurrentVersion(data.current_version || 0);
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
    setLoading(false);
  }, [workflowId]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  // Load diff for a version
  const loadDiff = async (versionId) => {
    setDiffLoading(true);
    try {
      const data = await apiCallJson(`/api/chatbot/workflows/${workflowId}/versions/${versionId}/diff`);
      setDiff(data.diff);
    } catch (err) {
      console.error('Failed to load diff:', err);
      setDiff(null);
    }
    setDiffLoading(false);
  };

  // Rollback
  const handleRollback = async (versionId, version) => {
    if (!window.confirm(`Roll back to version ${version}? The current state will be saved as a new version first.`)) return;
    setRolling(true);
    try {
      // First get the full version data
      const vData = await apiCallJson(`/api/chatbot/workflows/${workflowId}/versions/${versionId}`);
      // Execute rollback on backend
      await apiCallJson(`/api/chatbot/workflows/${workflowId}/versions/${versionId}/rollback`, { method: 'POST' });
      // Apply to UI
      onRollback({
        nodes: vData.version.nodes,
        edges: vData.version.edges,
        name: vData.version.name,
        description: vData.version.description,
        version: vData.version.version
      });
      loadVersions();
      setSelectedVersion(null);
      setDiff(null);
    } catch (err) {
      console.error('Rollback failed:', err);
    }
    setRolling(false);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return 'just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs/60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs/3600000)}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 bg-gray-800 text-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-2">
          <History className="h-4 w-4" />
          <h3 className="font-semibold text-sm">Version History</h3>
          <span className="text-xs bg-gray-600 px-1.5 py-0.5 rounded">v{currentVersion}</span>
        </div>
        <button onClick={loadVersions} className="p-1 hover:bg-gray-700 rounded">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Version list */}
      <div className="flex-1 overflow-y-auto">
        {loading && versions.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">Loading versions...</div>
        ) : versions.length === 0 ? (
          <div className="text-center py-8 px-4">
            <GitBranch className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No version history yet</p>
            <p className="text-xs text-gray-400 mt-1">Versions are created automatically each time you save.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {versions.map((v, idx) => {
              const isSelected = selectedVersion === v.id;
              return (
                <div key={v.id}>
                  <button
                    onClick={() => {
                      if (isSelected) { setSelectedVersion(null); setDiff(null); }
                      else { setSelectedVersion(v.id); loadDiff(v.id); }
                    }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${idx === 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-sm font-medium text-gray-800">v{v.version}</span>
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(v.created_at)}</span>
                    </div>
                    <div className="ml-4 mt-1">
                      <p className="text-xs text-gray-600 truncate">{v.change_summary || 'No changes recorded'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-400">{v.node_count} nodes, {v.edge_count} edges</span>
                        <span className="text-xs text-gray-400">by {v.created_by}</span>
                      </div>
                    </div>
                  </button>

                  {/* Expanded diff + actions */}
                  {isSelected && (
                    <div className="px-4 pb-3 bg-blue-50 border-t border-blue-100">
                      {/* Diff */}
                      {diffLoading ? (
                        <div className="text-xs text-gray-500 py-2">Loading diff...</div>
                      ) : diff ? (
                        <div className="mt-2 space-y-1.5">
                          <div className="text-xs font-medium text-gray-700">Changes since v{v.version}:</div>
                          <div className="text-xs space-y-1">
                            {diff.nodes.added.length > 0 && (
                              <div className="text-green-700">
                                + {diff.nodes.added.length} node{diff.nodes.added.length > 1 ? 's' : ''} added: {diff.nodes.added.map(n => n.label).join(', ')}
                              </div>
                            )}
                            {diff.nodes.removed.length > 0 && (
                              <div className="text-red-700">
                                − {diff.nodes.removed.length} node{diff.nodes.removed.length > 1 ? 's' : ''} removed: {diff.nodes.removed.map(n => n.label).join(', ')}
                              </div>
                            )}
                            {diff.nodes.modified.length > 0 && (
                              <div className="text-amber-700">
                                ~ {diff.nodes.modified.length} node{diff.nodes.modified.length > 1 ? 's' : ''} modified: {diff.nodes.modified.map(n => n.label).join(', ')}
                              </div>
                            )}
                            {diff.edges.added > 0 && <div className="text-green-700">+ {diff.edges.added} edge{diff.edges.added > 1 ? 's' : ''}</div>}
                            {diff.edges.removed > 0 && <div className="text-red-700">− {diff.edges.removed} edge{diff.edges.removed > 1 ? 's' : ''}</div>}
                            {diff.nodes.added.length === 0 && diff.nodes.removed.length === 0 && diff.nodes.modified.length === 0 && diff.edges.added === 0 && diff.edges.removed === 0 && (
                              <div className="text-gray-500 italic">No changes (identical to current)</div>
                            )}
                          </div>
                        </div>
                      ) : null}

                      {/* Rollback button */}
                      <button
                        onClick={() => handleRollback(v.id, v.version)}
                        disabled={rolling}
                        className="mt-3 w-full flex items-center justify-center space-x-1.5 px-3 py-2 bg-gray-800 text-white rounded-lg text-xs hover:bg-gray-900 disabled:opacity-50 transition-colors"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        <span>{rolling ? 'Rolling back...' : `Restore v${v.version}`}</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex-shrink-0">
        <div className="text-xs text-gray-500">
          {versions.length} version{versions.length !== 1 ? 's' : ''} saved
        </div>
      </div>
    </>
  );
};

export default ChatbotDesigner;